/**
 * Tab 4: Logistics & Personnel Planning
 * PSP/CRE Tool v3.0
 *
 * VERSION: 8.21.0 - ENH-026 + ENH-027 (March 26, 2026):
 *   ENH-026: Cumulative Transport Summary added at end of Section 4A (below resupply table).
 *     Delivery method breakdown (GROUND/AIR/BLUE_LIGHT/DRONE counts from resupplyEvents state).
 *     Blood product totals (PRBC/FFP/PLT/Cryo) from logisticsProjection.medical_requirements.
 *     resupply_summary.total_units when available. Shown only when resupplyEvents.length > 0.
 *   ENH-027: Convoy Planning card (ENH-010/ARCH-002) repositioned from above initial stock
 *     levels to below resupply schedule. "Configure schedule below" → "Configure schedule above".
 *     Logical UX flow: configure events → see aggregate → see daily reference.
 *
 * VERSION: 8.20.0 - BUG-202 (March 26, 2026):
 *   Three co-located root causes confirmed via live diagnostic (Tab5 v4.24.2 probes):
 *
 *   Bug 1 — UNKNOWN blood header (bloodAlertLevel):
 *     Old: bloodItems=['PRBC','FFP','Platelets','Cryoprecipitate'], reads
 *       supply_chain.days_remaining[item]?.days — uppercase keys never match lowercase
 *       calculator keys; days_remaining values are plain numbers not {days} objects.
 *       Result: all map() returns Infinity → filter removes all → length===0 → UNKNOWN.
 *     Fix: switch to days_remaining_enhanced with keys prbc_units/ffp_units/
 *       platelets_units/cryoprecipitate_units (calculator supplyKeyMap output).
 *       Added < 9999 guard (Infinity sentinel substitute after JSON round-trip).
 *
 *   Bug 2 — getConsumptionDataSource CALCULATED path never taken:
 *     Old: checked scenario?.medical_requirements?.blood?.daily_requirements —
 *       stale path; calculator writes to
 *       casualty_projections.medical_requirements.resupply_summary.daily_consumption.
 *       Result: always FALLBACK/PRELIMINARY → stockout predictions use estimates.
 *     Fix: read casualty_projections.medical_requirements.resupply_summary.daily_consumption.
 *
 *   Bug 3 — CALCULATED branch key mismatch (calculateStockoutPredictions):
 *     Old: dataSource.data.prbcs (trailing s), dataSource.data.platelets (wrong key).
 *       resupply_summary.daily_consumption keys are prbc/ffp/plt/cryo.
 *       Result: even if CALCULATED path reached, dailyConsumption.prbc = 0.
 *     Fix: prbc/ffp/plt/cryo — matching resupply_summary.daily_consumption structure.
 *
 * VERSION: 8.19.0 - ENH-028 (March 25, 2026):
 *   Add ESTIMATED badge to Section 4D Bed Occupancy header, system-wide card title,
 *   and explanatory note. Tab4 runs personnelIntelligence.calculateBedOccupancyAnalysis()
 *   (preliminary model, no SABReS, no CSU/PHA augmentation). Tab5 MASCAL timeline runs
 *   the full SABReS calculation with per-day granularity and organic+CSU denominators.
 *   Discrepancy is architectural and expected — UI now communicates this explicitly.
 *   BUG-208 reclassified: architectural behavior, not a defect. ENH-028 is the fix.
 *
 * VERSION: 8.18.0 - BUG-203 (March 25, 2026):
 *   Root cause: all major state variables (supplies, resupplyEvents, personnel,
 *   replacementPlanning, personnelArrivals, thpConfig, mentalHealthConfig, targetMDOS)
 *   were initialized from hardcoded defaults. The useEffect at line ~1207 populated them
 *   from scenario.logistics only AFTER the first render. All dependent useMemos
 *   (stockoutAnalysis, validation, bedOccupancy, personnelAdequacy) ran on first render
 *   with defaults, triggering CRITICAL stockout warnings that cleared only after a manual
 *   field change (which forced a state update → useMemos re-ran with correct values).
 *   Fix: lazy useState initializers read from scenario.logistics and localStorage
 *   psp_logistics_projection — same priority as the existing useEffects — so first-render
 *   useMemos see persisted values immediately, eliminating false CRITICAL warnings on load.
 *
 * VERSION: 8.18.0 - BUG-T21: Pre-Flight 4G directional note when tab4_logistics has issues (April 12, 2026).
 *   Added Info Alert inside 4G "Fix logistics issues in sections 4A–4F above" when tab4_logistics.status !== 'passed'.
 *   Renamed bottom navigation button "Back to Tab 3" → "Back to Medical (Tab 3)" to clarify its scope.
 *   Prevents users from incorrectly navigating back to Tab 3 when the fix is in Tab 4 sections above.
 *
 * VERSION: 8.17.0 - BUG-156: Resupply event day validation against operation duration (March 18, 2026).
 *   Root cause: handleResupplyChange applied the new day value without bounds checking.
 *   The day TextField had no inputProps min/max, so any integer was accepted — including
 *   values beyond operation duration (e.g. Day 42 for a 15-day operation).
 *   Fix A: handleResupplyChange now clamps day values to [1, duration_days] when
 *     field === 'day', using scenario?.operation_parameters?.duration_days (default 30
 *     when Tab2 not yet configured — conservative, avoids false rejects).
 *   Fix B: Day TextField gains inputProps={{ min: 1, max: duration_days }} so the
 *     browser spinner also refuses out-of-range values.
 *   Fix C: handleAddResupply clamps the default day ((prev.length+1)*7) against duration
 *     so auto-generated events are never out of range from the start.
 * VERSION: 8.16.0 - BUG-140: Add policy_days to theatre_holding_policy on save (March 18, 2026).
 *   Root cause: calculator MASCAL block reads scenario.logistics.theatre_holding_policy.policy_days
 *   first, then falls back to regex on .policy string. buildUpdates() spread thpConfig as-is;
 *   thpConfig.policy_days was never set → fallback always resolved to 7 → THP_7_DAY regardless
 *   of Tab4C selection. Fix: buildUpdates() and handleSave inline both augment thpConfig with
 *   policy_days: THEATRE_HOLDING_POLICY[thpConfig.policy]?.days ?? 7 so the reliable numeric
 *   path in the calculator always receives the correct value.
 * VERSION: 8.15.0 - BUG-136: National Standard dropdown no longer shows NATO for MULTINATIONAL force
 *   after reload. Root cause: load effect (line ~1198) unconditionally restored
 *   log.medical_supplies.national_standard — if Tab 4 was saved before BUG-122 fix the saved
 *   value was 'NATO', which overrode the correct _tab4InitNatStd resolution on every reload.
 *   Fix: skip the saved 'NATO' value when the force is MULTINATIONAL (has no single standard)
 *   AND the live resolution (_tab4InitNatStd) produced a non-NATO standard — prefer the
 *   live resolution in that case. Explicit planner override (non-NATO saved value) still loads. (March 17, 2026)
 *
 * VERSION: 8.15.0 - F3-MRoE-STREAM2: Added contextual Alert in Section 4A when MRoE civilian stream is active.
 *   Blood DOS now includes civilian T1/T2 patients (supplyChainModule.js v5.3.0 fix); without this note
 *   planners would not know why stock quantities exceed the military-only baseline.
 *   Alert renders only when mroe_config.enabled AND civilian_stream.competes_with_military.
 *   Shows T1/T2 daily patient counts, notes T3→HNS routing and EPW separate chain (GC III Art.12). (April 7, 2026)
 *
 * VERSION: 8.14.0 - BUG-122: MULTINATIONAL division national standard resolution — useState seed + load effect now detect via fbResult.brigades[] instead of hardcoding 'NATO' (March 16, 2026)
 *   IMPORT: DRONE_DELIVERY_SPECS, DRONE_DEFAULT_TYPE from classVIII.js v3.3.
 *   delivery_method Select: added DRONE option (Drone UAS).
 *   When DRONE selected: drone_type selector appears (CARGO_DRONE / LIGHT_DROP_DRONE)
 *     with payload summary and cold-chain note from DRONE_DELIVERY_SPECS constant.
 *   handleAddResupply: new events include drone_type: DRONE_DEFAULT_TYPE + ad_threat_override: null.
 *   Source: Ukraine 2022-2024 / US Army Project Convergence 22.
 *
 * VERSION: 8.12.0 - ENH-021: Resupply package dropdown now shows label + short_summary caption (March 14, 2026).
 *   Root cause: pkg.label alone truncated in 180px Select; names like "MASCAL Push Package" /
 *   "Standard Resupply Package" did not communicate contents to the planner.
 *   Fix: two-line MenuItem (Typography body2 label + caption short_summary); Select widened to
 *   minWidth 220. short_summary field added to all 6 PUSH_PACKAGES entries in classVIII.js v3.2.
 *   Labels also shortened (e.g. "Emergency Blood Package" → "Emergency Blood") for cleaner display.
 *   Zero logic changes — data model, save/load paths, getPackagesForDestination() all unchanged.
 *
 * VERSION: 8.11.0 - BUG-107: 4D badge aligned to bedOccupancyAnalysis (March 14, 2026).
 *   Root cause: badge read bedOccupancy.occupancy_rate (raw PersonnelIntelligence snapshot);
 *   THP/RTD card read bedOccupancyAnalysis.rate (Math.round of same, memoised on primitive).
 *   Under rapid Tab4 entry, currentScenario fires twice (scenario + preliminaryCasualties arrive
 *   asynchronously). bedOccupancy updates on the second fire; bedOccupancyAnalysis only updates
 *   when the primitive value changes — producing a window where badge ≠ RTD card.
 *   Fix: badge now reads bedOccupancyAnalysis?.rate + bedOccupancyAnalysis?.color, consistent
 *   with the RTD card and Tab4's threshold system. Status vocabulary unified (SURGE/NEAR CAPACITY
 *   instead of CRITICAL/HIGH/ELEVATED from PersonnelIntelligence). Expanded 4D section continues
 *   to display bedOccupancy.occupancy_rate (precise, settled value — correct for the detail view).
 *
 * VERSION: 8.10.0 - BUG-105: National Standard reads lead nation for MULTINATIONAL corps (March 13, 2026).
 *   natStandard resolvers (both useEffect load and calculateLogisticsProjection calls) now check
 *   force.corpsLeadNation first (ENH-018F field). Fixes wrong consumption rates (PRBC/FFP/IV)
 *   when Romanian is lead nation of a MULTINATIONAL corps.
 *
 * VERSION: 8.9.0 - BUG-081 + BUG-082: Per-leg unrefrigerated flag + Personnel Adequacy fix (March 12, 2026).
 *   BUG-081: Added unrefrigerated: false to all three leg state inits. Switch added per leg in
 *     MSR panel. Cold Chain Viability matrix: cellStatus() now accepts full leg object; new
 *     'no_vehicle' status overrides time check when refrigerated vehicle unavailable. bg/color/
 *     cellLabel updated. anyExceeded alert updated. No calculator changes.
 *   BUG-082: Fixed in personnelIntelligence.js v2.14 (see that file). Tab4 unchanged.
 *
 * VERSION: 8.8.0 - BUG-083: Convoy Planning card now schedule-aware (March 12, 2026).
 *   - Title: "Consumption Reference" when no events, "Resupply Requirement" when scheduled.
 *   - Trucks box: hidden when resupplyEvents.length === 0; replaced with "No convoys scheduled" placeholder.
 *   - Footnote: contextual text based on schedule state.
 *
 * VERSION: 8.7.1 - RESEARCH-RESUPPLY-001: "Push Package" column header → "Replenishment Package" (March 10, 2026).
 *   Tooltip updated: "Routine = scheduled delivery" → "Routine = standard replenishment".
 *   APP_RULES §9.1: "push"/"pull" are not NATO doctrine terms. Internal constant names (PUSH_PACKAGES,
 *   push_package) unchanged — only the user-visible label corrected.
 * VERSION: 8.7.0 - ACT1-SURGICAL-KITS: Remove surgical_kits from Tab4 (March 9, 2026):
 *   surgical_kits removed from: initial supplies state, initial stock display grid,
 *   resupply event Custom contents editor, L4-03 supply refresh path,
 *   handleTargetMDOSChange supply refresh, all three preset supply objects
 *   (NATO_BRIGADE, ROMANIAN_BRIGADE, US_BRIGADE).
 *   Reason: US Army ATP 4-02.25 only — not a NATO doctrine planning item.
 *   PROBLEM: bgcolor=warning.light (orange) card with warning.dark text = poor contrast.
 *   FIX: Outer card changed to grey.50/grey.300 (matches Tab5 convoy panel pattern).
 *   Inner metric boxes (white paper) and MSR sub-panel (conditional success/warning/error) unchanged.
 * VERSION: 8.6.1 - BUG-066: flushState markDirty (March 8, 2026):
 *   - BUG-066 FIXED: flushState() now calls markDirty() — toolbar nav bypassed
 *     Save & Continue, silently saving without flagging Tab 5 results as stale.
 *
 * VERSION: 8.6.0 - calculationService integration (March 8, 2026):
 *   - IMPORT: markDirty from calculationService.js (new service layer).
 *   - handleSave: calls markDirty() after onUpdate(updates) — signals Tab 5 that the
 *     scenario has changed and any existing calculation result is stale.
 *   - Tab 5 reads isDirty() on mount and after each save, shows "Scenario Changed" Alert.
 *   - Tab 5 clears dirty flag after next successful calculation.
 *   - No change to calculateLogisticsProjection(), localStorage writes, or validation.
 *
 * VERSION: 8.5.3 - MDOS-VAL-001: Inject calculator overall_mdos into saved mdos_analysis (March 7, 2026)
 *   ROOT CAUSE: proj was scoped inside if(casualties && opDuration) — updates object below could
 *   not see it. mdos_analysis was always saved with SupplyIntelligence result (~11% of correct
 *   consumption rate → 74-day fiction). FIX: hoist `let proj = null`. In updates, spread
 *   mdosAnalysis but override overall_mdos / status / limiting_factor from proj when available.
 *   data_source:'CALCULATOR' marks the field. Validation now reads correct value on first try.
 *
 * VERSION: 8.5.2 - ESLint: Remove unused NearMeIcon and AccessTimeIcon imports (March 6, 2026)
 *   Lines 891-892: NearMeIcon and AccessTimeIcon declared but never referenced in JSX or logic.
 *   Zero functional change.
 *
 * VERSION: 8.5.1 - ENH-014c: Wire msrLegs into calculateLogisticsProjection (March 6, 2026)
 *   - ADD: _buildMsrLegs(leg1,leg2,leg3) module-level helper — calls calculateConvoyTravelTime
 *     for each leg, returns [{one_way_hours},...] or null on error (graceful degradation).
 *   - MOD: Both calculateLogisticsProjection() call sites now pass 7th arg msrLegs via
 *     _buildMsrLegs(msrConfig, msrLeg2Config, msrLeg3Config). Cold chain viability check
 *     now active in simulation — degraded blood products zeroed on arrival day.
 *   - Output fields cold_chain_losses[] and cold_chain_viability{} now available for
 *     Tab5 display (future session).
 *
 * VERSION: 8.5.0 - Section 4F rewrite: Forward Supply Chain (March 6, 2026)
 *   REMOVE: Evacuation distance table from Tab 4 — wrong content (rearward patient
 *           movement belongs in Tab 3, not logistics). Per discussion March 6.
 *   REMOVE: Golden Hour compliance chips, per-facility evac table, evac chain alert.
 *   ADD:    3-leg forward supply chain inputs: Leg1 Depot→FMSS (existing msrConfig,
 *           kept), Leg2 FMSS→R2B (msrLeg2Config), Leg3 R2B→R1 (msrLeg3Config).
 *           All 3 legs share the same 4-field structure (distance/road/terrain/season).
 *           Live travel time preview rendered per leg.
 *   ADD:    ENH-014b Cold Chain Viability Matrix — 7 blood products × 3 legs.
 *           Reads BLOOD_TRANSPORT_LIMITS from classVIII.js v2.9.
 *           Red = transport time exceeds product limit. Yellow = >75% of limit.
 *           FDP always green (null limit). PLT most restrictive (4h).
 *   ADD:    Accordion header chips showing Leg1/2/3 one-way travel times.
 *   RENAME: expandedSections.evacuationDistances → supplyRoutes (UI-only state key).
 *   SAVE:   msr_leg2_config / msr_leg3_config persisted in buildUpdates().
 *   LOAD:   msr_leg2_config / msr_leg3_config restored on mount.
 *   ENH-010 and ARCH-002: Confirmed complete (not open) — updated CURRENT_STATE.
 *
 * VERSION: 8.4.0 - ARCH-002: MSR route wiring (March 6, 2026)
 *   - ADD: msrConfig state (distance_km, road_type, terrain, season) initialized from MSR_DEFAULTS.
 *   - ADD: Section 4F MSR input panel with live travel time preview (calculateConvoyTravelTime).
 *   - ADD: convoy_plan travel time display in Convoy Planning card when projection has route data.
 *   - WIRE: msrConfig passed as 6th arg to calculateLogisticsProjection() in handleSave and
 *     handleTargetMDOSChange. Output: logisticsProjection.convoy_plan.
 *   - SAVE: msr_config persisted in buildUpdates() → scenario.logistics.msr_config.
 *   - LOAD: msr_config restored from scenario.logistics.msr_config on mount.
 *
 * VERSION: 8.3.0 - ENH-010: Weight/Volume / Convoy Planning card (March 6, 2026)
 *   - ADD: Convoy Planning card below Initial Stock Levels (Section 4A).
 *     Reads logisticsProjection.daily_weight_volume (ENH-010-D from supplyChainModule v4.7.0).
 *     Displays: daily blood weight (kg), daily materiel weight (kg), total daily weight (kg),
 *     total daily volume (m³), trucks required (binding constraint: weight vs volume).
 *     Shows NATO standard truck specs (CONVOY_CAPACITY.STANDARD_TRUCK).
 *   - ADD: Package weight/volume chips in package contents viewer (Collapse rows).
 *     Computes on-the-fly from package blood product quantities × ITEM_WEIGHTS_KG.
 *     Actionable for G4: "this event delivers X kg, requires Y trucks".
 *   - IMPORT: calculateDailyWeightVolume from supplyChainModule; CONVOY_CAPACITY,
 *     ITEM_WEIGHTS_KG, ITEM_VOLUMES_M3 from classVIII.
 *
 * VERSION: 8.2.5 - PKG-004: Resupply package dropdown filtered by destination echelon (March 5, 2026)
 *   Root cause: All PUSH_PACKAGES rendered in flat dropdown regardless of mtf_target.
 *   ROLE_1_DAILY (0 blood products, 0 surgical) was selectable for corps-level events,
 *   causing silent zero-blood delivery in simulation — user expected blood, got none.
 *   Fix: import getPackagesForDestination() from classVIII.js (new in v2.6).
 *   Package Select now renders getPackagesForDestination(event.mtf_target || 'ALL').
 *   Invalid selection alert: red Alert shown inline if saved event.push_package is
 *   incompatible with current mtf_target (e.g. ROLE_1_DAILY + ROLE3 target).
 *   Auto-clear: handleResupplyChange clears push_package when mtf_target changes and
 *   current package is no longer valid for the new destination.
 * VERSION: 8.2.4 - MDOS-D-003: MDOS card red-on-load fix (March 5, 2026)
 *   Root cause: mdosAnalysis returns { overall_mdos: 0 } when no stock calculated yet.
 *   getMDOSColor(0): `0 == null` is false → guard bypassed → `!0` fires error/red branch.
 *   Fix A: bgcolor IIFE — `if (!logisticsProjection) return 'grey.100'` as first guard.
 *   Fix B: all display sites — introduce `displayMDOS` computed var: null when no projection,
 *           otherwise logisticsProjection.overall_mdos ?? mdosAnalysis.overall_mdos.
 *           getMDOSColor(null) correctly returns 'default' (grey). Big number shows '?'.
 *   Fix C: Status/Limiting IIFE — `if (!logisticsProjection)` returns "Not yet calculated".
 *   IMP-006 registered: useState(7) hardcoded default violates APP_RULES 2.1 — deferred.
 * VERSION: 8.2.3 - IMP-004/005: Import triageCategories + nationalStandards (March 4, 2026)
 *
 * IMP-004 (CLOSED): Hardcoded triage fractions 0.30/0.50/0.20 replaced with
 *   TRIAGE_CATEGORIES.T1_IMMEDIATE/T2_DELAYED/T3_MINIMAL.baseline_percentage.
 *   Two occurrences fixed:
 *   (a) averageIVConsumption useMemo — Priority 3 fallback (line ~1365)
 *   (b) calculateStockoutPredictions — blood consumption tMix (line ~1792)
 *   NOTE: Values change from 30/50/20 → 25/40/30 (T4 0.05 excluded — no treatment resources).
 *   Blood stock estimate impact: PRBC/casualty 2.80 → 2.30 (−18%). More accurate.
 *   Source: TRIAGE_CATEGORIES.baseline_percentage in triageCategories.js (APP_RULES 2.1).
 *
 * IMP-005 (CLOSED): Hardcoded 5000 brigade strength fallback in calculateStockoutPredictions
 *   replaced with NATIONAL_STANDARDS.NATO.brigade.strength (confirmed = 5000 in v2.3).
 *   Lines 1432/1460 (personnelBreakdownByUnit) left unchanged — those use force.total_strength
 *   (corps-level, always populated); their 5000 fallback is a different context, not IMP-005.
 *
 * VERSION: 8.2.2 - G-06: Four-tier MDOS Chip color (C-012) (March 4, 2026)
 * - G-06 (C-012): "Projected MDOS" Chip color changed from 3-tier hardcoded to 4-tier
 *   constants-based per ASSET_STATUS_THRESHOLDS (resupplyThresholds.js v1.1.0).
 *   Old: overall_mdos >= targetMDOS → success; >= targetMDOS/2 → warning; else → error.
 *   New: computes mdosCoveragePct = (overall_mdos / targetMDOS) × 100, then:
 *     ≥60% → success (GREEN), 40–59% → warning (YELLOW),
 *     20–39% → error (ORANGE), <20% → error (RED).
 *   ORANGE and RED both map to MUI 'error' — MUI has no native orange Chip color.
 *   All threshold values read from ASSET_STATUS_THRESHOLDS; no hardcoded percentages.
 *   ASSET_STATUS_THRESHOLDS added to existing resupplyThresholds import.
 * VERSION: 8.2.1 - MDOS-D-002: Stale projection card shows neutral when target_mdos_days mismatch (March 3, 2026)
 * VERSION: 8.2.0 - BED-OCC: New section 4D Bed Occupancy; sections renumbered 4D→4E, 4E→4F, 4F→4G (March 3, 2026)
 * - Section 4D: Bed Occupancy — system-wide + per-role breakdown, throughput ceiling, MASCAL warning
 * - expandedSections: added bedOccupancy key
 * - Mental Health: 4D → 4E; MSR: 4E → 4F; Validation: 4F → 4G
 * - C-009b closed: Role 1 excluded from bed counts (validationSystem.js v2.11)
 *
 * VERSION: 8.1.6 - UX: Status card repositioned + "Order by: Day X" label (March 3, 2026)
 * - UX-001: MDOS status card moved from top of 4A to BELOW Initial Stock Configuration card.
 *   Previously fired red CRITICAL_ZERO alarm on every Tab4 entry before user touched anything
 *   (stale localStorage projection with overall_mdos=0). Information architecture fix: cause
 *   (configuration input) before effect (status result). Header chip retains MDOS at a glance.
 *   No logic changes — purely DOM order change.
 * - UX-002: "Resupply: 7 days" badge replaced with "Order by: Day X" / "No resupply needed".
 *   Raw threshold value was ambiguous: untrained users misread "7 days" as op days remaining
 *   or a resupply interval. New label surfaces the actionable calendar day from the existing
 *   resupplyNeeds.resupply_by_day value (calculateResupplyNeeds() in nationalLogistics.js).
 *   Formula: resupply_by_day = max(1, currentMDOS - safetyBuffer) where safetyBuffer=2 (NATO default).
 *   e.g. targetMDOS=7, op=10d → currentMDOS=7, buffer=2 → "Order by: Day 5"
 *   CORRECTION v8.1.7: Previous version used resupply.standard_interval_days which does NOT exist
 *   in nationalLogistics.js — resolved silently to undefined||7, a hidden hardcoded value
 *   (APP_RULES 2.1 violation). Fixed to read resupplyNeeds.resupply_by_day directly.
 *   When no resupply needed (commandedMDOS >= opDuration) shows "No resupply needed" chip.
 *
 * VERSION: 8.1.5 - PKG-002 call-site: pass nationalStandard to generateSchedule (March 3, 2026)
 * - handleGenerateAutoSchedule now passes nationalStandard as 5th arg to generateSchedule()
 * - Activates PKG-002 national-standard DOS targets (Romanian:18/8/4d, US:12/6/2d, NATO:15/7/3d)
 * - nationalStandard added to handleGenerateAutoSchedule dependency array
 *
 * VERSION: 8.1.4 - IMP-002/IMP-003: Fallback removal (March 3, 2026)
 * - IMP-002: Removed two dead `|| 'NATO'` fallbacks (APP_RULES 6.1).
 *   Both chains already resolve via nationalStandard state (useState('NATO')).
 *   Locations: handleTargetMDOSChange natStandard, handleSave natStandard.
 * - IMP-003: Replaced hardcoded blood consumption rates {prbc:2.0, ffp:2.0, plt:0.4}
 *   in calculateStockoutPredictions() with values derived from currentNationalConfig
 *   (nationalLogistics). Romanian: 2.1/casualty; NATO: 2.8/casualty (APP_RULES 2.1).
 * - IMP-004/005: Documented sources for natoMix triage fractions and 5000 strength
 *   fallback — deferred until nationalStandards.js + triageCategories.js imported.
 * - currentNationalConfig added to calculateStockoutPredictions dependency array.
 *
 * VERSION: 8.1.3 - BED-001/MDOS-001: Guard mdosAnalysis + bedOccupancy against premature execution (March 2, 2026)
 * - mdosAnalysis: added guard on medical_configuration presence — MDOS is meaningless without Tab3 saved
 * - bedOccupancy: added guard on medical_configuration presence — bed count requires MTF data from Tab3
 * - Both calculations now return null until Tab3 has been saved at least once
 * - Eliminates "📊 Bed Occupancy: Using fallback estimate" and "✅ Validation calculated MDOS: 0 days"
 *   console logs that fired on Tab3 entry because Tab4 useMemos react to any currentScenario change
 * VERSION: 8.1.2 - Bug2/Bug3 MDOS card + resupply alert fixes (March 2, 2026)
 * VERSION: 8.1.2 - ISSUE-01/ISSUE-02 Fix: supply scaling + pending MDOS state (March 2, 2026)
 * VERSION: 8.1.1 - Bug1 zero-stock cascade + ESLint hooks fix (March 2, 2026)
 * VERSION: 8.1.0 - A-11: THP → TPRP UI label rename (March 1, 2026)
 *
 * v8.1.2 (March 2, 2026) — ISSUE-01 + ISSUE-02:
 * ISSUE-01: Surgical Kits / Controlled Substances / Dressings now scale from projection.
 *   - Added 3 missing mappings in handleTargetMDOSChange setSupplies block:
 *     rs.controlled_substances → controlled_substances_doses
 *     rs.dressings_sets → dressings_sets
 *     rs.surgical_kits → surgical_kits
 *   - Same 3 mappings added to localStorage load block (L4-04) for consistency on re-entry.
 *   - Upstream fix in supplyChainModule.js v4.4.0 and nationalLogistics.js v2.3.0 adds
 *     the consumption rates these fields require (see those files for details).
 * ISSUE-02: MDOS card no longer shows alarming red before any calculation.
 *   - getMDOSColor: null/undefined → 'default' (grey chip) instead of 'error' (red).
 *   - MDOS card bgcolor: null MDOS → 'grey.100' neutral instead of 'error.light' red.
 *
 * v8.1.0 (March 1, 2026) — PSP_CRE_MASTER_FIX_PLAN_v2_2 A-11 (C-011):
 * Replace deprecated "Theatre Holding Policy (THP)" with correct NATO term
 * "Theatre Patient Return Policy (TPRP)" per AJP 4.10C Article 1.16.
 *
 * SCOPE: UI display strings only. Internal identifiers unchanged:
 *   - thpConfig, handleThpChange, THEATRE_HOLDING_POLICY (constant)
 *   - theatreHoldingPolicy.js (file name)
 *   - theatre_holding_policy (data key in scenario object)
 * Rationale: Renaming identifiers would break imports and data persistence.
 * Only user-visible labels are changed.
 *
 * CHANGES (5 locations):
 *   - Section comment "Section 4C: Theatre Holding Policy" → TPRP
 *   - Accordion heading Typography "4C. Theatre Holding Policy & RTD" → TPRP
 *   - InputLabel "Theatre Holding Policy" → TPRP + Tooltip with AJP 4.10C reference
 *   - Select label prop "Theatre Holding Policy" → TPRP
 *   - MASCAL alert list item "Adjusting Theatre Holding Policy" → TPRP
 *
 * VERSION: 8.0.0 - Phase D + Phase A: Tab4 owns logistics projection (February 26, 2026)
 *
 * v8.0.4 (February 28, 2026) — Fix: handleTargetMDOSChange passes resupplyEvents
 *   - calculateLogisticsProjection in MDOS-change handler was missing resupplyEvents arg
 *   - Changing MDOS in Tab4 now keeps resupply events in the simulation (not erased)
 *
 * v8.0.3 (February 28, 2026) — HIGH-1 fix: Tab5 logistics reads immediately on Tab4 Save
 *   - handleSave adds logistics_saved_at: new Date().toISOString() to scenario.logistics
 *   - Tab5 useEffect now deps on scenario?.logistics?.logistics_saved_at alongside calculated_at
 *   - Eliminates "No Logistics Projection Available" shown before Recalculate
 *
 * v8.0.2 (February 28, 2026) — CRIT-1 fix: force-scaled resupply quantities
 *   - generateSchedule and STRATEGIES moved from non-existent ResupplySchedulerService
 *     into supplyChainModule.js (correct architectural home)
 *   - Import fixed: merged into existing ../calculationEngine/modules/supplyChainModule import
 *   - generateSchedule now computes quantity_modifier = ceil(neededPRBC / 50)
 *     delivering force-appropriate quantities instead of static 50 PRBC/event
 *
 * v8.0.0 (February 26, 2026) — PSP_CRE_MASTER_FIX_PLAN_v2_0 Phase D + Phase A:
 *
 * PHASE D — Tab4 becomes sole owner of calculateLogisticsProjection (ARCH-001 / ARCH-009):
 * - D-07: handleSave now calls calculateLogisticsProjection(casualties, natStandard, opDuration,
 *         targetMDOS, resupplyEvents) before writing scenario. Projection built from
 *         scenario.casualty_projections (produced by Tab3 calculator on its Save & Continue).
 * - D-08: handleSave writes result to localStorage 'psp_logistics_projection'. Tab5 reads from
 *         this key (read-only). handleTargetMDOSChange continues to recalculate on MDOS change.
 * - D-09: MDOS Dashboard chip and big-number now read logisticsProjection.overall_mdos (ARCH-006).
 *         Falls back to mdosAnalysis.overall_mdos when no projection yet exists.
 * - D-10: Resupply warning now reads overall_mdos from logisticsProjection (not mdosAnalysis).
 * - D-11: Supply helperText corrected: "From projection" / "Complete Tab 3 first" (not "From Tab 3").
 * - D-12: ESLint compliance — handleSave dependency array updated (includes resupplyEvents).
 * - Stale comment on L4-03 useEffect corrected: Tab4 (not Tab3) writes psp_logistics_projection.
 *
 * PHASE A — Label and doctrinal corrections:
 * - A-02 (ARCH-003): Section 4E renamed "MSR / Supply Routes" + tooltip added distinguishing
 *   forward supply routes (MSR) from rearward medical evacuation (Tab 3). Per ARCH-003.
 * - A-03 (C-003): "Role 3 (Combat Support Hospital)" → "Role 3 MTF (Field Hospital)" in two
 *   places (MDOS accordion + resupply MTF dropdown). US CSH designation not a NATO term (AJMedP-1).
 * - A-04 residual: "Blue Light evacuation" → "MASCAL Escalation Protocol" in MDOS critical
 *   threshold alert text. Internal BLUE_LIGHT constant names unchanged.
 *
 * v7.0.1 (February 26, 2026) — Post-L4 Corrections [preserved for history]:
 * - FIX: Remove handleSupplyChange (unused, supplies are read-only) — ESLint no-unused-vars.
 * - FIX: Renamed commandedMDOS → targetMDOS. Target MDOS is the user's planning input (days of
 *        buffer stock desired), not just an adequacy threshold. Changing it now calls
 *        calculateLogisticsProjection() and saves to localStorage, populating supply fields.
 * - FIX: handleTargetMDOSChange replaces handleCommandedMDOSChange. Calls the engine function
 *        calculateLogisticsProjection() (allowed per APP_RULES 2.2 — calling engine ≠ inline formula).
 * - FIX: "Recalculate Now" button now calls handleSave() before navigating. Previously called
 *        onNavigate(4) directly, losing unsaved resupply events.
 * - FIX: Alert text updated — no longer says "calculated by Tab 3" (Tab4 can also calculate).
 *
 * v7.0.0 (February 26, 2026) — FIX_PLAN_LOGISTICS_DOS_v1_0 Phase L-4:
 * - L4-01: handleCalculateFromDOS() deleted entirely (APP_RULES 2.2 — no calculations in UI)
 * - L4-02: All stock calculation inline formulas removed from Tab4. calculatedSuppliesRef
 *          removed (was only needed for handleCalculateFromDOS async-state workaround).
 * - L4-03: useEffect added — reads 'psp_logistics_projection' from localStorage on mount.
 *          Re-fires when scenario.calculated_at changes (Tab3 recalculated). Populates
 *          supplies display and commandedMDOS from stored projection.
 * - L4-04: Supply input fields changed to read-only (InputProps readOnly). Values populate
 *          from projection.required_stock. key mapping: projection prbc→prbc_units, etc.
 * - L4-05: commandedMDOS state added (default 7). Single TextField replaces targetDOS input.
 *          This is the order from command (e.g. "maintain 7 days of supply").
 * - L4-06: handleCommandedMDOSChange() updates commandedMDOS state AND writes
 *          psp_logistics_projection.commanded_mdos_days to localStorage. No recalculation.
 * - L4-07: DOS days_remaining display from projection color-coded against commandedMDOS
 *          (≥commanded = success/GREEN, <commanded = warning/AMBER, <half = error/RED).
 * - L4-08: Resupply planning verified compatible — projection shape includes required_stock
 *          and daily_consumption, which stockoutAnalysis already reads from scenario.
 *          No changes to resupply section.
 * - L4-09: If logisticsProjection is null (Tab3 not run yet): info Alert shown at top of
 *          supply section. "Run calculation in Tab 3 first." Tab4 does NOT calculate.
 * - Removed all diagnostic console.log blocks from handleSave (v6.5.5 debug logging).
 * - handleSave simplified: no longer runs calculator (Tab3 handles it via L-3). Now only
 *   saves logistics config (resupplyEvents, personnel, THP, etc.) and navigates.
 *
 * v6.5.6 (February 25, 2026):
 * - BUG: QuotaExceededError on localStorage — by_brigade[n].by_day arrays
 *   (~600 objects for 6 brigades × 10 days) pushed scenario over 5MB limit.
 * - FIX: Strip by_day from each brigade in prunedProjections before onUpdate().
 *   Tab5 never reads by_day — it uses supply_chain, medical_requirements, by_corps.
 * - BUG: cryoprecipitate_units warning on every save — cryo not in initial_stock
 *   (derived at 10% PRBC in calculateTotalMedicalRequirements, not stocked).
 * - FIX: Removed cryo from bloodProducts validation list. 3 products checked.
 *
 * v6.5.5 (February 19, 2026) - STOCK MERGE DEBUGGING:
 * - ISSUE: Tab4 saves 4132 units but calculator uses 3635 (old value)
 * - Added comprehensive debugging to track stock values through scenario merge
 * - Lines 2024-2039: Log stock at each stage (scenario prop → updates → merged)
 * - Lines 2044-2050: Fixed diagnostic path to read from initial_stock (was showing undefined)
 * - This will reveal where the 4132 → 3635 transformation occurs
 *
 * v6.5.4 (February 19, 2026) - CALCULATE STOCK SAVE BUG FIX:
 * - CRITICAL FIX: Calculate Stock button values now save correctly
 * - ISSUE: Input fields showed 11805 units but Tab4 saved 2953 (old value)
 * - ROOT CAUSE: React async state timing bug in handleCalculateFromDOS/buildUpdates
 *   - handleCalculateFromDOS calls setSupplies() (async state update)
 *   - User immediately clicks "Save & Continue"
 *   - buildUpdates() closure has OLD supplies state (hasn't updated yet)
 *   - Result: Saves stale 2953 instead of new 11805
 * - FIX: Added calculatedSuppliesRef to store values immediately (lines 675-678)
 *   - handleCalculateFromDOS stores in ref BEFORE setSupplies (lines 1928-1945)
 *   - buildUpdates reads from ref if present, otherwise uses state (line 1977)
 *   - Ref cleared after save in finally block (line 2114)
 * - RESULT: "Calculate Stock" → "Save & Continue" now works correctly
 * - IMPACT: User can set MDOS to 20 days, click Calculate, Save immediately, and see correct DOS in Tab5
 *
 * v6.5.3 (February 19, 2026) - BLOOD PRODUCT VALIDATION FIX:
 * - CRITICAL FIX: Updated validation to check correct blood product keys
 * - ISSUE: Tab4 calculated correctly but refused to save, warning "Missing blood product data"
 * - ROOT CAUSE: Validation checked days_remaining for uppercase keys (PRBC, FFP, Platelets, Cryoprecipitate)
 * - REALITY: Calculator v5.4.5+ returns days_remaining_enhanced with lowercase _units suffix keys
 *   - prbc_units, ffp_units, platelets_units, cryoprecipitate_units
 * - SYMPTOM: Console showed "Supply chain data: {prbc: 8, ffp: 8, platelets: 44}" but validation failed
 * - IMPACT: Tab4 computed correct DOS (8 days) but didn't save to localStorage
 * - FIX: Lines 2026-2036 - Changed validation to check days_remaining_enhanced with _units keys
 * - RESULT: Tab4 now saves calculator results properly, Tab5 displays correct blood DOS
 * - RELATED: Works with Calculator v5.4.5 supply key mapping and Supply Module v2.0.0 field normalization
 *
 * v6.5.2 (February 14, 2026) - TAB3 COMPLETION GUARD:
 * - CRITICAL FIX: Added Tab3 completion check to preliminaryCasualties useMemo
 * - ISSUE: Calculator ran when entering Tab3 (before medical config complete)
 * - ROOT CAUSE: Guard 1 checked Tab2, but missing Guard 2 for Tab3
 * - BUG SYMPTOM: 97-page console log, BUG-057 warnings (NaN survival modifiers)
 * - DESIGN INTENT: Tab4 should get preliminary estimates AFTER Tab3 configures medical
 * - FIX: Added guard `if (!scenario?.medical_configuration?.configured) return null;`
 * - RESULT: Calculator now runs at Tab4 entry (correct timing) with complete medical config
 * - Lines changed: 765-769 (added Guard 2 check)
 *
 * v6.5.1 (February 13, 2026) - PRELIMINARY CASUALTIES GUARD:
 * - CRITICAL FIX: Added guard to preliminaryCasualties useMemo (line 749)
 * - ISSUE: Calculator ran in Tab1 before Tab2 configured, causing "Operation missing duration_days" error
 * - ROOT CAUSE: useMemo triggered on every scenario change without checking operation_parameters
 * - FIX: Added guard `if (!scenario?.operation_parameters?.duration_days) return null;`
 * - RESULT: Calculator only runs when Tab2 (Operations) is complete
 * - This was MISSED in v6.5.0 - fixed the 6 derived calculations but not the base calculation
 * - Lines changed: 749-764 (added guard + console.error in catch)
 *
 * v6.5.0 (February 12, 2026) - CALCULATOR TRIGGER FIX + ESLINT COMPLIANCE:
 * - CRITICAL FIX: Prevent calculation functions from running in Tab1/Tab2 (before operation configured)
 * - ROOT CAUSE: useMemo calculations triggered on every scenario change, even without operation_parameters
 * - ISSUE: Console showed "[SECTION 12] CRITICAL: Operation missing duration_days (got: undefined)"
 * - AFFECTED FUNCTIONS (6): mdosAnalysis, rtdProjection, bedOccupancy, csrProjection, validation, personnelAdequacy
 * - FIX: Added guard `if (!currentScenario?.operation_parameters?.duration_days) return null;` to each
 * - RESULT: Calculations only run when operation is properly configured (Tab2 complete)
 * - Lines changed: 814-822, 824-832, 834-842, 844-852, 854-862, 894-902
 * 
 * - ESLINT FIX 1: bloodAlertLevel dependency array (line 863)
 *   - Added missing dependency: scenario?.operation_parameters?.duration_days
 *   - Used in fallback calculation but was missing from dependency array
 * 
 * - ESLINT FIX 2: calculationError unused variable (line 658)
 *   - ROOT CAUSE: State was set (lines 1880, 1913) but never displayed in UI
 *   - FIX: Added Alert display in validation section (lines 3800-3807)
 *   - RESULT: Calculator errors now shown in UI, not just browser alert
 *   - Better UX: Persistent error message instead of dismissible alert
 *
 * v6.4.0 (February 12, 2026) - BLOOD ALERT CALCULATION FIX:
 * - BUG FIX: bloodAlertLevel now uses calculator's supply_chain.days_remaining
 * - ROOT CAUSE: Previous version only checked PRBC, showed GREEN even when platelets critically low
 * - ISSUE: User with 3-day platelet supply saw "Blood: GREEN" (should be RED!)
 * - FIX: Now checks ALL blood products (PRBC, FFP, Platelets, Cryoprecipitate)
 * - LOGIC: Uses minimum days remaining across all products (platelets often limiting factor)
 * - THRESHOLDS: RED ≤3 days (critical), YELLOW ≤5 days (warning), BLUE ≤7 days (monitor), GREEN >7 days
 * - DATA SOURCE: scenario.casualty_projections.supply_chain.days_remaining (from calculator v5.2.0)
 * - FALLBACK: If no supply_chain data, uses legacy PRBC-only calculation
 * - Lines changed: 817-850 (bloodAlertLevel useMemo completely rewritten)
 *
 * v6.3.0 (February 11, 2026) - CALCULATOR INTEGRATION (Tab3 v4.1.0 pattern):
 * - Added async calculator execution on "Save & Continue"
 * - Added isCalculating + calculationError states
 * - Added CircularProgress import for loading indicator
 * - Calculator runs automatically before saving (mirrors Tab3 v4.1.0)
 * - Saves calculator results to scenario.{medical_requirements, casualty_projections}
 * - Error handling: saves logistics-only if calculator fails
 * - Implements FIX-003 from CURRENT_STATE.md documentation
 *
 * v6.2.0 (February 11, 2026):
 * - CLEANUP: Removed 22 debug console.log/warn statements
 * - VERIFIED: Tab3 v4.1.0 runs calculator on Save & Continue
 * - VERIFIED: Tab4 correctly reads calculator results from scenario.medical_requirements
 * - VERIFIED: Tab4 correctly reads calculator results from scenario.casualty_projections
 * - VERIFIED: Preliminary calculation fallback works for edge cases
 * - DATA FLOW: Tab3 (Save) → Calculator → scenario.{medical_requirements, casualty_projections} → Tab4 (read)
 *
 * v6.1.0 (February 10, 2026):
 * - ARCH: Added forwardRef + useImperativeHandle for toolbar navigation compatibility
 *   - App.jsx can call tab4Ref.current.flushState() before toolbar tab switch
 *   - Tab4 is Pattern B: buffers state locally, saves on explicit Save & Continue
 *   - flushState silently saves logistics data without onComplete or onNavigate
 *   - buildUpdates() extracted from handleSave: single data construction path
 *   - handleSave: buildUpdates() -> onUpdate -> onComplete -> onNavigate
 *   - flushState: buildUpdatesRef.current() -> onUpdateRef.current only
 * - ENCODING FIX: Removed all non-ASCII characters from code lines (48 lines cleaned)
 *
 * VERSION: 6.0.3 - CRITICAL PRIORITY FIX (February 6, 2026)
 * 
 * v6.0.3 CRITICAL FIX (February 6, 2026):
 * - FIX: Check forceBuilderResult.total_strength FIRST, not totalStrength
 * - ROOT CAUSE: v6.0.2 checked totalStrength (2,000) before total_strength (36,000)
 * - WRONG ORDER: totalStrength -> total_strength -> forceBuilderResult.total_strength
 * - RIGHT ORDER: forceBuilderResult.total_strength -> calculate -> legacy fallbacks
 * - USER IMPACT: Even with correct data (36,000), displayed wrong value (2,000)
 * - RESULT: Now checks forceBuilderResult.total_strength (36,000) FIRST 
 * - LINES CHANGED: 1113-1154 (forceStrength useMemo priority reordered)
 * 
 * v6.0.2 CRITICAL FIX (February 6, 2026):
 * - FIX: Force calculation now correctly sums ALL echelons (Corps + Divisions + Brigades)
 * - ROOT CAUSE: v6.0.1 only checked pre-calculated fields, which may not exist or be incomplete
 * - PROBLEM: User with 3 Divisions + 9 Brigades (~46,000 troops) saw "Force: 2,000" (Corps HQ only)
 * - SOLUTION: Robust calculation that sums forceBuilderResult structure:
 *   1. Try pre-calculated totals first (fastest)
 *   2. If missing, calculate: corps.strength + divisions[].strength + brigades[].strength
 *   3. Only return undefined if truly no force data exists
 * - RESULT: Correctly displays full force regardless of how Tab1 saved it 
 * - EXAMPLE: Corps (2,000) + 3 Divisions (13,500) + 9 Brigades (40,500) = 56,000 total
 * - LINES CHANGED: 1091-1133 (forceStrength useMemo now has robust calculation logic)
 * 
 * v6.0.1 BUG FIX (February 6, 2026):
 * - FIX: "Force: N/A" bug in Upstream Data Integration display
 * - ROOT CAUSE: Display chip only checked scenario.force.total_strength (single field)
 * - Tab1 can save force data in multiple formats/locations
 * - SOLUTION: Added forceStrength useMemo with fallback chain (prevented N/A display)
 * - RESULT: Force display no longer shows "N/A" 
 * - NOTE: v6.0.1 prevented "N/A" but still showed wrong total (2,000 instead of 46,000)
 * - NOTE: v6.0.2 fixes the calculation to show correct total
 * 
 * VERSION: 6.0.0 - v3.0 PHASE 4: Sub-Unit Consumption Calculations (February 3, 2026)
 * 
 * v6.0.0 PHASE 4 - SUB-UNIT CONSUMPTION CALCULATIONS (February 3, 2026):
 * - personnelBreakdownByUnit processes brigade.sub_units for accurate consumption
 * - Each sub-unit uses its casualty_modifier from UNIT_TYPES (not hardcoded)
 * - Imports UNIT_TYPES from unitTypes.js
 * - Backwards compatible via legacy path for units without sub_units
 * 
 * VERSION: 5.22.0 - Phase 4.7: Guided Workflow & Validation (January 29, 2026)
 * 
 * v5.22.0 PHASE 4.7 IMPLEMENTATION (January 29, 2026):
 * - GUIDED WORKFLOW:
 *   - Added resupplyModified state to track when events are changed
 *   - Triggers set on: handleAddResupply(), handleRemoveResupply(), handleResupplyChange(), handleApplyAutoSchedule()
 *   - "Return to Tab 5" banner appears when modifications detected
 *   - Banner includes "Recalculate Now" button (navigates to Tab 5)
 *   - Closeable alert with onClose handler
 *   - Added id="resupply-section" for scroll targeting from Tab 5
 * - CROSS-TAB NAVIGATION:
 *   - Tab 4 receives onNavigate prop from App
 *   - User can navigate Tab 4 -> Tab 5 with one click
 *   - Prevents confusion about stale data
 *   - Complements Tab 5 -> Tab 4 navigation (Phase 4.7.1)
 * - USER EDUCATION:
 *   - Clear guidance on when recalculation needed
 *   - Success alert (green) indicates action required but not critical
 *   - Explains WHY recalculation needed: "see updated DOS projections"
 * 
 * v5.21.0 PHASE 4.5 IMPLEMENTATION (January 29, 2026):
 * - PHASE 4.5.1: Brigade-Specific Targeting
 *   - Enabled destination_brigade dropdown in resupply events table
 *   - Populated with brigades from force composition (forceBuilderResult.brigades)
 *   - "ALL" option as default for distributed resupply
 *   - Allows targeted resupply to specific brigade vs force-wide distribution
 * - PHASE 4.5.2: Alternate Supply Sources
 *   - Enabled source field dropdown (previously hardcoded to 'ROLE_3')
 *   - Options: ROLE_3, ROLE_4, THEATER_DEPOT, MULTINATIONAL_SUPPLY, HOST_NATION
 *   - Each source has different delivery timeframes defined in resupplyThresholds.js
 *   - Real-time display of estimated delivery time based on source + method combination
 * - PHASE 4.5.3: Configurable Severity Thresholds
 *   - New constants file: /constants/resupplyThresholds.js
 *   - National threshold profiles: NATO (3/5/7), ROMANIAN (4/6/8), US (2/4/6)
 *   - Threshold profile selector in Initial Stock Configuration section
 *   - calculateStockoutPredictions() now uses getStockoutSeverity() from profile
 *   - Recommended resupply day uses profile's safety margin (1-3 days)
 * - PHASE 4.5.4: Supply Source Definitions
 *   - SUPPLY_SOURCES constant with delivery characteristics per source
 *   - Delivery time varies by method: BLUE_LIGHT (2-12h), AIR (4-24h), GROUND (8-72h)
 *   - Reliability ratings: ROLE_3 (95%), THEATER_DEPOT (99%), MULTINATIONAL (85%)
 *   - Capacity constraints: UNLIMITED vs LIMITED
 * - PHASE 4.5.5: Injury Mechanism Multipliers (Constants Only)
 *   - INJURY_MULTIPLIERS in resupplyThresholds.js
 *   - Small arms (1.5x), Artillery (2.5x), IED (3.0x), Mine (3.2x)
 *   - calculateAdjustedConsumption() function for threat-profile-based rates
 *   - Calculator integration deferred to future phase
 * 
 * BENEFITS:
 * - Brigade-level resupply planning for multi-brigade operations
 * - Realistic source selection with delivery time estimation
 * - National doctrine compliance via configurable thresholds
 * - Foundation for injury mechanism-based consumption modeling
 * 
 * v5.20.0 PHASE 4.4 IMPLEMENTATION (January 29, 2026):
 * - PHASE 4.4.1: Core Algorithm Development
 *   - New utility: /utils/resupplyScheduler.js with generateSchedule() function
 *   - 3 scheduling strategies: CONSERVATIVE (7+ DOS), BALANCED (5+ DOS), MINIMAL (3+ DOS)
 *   - selectOptimalPackage() - matches deficit to best push package from classVIII.js
 *   - selectDeliveryMethod() - chooses BLUE_LIGHT vs GROUND based on urgency
 * - PHASE 4.4.2: Package Selection Logic
 *   - Analyzes consumption rate and deficit to match optimal package
 *   - Prefers fewer large packages over many small packages
 *   - Calculates efficiency score for each package option
 * - PHASE 4.4.3: Scheduling Strategies
 *   - CONSERVATIVE: Maintain 7+ DOS throughout, 2-day safety margin
 *   - BALANCED: Maintain 5+ DOS, minimize events, 1-day safety margin
 *   - MINIMAL: Avoid stockout only, fewest packages, 1-day safety margin
 *   - Strategy selector dropdown in UI
 * - PHASE 4.4.4: Preview & Validation
 *   - Auto-schedule preview dialog shows complete schedule before applying
 *   - Before/after DOS comparison (current vs with resupply)
 *   - Event-by-event breakdown with packages, methods, products covered
 *   - Total units summary across all resupply events
 * - PHASE 4.4.5: Apply/Revert Functionality
 *   - handleApplyAutoSchedule() merges auto-events with manual events
 *   - Preserves user's manual overrides
 *   - handleRevertAutoSchedule() button removes auto-generated events
 *   - lastAutoSchedule state stores previous configuration for revert
 * 
 * BENEFITS:
 * - One-click generates mathematically optimal resupply schedule
 * - Preview before applying prevents mistakes
 * - Multiple strategies for different operational contexts
 * - Revert capability allows experimentation without data loss
 * - Eliminates manual trial-and-error iteration
 * 
 * v5.19.0 PHASE 4.3 IMPLEMENTATION (January 29, 2026):
 * - PHASE 4.3.1: Calculator Results Integration
 *   - Enhanced calculateStockoutPredictions() to check scenario.medical_requirements.blood
 *   - Priority: Calculator results > Preliminary estimates > Fallback NATO rates
 *   - Returns data source metadata: source, label, color, badge
 *   - Logs which data source is being used for transparency
 * - PHASE 4.3.2: Discrepancy Detection & Alert
 *   - New consumptionDiscrepancy computed value detects >10% differences
 *   - Compares preliminary estimates vs actual calculator results
 *   - Shows side-by-side comparison with percentage difference
 *   - Alert only appears when calculator has run AND discrepancies exist
 * - PHASE 4.3.3: Visual Indicators
 *   - Added data source badge to stockout predictions alert header
 *   - Badge shows: "CALCULATED" (green), "PRELIMINARY" (yellow), "ESTIMATE" (blue)
 *   - Added data source badge to MDOS display card
 *   - Updated alert text to reference data source type
 * - PHASE 4.3.4: Cross-Tab Status Sync
 *   - stockoutAnalysis now includes dataSource and dailyConsumption
 *   - consumptionDataSource available throughout component
 *   - Enables future Tab 5 callback integration
 * 
 * BENEFITS:
 * - User always knows if data is preliminary or calculated
 * - No confusion about Tab 4 vs Tab 5 consumption rates
 * - Automatic alerts when rates significantly differ
 * - Clear visual distinction between estimate quality levels
 * 
 * v5.18.0 CRITICAL FIXES (January 28, 2026):
 * - BUG FIX: Package contents display showing 0/0/0 for all values
 *   - Root cause: Tab4 was accessing flat structure (contents.prbc_units)
 *   - classVIII uses nested structure (contents.blood_products.prbc_units)
 *   - Fixed lines 2062-2092: Extract bloodProducts and pharmaceuticals objects
 *   - Now correctly displays: STANDARD_RESUPPLY (50/40/20), SURGICAL_RESUPPLY (30/30/10)
 * - BUG FIX: UTF-8 encoding corruption throughout file (11 instances fixed)
 *   - Fixed - bullets (lines 1705, 1706, 2084-2092, 2617)
 *   - Fixed  chart emoji (lines 554, 1254)
 *   - Fixed [GREEN] [BLUE] [YELLOW] [RED] circle emojis (lines 1414, 1417, 1420, 1423 - MDOS legend)
 *   - Fixed 🏛️ building emoji (line 1647 - NATO standard dropdown)
 *   - Fixed 🇷🇴 🇺🇸 flag emojis (lines 1648, 1649)
 * - Impact: All 8 push packages display correctly with proper formatting
 * 
 * v5.17.0 ARCHITECTURAL IMPROVEMENTS (January 28, 2026):
 * - ARCHITECTURE: Consolidated push packages to single source (classVIII.js)
 *   - Import PUSH_PACKAGES from '../constants/classVIII' (already correct)
 *   - nationalLogistics.js now thin - only national overrides
 *   - Eliminated three-way code duplication (classVIII/nationalLogistics/calculator)
 * - UI: Consumption rates now always visible by default
 *   - Changed dosHelperOpen initial state from false -> true
 *   - Users can still collapse if desired, but starts expanded
 *   - Educational value: Reference data always accessible
 * - FIX: "Package Contents Unavailable" error resolved
 *   - Added EMERGENCY_BLOOD and STANDARD_RESUPPLY to classVIII.js
 *   - Added package aliases: MASS_CASUALTY, SURGICAL_SUPPORT
 *   - Tab4 dropdown, calculator, and Tab5 now use same codes
 * - DOCUMENTATION: Updated Phase 4.1 and 4.2 specifications
 *   - Phase 4.1: Calculator must import PUSH_PACKAGES (no hardcoded values)
 *   - Phase 4.2: Tab 5 must import PUSH_PACKAGES for labels
 *   - Data structures documented for both phases
 * 
 * v5.16.0 EDUCATIONAL ENHANCEMENT (January 28, 2026):
 * - FEATURE: Hybrid IV consumption calculation with educational progression
 *   - Shows planning evolution: NATO standard -> Estimated -> Calculated
 *   - Priority 1: Final casualty results (Tab 5) - most accurate
 *   - Priority 2: Preliminary casualties (Tab 4) - rough estimate  
 *   - Priority 3: NATO standard distribution (30/50/20) - doctrine baseline
 *   - Never shows "?" - always provides actionable value
 * - DISPLAY: Data quality indicator below IV rate
 *   - Green "(calculated)" - precise from full calculator
 *   - Blue "(estimated)" - preliminary from force structure
 *   - Gray "(NATO standard)" - doctrine baseline fallback
 * - CALCULATION: Weighted average based on actual triage mix
 *   - Uses iv_fluids_per_t1, iv_fluids_per_t2, iv_fluids_per_t3
 *   - Reflects operation-specific casualty severity
 *   - Educational: Students see how intensity affects supply needs
 * - FIX: Resolves "?" display issue when iv_per_casualty field missing
 * 
 * v5.15.0 UI/UX IMPROVEMENTS (January 28, 2026):
 * - FIX UI-001: Removed duplicate DOS information
 *   - Old: "What is DOS?" alert box below selectors (redundant)
 *   - New: Simplified consumption rates quick reference card
 *   - Shows: PRBC, FFP, IV, Antibiotics consumption per casualty
 *   - Visual cards with color-coded values and NATO standard targets
 *   - Links to comprehensive MDOS accordion above for full explanation
 * - FIX UI-002: Improved label visibility on blue background
 *   - TextField labels: Bold (700 weight), larger font (0.95rem), white color
 *   - Helper text: Bold (600 weight), larger font (0.8rem), white color
 *   - FormControl InputLabel: Bold (700 weight), white color, persists when focused
 *   - All caption text: Bold (600 weight), larger font (0.8rem), white color
 *   - Improved contrast for "Target Days of Supply", "National Standard", 
 *     "Consumption rates", "Auto-fill stock levels", "Op Duration"
 *   - Much better readability against primary.light blue background
 * 
 * v5.14.0 PHASE 4.0: Resupply Planning - ALL TASKS COMPLETE (January 28, 2026):
 * - TASK 4.0.1: Enhanced resupply event data structure (20 min)
 *   - Added `source` field (where supplies come from, e.g., 'ROLE_3')
 *   - Added `mtf_target` field (which MTF receives, e.g., 'ROLE2_BASIC', 'ALL')
 *   - Added `destination_brigade` field (which brigade, e.g., 'brigade_1', 'ALL')
 *   - Enables targeted resupply planning vs global distribution
 * - TASK 4.0.2: Destination MTF Dropdown (30 min)
 *   - Added dropdown to select destination MTF for each resupply event
 *   - Options: 'ALL' (distributed), MTF types, or specific facilities
 *   - Dynamically populated from medical facilities in scenario
 *   - Supports both generic types and specific facility selection
 * - TASK 4.0.3: Destination Column in Resupply Table (20 min)
 *   - Added "Destination MTF" column to resupply events table
 *   - Displays selected destination with dropdown selector
 *   - Visual indication of distribution vs targeted resupply
 *   - Updated colspan for package contents expansion row (5->6)
 * - TASK 4.0.4: Stockout Prediction Calculator (30 min)
 *   - Implemented calculateStockoutPredictions() function
 *   - Calculates predicted stockout days for each blood product
 *   - Uses casualty projections and consumption rates
 *   - Categorizes by severity: CRITICAL (<3 days), WARNING (<5 days), CAUTION (<7 days)
 *   - Recommends resupply scheduling (2 days before stockout)
 * - TASK 4.0.5: Auto-Suggest Resupply Panel (30 min)
 *   - Added alert panel showing predicted stockouts
 *   - Individual cards for each product at risk with severity color-coding
 *   - Shows current stock, daily consumption, days remaining, stockout day
 *   - Individual "Schedule Resupply" buttons per product
 *   - "Auto-Schedule All" button for batch scheduling
 * - TASK 4.0.6: MDOS Educational Accordion (30 min)
 *   - Added expandable accordion explaining MDOS concept
 *   - NATO standard MDOS requirements by facility role
 *   - Step-by-step MDOS calculation methodology
 *   - Why MDOS matters: operational planning, MASCAL preparedness, critical thresholds
 *   - References: NATO STANAG 2228, AMedP-9.1
 * - TASK 4.0.7: Auto-Schedule Resupply Handler (20 min)
 *   - Implemented handleAutoScheduleResupply() function
 *   - Supports single product or batch auto-scheduling
 *   - Groups predictions by day to create consolidated events
 *   - Automatically selects push package based on severity
 *   - Automatically selects delivery method based on urgency
 *   - Marks auto-generated events with reasoning
 * - TASK 4.0.8: Code Quality & ESLint (20 min)
 *   - Verified all Material-UI icons properly imported
 *   - No local icon creation - all from @mui/icons-material
 *   - Defensive programming with null checks throughout
 *   - Proper callback dependencies in useMemo/useCallback
 *   - UTF-8 safe (emojis removed from critical paths)
 * 
 * PHASE 4.0 TOTAL: 3 hours (200 minutes)
 * 
 * v5.12.0 FIX (January 19, 2026):
 * - CRIT-007 FIX: Changed onUpdate() to only pass logistics, not entire scenario
 *   - BEFORE: onUpdate({ ...scenario, logistics }) - spreads stale scenario
 *   - AFTER: onUpdate({ logistics }) - App merges with current state
 *   - Root cause: Tab 4's scenario prop is stale (hasn't received earlier tabs' updates)
 *     so spreading it overwrites Tabs 1-3 data with undefined values
 *   - Symptom: Validation shows "No brigade configurations - Tab 2 data not saved"
 *     after Tab 4 save, even though Tab 2 was saved correctly
 * 
 * v5.11.0 FIXES (January 17, 2026):
 * - FIX T4-015: Evacuations showing 0 in Section 4C
 *   - Root cause: rtdProjection.evacuations?.total structure mismatch
 *   - Added computed evacuationsCount with multiple fallback sources:
 *     1. rtdProjection.evacuations?.total (object)
 *     2. rtdProjection.evacuations (direct number)
 *     3. rtdProjection.strategic_evacuations
 *     4. preliminaryCasualties.summary.evacuations
 *     5. Calculated: Total - RTD - Deaths
 *   - Now correctly displays strategic evacuation count
 * - FIX T4-016: Bed Occupancy >100% display enhancement
 *   - >100% is VALID during MASCAL/surge conditions
 *   - Added visual MASCAL warning when bed demand exceeds capacity
 *   - Color-coded: Green (<80%), Yellow (80-100%), Orange (100-120%), Red (>120%)
 *   - Tooltip explains what >100% means
 * 
 * v5.10.0 FIXES (January 15, 2025):
 * - FIX T4-012: Section 4E "No Medical Facilities" bug
 *   - Root cause: Tab 3 saves to scenario.medical_configuration.facilities
 *   - Tab 4 was looking in scenario.medical.facilities (wrong path)
 *   - Added medical_configuration.facilities to lookup chain (line 549)
 * - ADD T4-011: MDOS Educational Tooltip
 *   - Info icon with detailed explanation of MDOS calculation
 *   - Shows what GREEN/YELLOW/RED status means with color indicators
 *   - References NATO STANAG 2228 and AMedP-9.1 standards
 *   - Tooltip appears on hover over "Medical Days of Supply" text
 * - FIX T4-013: Push Package Contents verification
 *   - Added defensive null checks to getPushPackageDetails() calls
 *   - Shows warning alert when package details unavailable
 *   - Suggests selecting different package or using Custom option
 * - ADD T4-014: Personnel Arrival Destination selector
 *   - New dropdown column in Personnel Arrivals table
 *   - Options: "All Units (Distributed)", "Reserve Pool (Hold)", specific brigades
 *   - Dynamically populated from force structure
 *   - Integrates with personnel arrivals schedule
 * 
 * v5.9.0 FEATURES (January 13, 2025):
 * - ADD: Personnel Arrivals Schedule (like resupply events for personnel)
 *   - Day-based arrival table: "Day 5: +50 combat personnel"
 *   - Supports COMBAT and MEDICAL personnel types
 *   - Integrates with personnelIntelligence.calculateReservePoolDepletion()
 *   - Essential for 14+ day operations planning
 * - FIX: Bed Occupancy displayed 13500% instead of 135%
 *   - Root cause: occupancy_rate is already a percentage (135), not decimal (1.35)
 *   - Was: Math.round(bedOccupancy.occupancy_rate * 100) => 135 * 100 = 13500
 *   - Now: Math.round(bedOccupancy.occupancy_rate) => 135
 * 
 * v5.8.0 FEATURES (January 13, 2025):
 * - ADD DC-004: Evacuation distances display section (4E)
 *   - Shows all medical facilities with their evacuation routes
 *   - Displays distance, receiving facility, terrain, route type
 *   - Golden Hour compliance status per facility
 *   - Summary statistics for evacuation chain
 *   - Links back to Tab 3 for configuration
 * 
 * v5.7.3 - MDOS Calculation Sync Fix (January 12, 2025)
 * 
 * v5.7.3 FIXES (January 12, 2025):
 * - FIX: handleCalculateFromDOS now uses same force strength lookup as supplyIntelligence.js
 *   - Was: scenario?.force?.total_strength || 5000
 *   - Now: totalStrength || total_strength || forceBuilderResult.corps.strength || 5000
 *   - BUG: Stock was calculated for 50 casualties/day (5000 fallback)
 *         but MDOS used 480/day (from corps.strength 48000)
 *   - Result: MDOS showed 1 day even after Calculate Stock ran
 * 
 * v5.7.2 FIXES (January 12, 2025):
 * - ADD: Tourniquets and morphine_doses to DOS calculation
 *   - Tourniquets: 2 per T1 + 0.5 per T2 (hemorrhage control)
 *   - Morphine: 4 doses/T1 + 2/T2 + 1/T3 (24h pain management)
 *   - Auto-calculated when user clicks "Calculate Stock"
 *   - User can override calculated values
 * 
 * v5.7.1 FIXES (January 12, 2025):
 * - ADD: Fallback casualty estimates for RTD when calculator returns 0
 *   - Uses historical rates: 0.5% daily battle + 0.3% DNBI casualties
 *   - Provides RTD estimates even when Tab 2/5 not complete
 *   - Shows "Based on Estimates" chip when using fallback
 *   - Triage distribution based on Ukraine data (T1:15%, T2:25%, T3:55%, T4:5%)
 * - FIX: RTD display now shows warning when using estimated values
 *   - Yellow background indicates estimates vs blue for calculated values
 *   - Note explains these are estimates until Tab 5 calculation runs
 * 
 * v5.7.0 FIXES (January 12, 2025):
 * - ADD: Tourniquet and Morphine Doses fields to Initial Stock Levels
 *   - These are required by validateStockLevels() in classVIII.js
 *   - Tourniquets: MTF resupply stock (note: soldiers have CAT in IFAKs)
 *   - Added to default state, all three presets (NATO, Romanian, US)
 * - ADD: Explanatory note about tourniquet counts (IFAK vs MTF stock)
 * 
 * v5.6.9 FIXES (January 12, 2025):
 * - FIX: Removed direct localStorage.setItem('psp_cre_scenario') 
 *   - Was using different key than App.jsx's LocalStorageManager
 *   - Now only uses onUpdate() to update parent state
 *   - App.jsx handles persistence via LocalStorageManager
 * - FIX: Validation recommendations now filter out generic "X warning(s) found" messages
 *   - Avoids duplicate display since detailed warnings are shown above
 * - ADD: logistics.configured = true flag for validation tracking
 * 
 * v5.6.8 FIXES (January 12, 2025):
 * - FIX: Removed redundant "Load Preset" and "Save & Continue" from header
 *   - Navigation buttons remain at bottom only for cleaner UI
 * - FIX: Validation warnings now correctly extracted from validation.checks.*.issues[]
 *   - Previous fix looked for validation.warnings[] which doesn't exist
 *   - Now iterates through validation.checks (object) to get all issues
 *   - Displays warnings and errors separately with tab source and fix suggestions
 * - FIX: "View All Tab Validation Details" table shows each tab check status
 * 
 * v5.6.7 FIXES (January 12, 2025):
 * - FIX: Initial Stock Configuration alignment - all elements now bottom-aligned
 *   - Changed Grid alignItems from "center" to "flex-end"
 *   - Added helper text under Select ("Consumption rates")
 *   - Added helper text under Button ("Auto-fill stock levels")
 *   - Restructured "Op Duration" display with value above label
 * 
 * v5.6.6 FIXES (January 11, 2025):
 * - NO HARDCODING: Imports DEFAULT_CASUALTY_MIX from classVIII.js
 * - FIX: handleCalculateFromDOS uses DEFAULT_CASUALTY_MIX for percentage calculations
 * - FIX: UI text visibility - white text on blue background in Initial Stock Configuration
 * - SYNC: Tab4, supplyIntelligence, and classVIII all use same DEFAULT_CASUALTY_MIX constant
 * 
 * v5.6.5 FIXES (January 11, 2025):
 * - FIX: Resupply warning now shows "Current MDOS" instead of "Target DOS"
 *   - Was misleading: "Target DOS (10) < Op Duration (10)" (false statement)
 *   - Now correct: "Current MDOS (2) < Op Duration (10)" (true statement)
 * 
 * v5.6.2 FIXES (January 11, 2025):
 * - FIX: 'duration' variable now used in personnelBreakdownByUnit
 *   - Added dailyCasualtyRate calculation
 *   - Added daily_casualties per unit
 *   - Added duration and daily_casualty_rate to return object
 * - FIX: 'totalStrength' variable now included in return object
 * - FIX: T4-007 table now shows Daily Rate column
 * - FIX: Summary card shows Operation Duration and Daily Casualty Rate
 * 
 * v5.6.1 FIXES (January 11, 2025):
 * - USE: getPushPackageDetails() in T4-005 package viewer
 * - USE: getNationalLogistics() in DOS helper for consumption rates display
 * - Now all 6 nationalLogistics imports are properly used
 * 
 * v5.6 FEATURES (January 11, 2025):
 * - ADD T4-005: Push package contents viewer
 *   - Shows Line 1/2/3 breakdown for each package
 *   - Collapsible details per resupply event
 * - ADD T4-006: Custom push package definition UI
 *   - Input fields for each supply category when "Custom" selected
 * - ADD T4-007: Personnel breakdown by unit type
 *   - Shows which units will need most replacements
 *   - Based on preliminary casualty projections
 * 
 * v5.5 FEATURES (January 11, 2025):
 * - ADD T4-003: Initial DOS input box with auto-calculate stock
 *   - User inputs target DOS, system calculates required stock quantities
 *   - National standard selector (NATO, ROMANIAN, US)
 *   - Shows differences from NATO baseline
 * - ADD T4-004: Resupply warning when DOS < operation duration
 *   - Calculates when resupply is needed
 *   - Shows shortfall days and urgency
 * - ADD NL-003: National standard selector integration
 * - INTEGRATE: nationalLogistics.js for consumption rates
 * 
 * v5.4 FIXES (January 11, 2025):
 * - FIX: RTD = 0 because no casualty data available
 *   - Added preliminary casualty calculation when Tab 4 loads
 *   - Tab4 now runs calculateCasualties() to get estimates
 *   - currentScenario includes casualty_projections for RTD/bed occupancy
 * - FIX: CSR Daily Rate showing "0.0" due to toFixed(1) precision
 *   - Changed to display as percentage: "0.32%" instead of "0.003"
 * - FIX: Missing navigation buttons at bottom
 *   - Added "Back to Tab 3" and "Save & Continue" buttons
 *   - Consistent with other tabs' UI pattern
 * 
 * v5.3 FIX (January 10, 2025):
 * - Force: N/A fixed - reads both totalStrength and total_strength
 * 
 * VERIFICATION (January 9, 2025):
 * All T45-002 to T45-005 requirements ALREADY IMPLEMENTED:
 * - T45-002: MDOS Display - âœ... Lines 148, 337, 343-360 (dashboard with days, status, progress)
 * - T45-003: Blood Supply Tracking - âœ... Lines 87-89, 163-170, 362-448 (stock, alerts, WBB)
 * - T45-004: Personnel Adequacy - âœ... Lines 173, 456-522 (calculation, dashboard, shortages)
 * - T45-005: THP Selector - âœ... Lines 71, 107, 573-595 (full Theatre Holding Policy section)
 * 
 * Purpose: User interface for logistics planning
 * - All calculations done by calculation engine modules
 * - This file handles UI/UX only
 * - No hardcoded values - everything from constants via calculation engine
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 14, 2024
 * Updated: January 11, 2025 - All nationalLogistics imports used
 * 
 * ARCHITECTURE:
 * Tab4 (UI) -> Calculation Engine (Logic) -> Constants (Data)
 * 
 * T45-004 IMPLEMENTATION:
 * - Calls PersonnelIntelligence.calculatePersonnelAdequacy()
 * - Function added to personnelIntelligence.js
 * - Uses constants from mtfCapabilities.js for staffing requirements
 * - NO hardcoded values in this file
 * 
 * German user example: Tab4 unchanged, calculation engine unchanged,
 * only nationalStandards.js needs modification
 */

import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Accordion,
  AccordionSummary, AccordionDetails, FormControl, InputLabel,
  Select, MenuItem, Switch, FormControlLabel, Alert, AlertTitle,
  Chip, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Card, CardContent, LinearProgress,
  List, ListItem, ListItemText, Tooltip, Collapse, CircularProgress
} from '@mui/material';

// MUI Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VerifiedIcon from '@mui/icons-material/Verified';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoIcon from '@mui/icons-material/Info';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';


import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// ============================================================================
// CALCULATION ENGINE IMPORTS
// ============================================================================

import * as SupplyIntelligence from '../calculationEngine/modules/supplyIntelligence';
import * as PersonnelIntelligence from '../calculationEngine/modules/personnelIntelligence';
// v3.0 PHASE 4: Import UNIT_TYPES for accurate casualty modifiers from sub-units
import { UNIT_TYPES } from '../constants/unitTypes';
import * as ValidationSystem from '../calculationEngine/modules/validationSystem';
// v5.4: Import casualtyCalculator for preliminary estimates
import { calculateCasualties } from '../calculationEngine/casualtyCalculator';
// v7.0.1 FIX: Import calculateLogisticsProjection so Tab4 can recalculate projection
// when user changes Target MDOS. Tab4 is allowed to CALL engine functions (APP_RULES 2.2
// prohibits inline formulas in UI, not calls to the calculation engine).
import { calculateLogisticsProjection, generateSchedule, STRATEGIES, calculateConvoyTravelTime } from '../calculationEngine/modules/supplyChainModule';
// L4-01: DEFAULT_CASUALTY_MIX import removed — was only used by handleCalculateFromDOS (deleted).

// ============================================================================
// CONSTANTS IMPORTS (for UI options only)
// ============================================================================

import { PUSH_PACKAGES, MDOS_STANDARDS, getPackagesForDestination, CONVOY_CAPACITY, ITEM_WEIGHTS_KG, ITEM_VOLUMES_M3, MSR_ROAD_SPEEDS, MSR_TERRAIN_MODIFIERS, MSR_SEASON_MODIFIERS, MSR_DEFAULTS, BLOOD_TRANSPORT_LIMITS, DRONE_DELIVERY_SPECS, DRONE_DEFAULT_TYPE } from '../constants/classVIII';
// PHASE 4.4: Import resupply scheduler
// PHASE 4.5: Import resupply thresholds and supply sources
import { 
  THRESHOLD_PROFILES, 
  SUPPLY_SOURCES, 
  getThresholdProfile,
  getStockoutSeverity,
  getSupplySource,
  estimateDeliveryTime,
  ASSET_STATUS_THRESHOLDS  // G-06 / C-012: four-tier supply/asset status coloring
} from '../constants/resupplyThresholds';
import { THEATRE_HOLDING_POLICY } from '../constants/theatreHoldingPolicy';
// v5.5: Import national logistics for T4-003 Target DOS feature
import {
  getNationalLogistics,
  getNationalDifferences,
  // calculateRequiredStock removed in v7.0.0 (L4-01) — was only used by handleCalculateFromDOS
  calculateResupplyNeeds,
  getPushPackageDetails,
  AVAILABLE_NATIONAL_STANDARDS
} from '../constants/nationalLogistics';

// IMP-004 (March 4, 2026): Import canonical triage fractions — replaces hardcoded 0.30/0.50/0.20.
// triageCategories.js is the single source of truth per APP_RULES 2.1.
import { TRIAGE_CATEGORIES } from '../constants/triageCategories';

// IMP-005 (March 4, 2026): Import national brigade strength — replaces hardcoded 5000.
// NATIONAL_STANDARDS.NATO.brigade.strength = 5000 (confirmed nationalStandards.js v2.3).
import { NATIONAL_STANDARDS } from '../constants/nationalStandards';
import { markDirty } from '../services/calculationService.js';
import { lsWrite, lsRead } from '../utils/lsStore.js'; // BUG-FF-012
import { useTranslation } from 'react-i18next';

// ============================================================================
// ENH-014c: Build the msrLegs array from 3 leg configs for cold chain viability check.
// Safely calls calculateConvoyTravelTime() — returns null legs on error (graceful degradation).
// @returns {Array|null} [{ one_way_hours }, { one_way_hours }, { one_way_hours }] or null
const _buildMsrLegs = (leg1Config, leg2Config, leg3Config) => {
  try {
    return [
      calculateConvoyTravelTime(leg1Config),
      calculateConvoyTravelTime(leg2Config),
      calculateConvoyTravelTime(leg3Config),
    ];
  } catch (_) {
    return null;
  }
};

// COMPONENT
// ============================================================================

const Tab4_Logistics = forwardRef(function Tab4_Logistics({ scenario, onUpdate, onComplete, onNavigate }, ref) {
  const { t } = useTranslation();

  // ============================================================================
  // TOOLBAR NAVIGATION ARCHITECTURE
  // ============================================================================

  // Stable ref for onUpdate - synced each render to avoid stale closure
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Ref to buildUpdates - populated after all state is available (each render)
  const buildUpdatesRef = useRef(null);

  // Exposed to App.jsx via tab4Ref for toolbar navigation.
  // Tab4 is Pattern B: buffers state locally, saves on explicit Save & Continue.
  // flushState saves current logistics WITHOUT triggering onComplete or onNavigate.
  useImperativeHandle(ref, () => ({
    flushState: () => {
      if (buildUpdatesRef.current && onUpdateRef.current) {
        onUpdateRef.current(buildUpdatesRef.current());
        // BUG-066: markDirty required — toolbar nav bypasses Save & Continue handler.
        markDirty();
      }
    }
  }));

  // STATE
  // =========================================================================
  
  const [expandedSections, setExpandedSections] = useState({
    supplies: true, personnel: false, thp: false, bedOccupancy: false, mentalHealth: false, supplyRoutes: false, validation: false
  });

  const [supplies, setSupplies] = useState(() => {
    // BUG-203 FIX (March 25, 2026): Lazy-initialize from persisted data so first-render
    // useMemos (stockoutAnalysis, validation) see correct values, not hardcoded defaults.
    // Priority: localStorage projection required_stock > scenario.logistics > defaults.
    const _defaults = {
      iv_fluids_liters: 500, prbc_units: 100, ffp_units: 100, platelets_units: 20,
      antibiotics_doses: 300, controlled_substances_doses: 200,
      dressings_sets: 500, oxygen_liters: 10000, anesthetics_uses: 50,
      tourniquets: 100, morphine_doses: 200
    };
    const _saved = scenario?.logistics?.medical_supplies?.initial_stock || {};
    // Mirror second useEffect (line ~1269): prefer logistics projection required_stock
    const _proj = (() => {
      try {
        const p = lsRead('psp_logistics_projection');
        const rs = p?.required_stock;
        if (!rs) return {};
        return {
          iv_fluids_liters:            rs.crystalloid             ?? undefined,
          prbc_units:                  rs.prbc                    ?? undefined,
          ffp_units:                   rs.ffp                     ?? undefined,
          platelets_units:             rs.platelets               ?? undefined,
          antibiotics_doses:           rs.antibiotics             ?? undefined,
          tourniquets:                 rs.tourniquets             ?? undefined,
          morphine_doses:              rs.analgesics              ?? undefined,
          controlled_substances_doses: rs.controlled_substances   ?? undefined,
          dressings_sets:              rs.dressings_sets          ?? undefined,
        };
      } catch { return {}; }
    })();
    // Remove undefined keys so spread doesn't overwrite with undefined
    const _projClean = Object.fromEntries(Object.entries(_proj).filter(([,v]) => v !== undefined));
    return { ..._defaults, ..._saved, ..._projClean };
  });

  // PHASE 1.0: WBB state moved to Tab 3 (medical_configuration.blood_config)
  // const [wbb, setWbb] removed - now read-only display from scenario

  const [resupplyEvents, setResupplyEvents] = useState(() =>
    scenario?.logistics?.medical_supplies?.resupply_events || []
  );
  
  // v6.3.0: Calculator integration states (mirrors Tab3 v4.1.0)
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  
  // PHASE 4.7.2: Track resupply modifications for "Return to Tab 5" prompt
  const [resupplyModified, setResupplyModified] = useState(false);

  const [personnel, setPersonnel] = useState(() => ({
    surgeons: 4, physicians: 6, nurses: 30, medics: 20, support_staff: 15,
    psychiatrists: 1, psychologists: 2, chaplains: 2,
    ...(scenario?.logistics?.personnel || {})  // BUG-203 FIX
  }));

  const [replacementPlanning, setReplacementPlanning] = useState(() => ({
    enabled: false, reserve_combat: 0, reserve_medical: 0, replacement_timeline_days: 7,
    ...(scenario?.logistics?.replacement_planning || {})  // BUG-203 FIX
  }));

  // v5.9.0: Personnel Arrivals Schedule (like resupply events for personnel)
  const [personnelArrivals, setPersonnelArrivals] = useState(() =>
    scenario?.logistics?.personnel_arrivals || []  // BUG-203 FIX
  );

  const [thpConfig, setThpConfig] = useState(() => ({
    policy: 'THP_7_DAY', strategic_evac_available: true, role3_in_theatre: true, evac_sorties_per_day: 4,
    ...(scenario?.logistics?.theatre_holding_policy || {})  // BUG-203 FIX
  }));

  const [mentalHealthConfig, setMentalHealthConfig] = useState(() => ({
    csc_team_deployed: false, restoration_beds: 10, peer_support_trained: 20,
    ...(scenario?.logistics?.mental_health || {})  // BUG-203 FIX
  }));

  const [presetDialogOpen, setPresetDialogOpen] = useState(false);

  // v7.0.1 FIX: Renamed commandedMDOS → targetMDOS. This is the user's planning input:
  // "how many days of buffer stock do I want after the operation ends?"
  // When changed, Tab4 calls calculateLogisticsProjection() and saves to localStorage.
  const [targetMDOS, setTargetMDOS] = useState(() => {
    // BUG-203 FIX: prefer projection target_mdos_days, fall back to saved commanded_mdos
    try {
      const _p = lsRead('psp_logistics_projection');
      if (_p?.target_mdos_days != null) return _p.target_mdos_days;
    } catch { /* ignore */ }
    return scenario?.logistics?.medical_supplies?.commanded_mdos || 1;
  });
  // L4-03: Logistics projection loaded from localStorage (written by Tab3 Save & Continue)
  const [logisticsProjection, setLogisticsProjection] = useState(null);
  // BUG-122 FIX: Seed from scenario force structure rather than hardcoding 'NATO'.
  // For MULTINATIONAL division builds, scenario.force has no single national_standard —
  // each brigade has its own. Use the first non-MULTINATIONAL brigade's standard as the
  // usable consumption-rate standard for Tab 4 planning.
  const _tab4InitNatStd = (() => {
    const f = scenario?.force;
    if (!f) return 'NATO';
    if (f.corpsLeadNation) return f.corpsLeadNation;
    if (f.national_standard && f.national_standard !== 'MULTINATIONAL') return f.national_standard;
    const fbr = f.forceBuilderResult;
    if (fbr?.corps?.nationalStandard && fbr.corps.nationalStandard !== 'MULTINATIONAL')
      return fbr.corps.nationalStandard;
    const firstUsable = fbr?.brigades?.find(
      b => b.nationalStandard && b.nationalStandard !== 'MULTINATIONAL'
    )?.nationalStandard;
    return firstUsable || 'NATO';
  })();
  const [nationalStandard, setNationalStandard] = useState(_tab4InitNatStd);
  const [dosHelperOpen, setDosHelperOpen] = useState(true); // v5.16: Default true - always show consumption rates

  // ARCH-002: MSR forward supply route configuration
  // BUG-081: unrefrigerated flag added to all three legs.
  // false = refrigerated vehicle available (cold chain maintained throughout leg).
  // true  = no refrigerated vehicle — cold-chain products cannot be transported on this leg
  //         regardless of travel time. Overrides the time-based viability check.
  const [msrConfig, setMsrConfig] = useState({ ...MSR_DEFAULTS, unrefrigerated: false });
  // ENH-014b Leg 2: FMSS → R2B (forward distribution, shorter, typically unimproved)
  const [msrLeg2Config, setMsrLeg2Config] = useState({
    distance_km: 15, road_type: 'UNPAVED_IMPROVED', terrain: 'ROLLING', season: 'DRY', unrefrigerated: false
  });
  // ENH-014b Leg 3: R2B → R1 (tactical resupply, shortest, most contested)
  const [msrLeg3Config, setMsrLeg3Config] = useState({
    distance_km: 5, road_type: 'UNPAVED_UNIMPROVED', terrain: 'ROLLING', season: 'DRY', unrefrigerated: false
  });

  // v5.6: T4-005/T4-006 Push package viewer state
  const [expandedPackageIndex, setExpandedPackageIndex] = useState(null);

  // PHASE 4.4: Auto-schedule state
  const [autoScheduleStrategy, setAutoScheduleStrategy] = useState('BALANCED');
  const [autoSchedulePreview, setAutoSchedulePreview] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [lastAutoSchedule, setLastAutoSchedule] = useState(null); // For revert functionality

  // PHASE 4.5: Threshold profile and supply source state
  const [thresholdProfile, setThresholdProfile] = useState('NATO'); // NATO, ROMANIAN, US

  // =========================================================================
  // LOAD FROM SCENARIO
  // =========================================================================
  
  useEffect(() => {
    if (scenario?.logistics) {
      const log = scenario.logistics;
      if (log.medical_supplies?.initial_stock) setSupplies(prev => ({ ...prev, ...log.medical_supplies.initial_stock }));
      // PHASE 1.0: WBB loading removed - now configured in Tab 3
      if (log.medical_supplies?.resupply_events) setResupplyEvents(log.medical_supplies.resupply_events);
      if (log.personnel) setPersonnel(prev => ({ ...prev, ...log.personnel }));
      if (log.replacement_planning) setReplacementPlanning(prev => ({ ...prev, ...log.replacement_planning }));
      // v5.9.0: Load personnel arrivals schedule
      if (log.personnel_arrivals) setPersonnelArrivals(log.personnel_arrivals);
      if (log.theatre_holding_policy) setThpConfig(prev => ({ ...prev, ...log.theatre_holding_policy }));
      if (log.mental_health) setMentalHealthConfig(prev => ({ ...prev, ...log.mental_health }));
      // v5.5: Load DOS configuration
      if (log.medical_supplies?.commanded_mdos) setTargetMDOS(log.medical_supplies.commanded_mdos);
      // BUG-105 FIX: Prefer saved national_standard; fall back to corpsLeadNation (ENH-018F)
      // so MULTINATIONAL corps with a lead nation does not default to 'NATO' consumption rates.
      // BUG-122 FIX: Also handle MULTINATIONAL division (no corpsLeadNation, no single standard).
      // BUG-136 FIX: Skip saved 'NATO' value when force is MULTINATIONAL and the live init
      // resolution produced a better (non-NATO) standard. Pre-BUG-122 saves always wrote
      // national_standard: 'NATO' for MULTINATIONAL forces — restoring that here overwrote
      // the corrected BUG-122 resolution on every reload.
      // Resolution logic is inlined here (mirrors _tab4InitNatStd) to avoid adding a
      // component-scope IIFE to the useEffect dep array (would cause infinite re-renders).
      const _savedStd = log.medical_supplies?.national_standard;
      const _forceIsMultinational = scenario?.force?.nationalStandard === 'MULTINATIONAL' ||
        (!scenario?.force?.corpsLeadNation && !scenario?.force?.national_standard);
      const _resolvedNatStd = (() => {
        const f = scenario?.force;
        if (!f) return 'NATO';
        if (f.corpsLeadNation) return f.corpsLeadNation;
        if (f.national_standard && f.national_standard !== 'MULTINATIONAL') return f.national_standard;
        const fbr = f.forceBuilderResult;
        if (fbr?.corps?.nationalStandard && fbr.corps.nationalStandard !== 'MULTINATIONAL')
          return fbr.corps.nationalStandard;
        const firstUsable = fbr?.brigades?.find(
          b => b.nationalStandard && b.nationalStandard !== 'MULTINATIONAL'
        )?.nationalStandard;
        return firstUsable || 'NATO';
      })();
      if (_savedStd && !(_forceIsMultinational && _savedStd === 'NATO' && _resolvedNatStd !== 'NATO')) {
        setNationalStandard(_savedStd);
      } else if (scenario?.force?.corpsLeadNation) {
        setNationalStandard(scenario.force.corpsLeadNation);
      } else {
        // MULTINATIONAL division — resolve from first brigade
        const _fbr = scenario?.force?.forceBuilderResult;
        const _firstBrigadeStd = _fbr?.brigades?.find(
          b => b.nationalStandard && b.nationalStandard !== 'MULTINATIONAL'
        )?.nationalStandard;
        if (_firstBrigadeStd) setNationalStandard(_firstBrigadeStd);
      }
      // ARCH-002: Load MSR config if previously saved
      if (log.msr_config) setMsrConfig(prev => ({ ...MSR_DEFAULTS, ...log.msr_config }));
      if (log.msr_leg2_config) setMsrLeg2Config(prev => ({ ...prev, ...log.msr_leg2_config }));
      if (log.msr_leg3_config) setMsrLeg3Config(prev => ({ ...prev, ...log.msr_leg3_config }));
    }
  }, [scenario]);

  // D-02/D-08: Load logistics projection from localStorage on mount and on Tab3 recalculate.
  // Tab4 Save & Continue writes 'psp_logistics_projection' (D-08 — Tab4 is sole owner per ARCH-009).
  // Also re-fires when Tab3 recalculates (scenario.calculated_at changes) so the display stays fresh
  // even before the user hits Tab4 Save & Continue for the first time in a session.
  useEffect(() => {
    const proj = lsRead('psp_logistics_projection');
    if (!proj) {
      setLogisticsProjection(null);
      return;
    }
    try {
      setLogisticsProjection(proj);
      // v7.0.1: Restore targetMDOS from projection (renamed from commandedMDOS)
      if (proj.target_mdos_days != null) {
        setTargetMDOS(proj.target_mdos_days);
      }
      // L4-04: Populate supply display fields from projection.required_stock (read-only)
      // Key mapping: supplyChainModule output key → Tab4 supplies state key
      if (proj.required_stock) {
        const rs = proj.required_stock;
        setSupplies(prev => ({
          ...prev,
          iv_fluids_liters:            rs.crystalloid          ?? prev.iv_fluids_liters,
          prbc_units:                  rs.prbc                 ?? prev.prbc_units,
          ffp_units:                   rs.ffp                  ?? prev.ffp_units,
          platelets_units:             rs.platelets            ?? prev.platelets_units,
          antibiotics_doses:           rs.antibiotics          ?? prev.antibiotics_doses,
          tourniquets:                 rs.tourniquets          ?? prev.tourniquets,
          morphine_doses:              rs.analgesics           ?? prev.morphine_doses,
          // ISSUE-01 FIX: three previously unmapped fields now wired from required_stock
          controlled_substances_doses: rs.controlled_substances ?? prev.controlled_substances_doses,
          dressings_sets:              rs.dressings_sets        ?? prev.dressings_sets,
          // surgical_kits removed — US Army ATP 4-02.25 only, not NATO doctrine
        }));
      }
      // Sync nationalStandard display from projection
      if (proj.national_standard) setNationalStandard(proj.national_standard);
    } catch (e) {
      // Invalid JSON in localStorage — clear projection state
      setLogisticsProjection(null);
    }
  }, [scenario?.calculated_at]); // Re-fires whenever Tab3 runs Save & Continue
  // =========================================================================
  
  // v5.4: Run preliminary casualty estimate for RTD/CSR calculations
  // This gives Tab 4 access to casualty projections before Tab 5 runs the full calculator
  // v6.5.1: Added guard to prevent running before Tab2 completion
  // v6.5.2: Added guard to prevent running before Tab3 completion
  const preliminaryCasualties = useMemo(() => {
    // GUARD 1: Don't run calculator if operation not configured yet (Tab2 incomplete)
    if (!scenario?.operation_parameters?.duration_days) {
      return null; // Tab2 not complete, skip calculation
    }
    
    // GUARD 2: Don't run calculator if medical config not complete yet (Tab3 incomplete)
    // v6.5.2: This prevents calculator from running at Tab3 entry with incomplete medical data
    // Calculator should only run at Tab4 entry after Tab3 has configured medical parameters
    if (!scenario?.medical_configuration?.configured) {
      return null; // Tab3 not complete, skip calculation
    }
    
    try {
      if (calculateCasualties) {
        const result = calculateCasualties(scenario);
        return result;
      }
    } catch (e) {
      console.error('Tab4 preliminaryCasualties error:', e);
    }
    return null;
  }, [scenario]);

  // v5.6.9: Fallback casualty estimates for RTD when calculator returns 0
  // Uses simple historical rates: ~1-2% battle casualties per day for intense combat
  // Plus DNBI rates for more reliable estimates
  const fallbackCasualtyProjection = useMemo(() => {
    // v5.7.3: Use same force strength lookup pattern as supplyIntelligence.js
    const forceStrength = scenario?.force?.totalStrength || 
                          scenario?.force?.total_strength || 
                          scenario?.force?.forceBuilderResult?.corps?.strength ||
                          0;
    const duration = scenario?.operation_parameters?.duration_days || 7;
    
    if (forceStrength === 0) return null;
    
    // Conservative estimate: 0.5% daily battle casualties, 0.3% DNBI
    const dailyBattleRate = 0.005;  // 0.5% per day
    const dailyDNBIRate = 0.003;    // 0.3% per day (disease, injury)
    
    const totalBattleCasualties = Math.round(forceStrength * dailyBattleRate * duration);
    const totalDNBI = Math.round(forceStrength * dailyDNBIRate * duration);
    const totalCasualties = totalBattleCasualties + totalDNBI;
    
    // Distribution by triage (based on Ukraine data)
    // T1: 15%, T2: 25%, T3: 55%, T4: 5%
    return {
      total: totalCasualties,
      daily_average: Math.round(totalCasualties / duration),
      by_triage: {
        t1: Math.round(totalCasualties * 0.15),
        t2: Math.round(totalCasualties * 0.25),
        t3: Math.round(totalCasualties * 0.55),
        t4: Math.round(totalCasualties * 0.05)
      },
      is_estimate: true,
      source: <strong> Fallback estimate based on historical rates </strong>
    };
  }, [scenario?.force?.totalStrength, scenario?.force?.total_strength, scenario?.force?.forceBuilderResult?.corps?.strength, scenario?.operation_parameters?.duration_days]);

  const currentScenario = useMemo(() => {
    // Use preliminary casualties if available, otherwise use fallback or existing projections
    let casualtyData = null;
    
    if (preliminaryCasualties?.summary?.total_casualties > 0) {
      casualtyData = {
        total: preliminaryCasualties.summary.total_casualties,
        daily_average: preliminaryCasualties.summary.daily_average || 0,
        by_triage: {
          t1: preliminaryCasualties.summary?.by_triage?.T1 || 0,
          t2: preliminaryCasualties.summary?.by_triage?.T2 || 0,
          t3: preliminaryCasualties.summary?.by_triage?.T3 || 0,
          t4: preliminaryCasualties.summary?.by_triage?.T4 || 0
        }
      };
    } else if (scenario?.casualty_projections?.total > 0) {
      casualtyData = scenario.casualty_projections;
    } else if (fallbackCasualtyProjection) {
      // v5.6.9: Use fallback estimates so RTD isn't 0
      casualtyData = fallbackCasualtyProjection;
    }
    
    return {
      ...scenario,
      casualty_projections: casualtyData,
      logistics: {
        medical_supplies: { initial_stock: supplies, resupply_events: resupplyEvents },
        // PHASE 1.0: walking_blood_bank removed - now in medical_configuration.blood_config
        personnel, replacement_planning: replacementPlanning,
        personnel_arrivals: personnelArrivals,  // v5.9.0: Personnel arrivals schedule
        // BUG-140 FIX: include policy_days for Tab4 internal RTD display consistency
        theatre_holding_policy: {
          ...thpConfig,
          policy_days: THEATRE_HOLDING_POLICY[thpConfig.policy]?.days ?? 7
        },
        mental_health: mentalHealthConfig
      }
    };
  }, [scenario, supplies, resupplyEvents, personnel, replacementPlanning, personnelArrivals, thpConfig, mentalHealthConfig, preliminaryCasualties, fallbackCasualtyProjection]);

  // MDOS Analysis (from Calculation Engine)
  // BUG FIX (Feb 12, 2026): Don't run if operation not configured (prevents errors in Tab1/Tab2)
  // BUG FIX (Mar 2, 2026 — MDOS-001): Don't run if Tab3 not yet saved.
  // medical_configuration is written by Tab3 Save & Continue. Without it, MDOS = 0 (no supply data)
  // which logged a misleading "✅ Validation calculated MDOS: 0 days" on Tab3 entry because Tab4
  // useMemos re-run whenever currentScenario changes (all tabs are mounted simultaneously).
  const mdosAnalysis = useMemo(() => {
    if (!currentScenario?.operation_parameters?.duration_days) {
      return null; // Operation not configured yet (Tab1/Tab2 incomplete)
    }
    if (!currentScenario?.medical_configuration) {
      return null; // Tab3 not yet saved — no supply data to analyse
    }
    return SupplyIntelligence.calculateScenarioMDOS(currentScenario);
  }, [currentScenario]);

  // RTD Projection (from Calculation Engine)
  // BUG FIX (Feb 12, 2026): Don't run if operation not configured (prevents errors in Tab1/Tab2)
  const rtdProjection = useMemo(() => {
    if (!currentScenario?.operation_parameters?.duration_days) {
      return null; // Operation not configured yet, skip calculation
    }
    return PersonnelIntelligence.calculateRTDProjection(currentScenario);
  }, [currentScenario]);

  // Bed Occupancy (from Calculation Engine)
  // BUG FIX (Feb 12, 2026): Don't run if operation not configured (prevents errors in Tab1/Tab2)
  // BUG FIX (Mar 2, 2026 — BED-001): Don't run if Tab3 not yet saved.
  // calculateBedOccupancyAnalysis reads medical_configuration.facilities/mtf_list/mtf_structure —
  // all written by Tab3 Save & Continue. Without them all 4 priority paths return empty and the
  // function falls through to a force-echelon estimate, logging a spurious
  // "📊 Bed Occupancy: Using fallback estimate" on every Tab3 entry.
  const bedOccupancy = useMemo(() => {
    if (!currentScenario?.operation_parameters?.duration_days) {
      return null; // Operation not configured yet (Tab1/Tab2 incomplete)
    }
    if (!currentScenario?.medical_configuration) {
      return null; // Tab3 not yet saved — no MTF data to calculate bed occupancy from
    }
    return PersonnelIntelligence.calculateBedOccupancyAnalysis(currentScenario);
  }, [currentScenario]);

  // CSR Projection (from Calculation Engine)
  // BUG FIX (Feb 12, 2026): Don't run if operation not configured (prevents errors in Tab1/Tab2)
  const csrProjection = useMemo(() => {
    if (!currentScenario?.operation_parameters?.duration_days) {
      return null; // Operation not configured yet, skip calculation
    }
    return PersonnelIntelligence.calculateCSRProjection(currentScenario);
  }, [currentScenario]);

  // Validation (from Calculation Engine)
  // BUG FIX (Feb 12, 2026): Don't run if operation not configured (prevents errors in Tab1/Tab2)
  const validation = useMemo(() => {
    if (!currentScenario?.operation_parameters?.duration_days) {
      return null; // Operation not configured yet, skip validation
    }
    return ValidationSystem.runCompleteValidation(currentScenario);
  }, [currentScenario]);

  // Blood Alert Level - BUG FIX (Feb 12, 2026): Use calculator supply_chain data
  // OLD: Only checked PRBC, showed GREEN even when platelets critically low
  // NEW: Checks ALL blood products (PRBC, FFP, Platelets, Cryo), uses minimum days
  const bloodAlertLevel = useMemo(() => {
    const supplyChain = scenario?.casualty_projections?.supply_chain;
    
    // If no calculator results, use fallback calculation
    if (!supplyChain?.days_remaining) {
      const avgDailyCasualties = (scenario?.casualty_projections?.total || 100) / (scenario?.operation_parameters?.duration_days || 7);
      const bloodDays = supplies.prbc_units / (avgDailyCasualties * 2);
      if (bloodDays <= 3) return { level: 'RED', color: 'error' };
      if (bloodDays <= 5) return { level: 'YELLOW', color: 'warning' };
      if (bloodDays <= 7) return { level: 'BLUE', color: 'info' };
      return { level: 'GREEN', color: 'success' };
    }
    
    // Use calculator's detailed stockout predictions for ALL blood products
    // BUG-202 FIX: days_remaining values are plain numbers; .days accessor lives on
    // days_remaining_enhanced (supplyKeyMap: prbc→prbc_units, platelets→platelets_units etc.)
    // Old: days_remaining['PRBC']?.days — uppercase key + wrong accessor → always Infinity → UNKNOWN
    const bloodEnhancedKeys = ['prbc_units', 'ffp_units', 'platelets_units', 'cryoprecipitate_units'];
    const daysRemaining = bloodEnhancedKeys
      .map(key => supplyChain.days_remaining_enhanced?.[key]?.days ?? Infinity)
      .filter(days => days !== Infinity && days < 9999);
    
    // No blood data available
    if (daysRemaining.length === 0) {
      return { level: 'UNKNOWN', color: 'default' };
    }
    
    // Minimum days across all blood products (platelets often the limiting factor)
    const minDays = Math.min(...daysRemaining);
    
    // Critical thresholds based on NATO doctrine
    if (minDays <= 3) return { level: 'RED', color: 'error' };       // Critical - immediate resupply
    if (minDays <= 5) return { level: 'YELLOW', color: 'warning' };  // Warning - plan resupply
    if (minDays <= 7) return { level: 'BLUE', color: 'info' };       // Monitor - approaching threshold
    return { level: 'GREEN', color: 'success' };                      // Adequate
  }, [scenario?.casualty_projections, scenario?.operation_parameters?.duration_days, supplies.prbc_units]);

  // T45-004: Personnel Adequacy Check (from Calculation Engine)
  // BUG FIX (Feb 12, 2026): Don't run if operation not configured (prevents errors in Tab1/Tab2)
  const personnelAdequacy = useMemo(() => {
    if (!currentScenario?.operation_parameters?.duration_days) {
      return null; // Operation not configured yet, skip calculation
    }
    return PersonnelIntelligence.calculatePersonnelAdequacy(currentScenario);
  }, [currentScenario]);

  // v5.11.0: T4-015 FIX - Computed evacuations with multiple fallback sources
  // Root cause: rtdProjection.evacuations may be number, object, or missing
  const evacuationsCount = useMemo(() => {
    // Priority 1: rtdProjection.evacuations?.total (object structure)
    if (rtdProjection?.evacuations?.total > 0) {
      return rtdProjection.evacuations.total;
    }
    // Priority 2: rtdProjection.evacuations (direct number)
    if (typeof rtdProjection?.evacuations === 'number' && rtdProjection.evacuations > 0) {
      return rtdProjection.evacuations;
    }
    // Priority 3: rtdProjection.strategic_evacuations
    if (rtdProjection?.strategic_evacuations > 0) {
      return rtdProjection.strategic_evacuations;
    }
    // Priority 4: preliminaryCasualties.summary.evacuations
    if (preliminaryCasualties?.summary?.evacuations > 0) {
      return preliminaryCasualties.summary.evacuations;
    }
    // Priority 5: Calculate from RTD data (Total - RTD - Deaths)
    const totalCasualties = currentScenario?.casualty_projections?.total || 
                            preliminaryCasualties?.summary?.total_casualties || 0;
    const totalRTD = rtdProjection?.total_rtd || 0;
    const totalDeaths = preliminaryCasualties?.summary?.total_deaths || 
                        currentScenario?.casualty_projections?.deaths || 0;
    
    if (totalCasualties > 0 && totalRTD > 0) {
      // Evacuations = Casualties that don't RTD and don't die
      const calculatedEvac = Math.max(0, totalCasualties - totalRTD - totalDeaths);
      // Apply THP-based estimation: ~30-60% of non-RTD casualties need strategic evac
      const evacuationRate = thpConfig.strategic_evac_available ? 0.5 : 0.3;
      return Math.round(calculatedEvac * evacuationRate);
    }
    
    return 0;
  }, [rtdProjection, preliminaryCasualties, currentScenario?.casualty_projections, thpConfig.strategic_evac_available]);

  // v5.11.0: T4-016 FIX - Bed occupancy analysis with MASCAL indicators
  const bedOccupancyAnalysis = useMemo(() => {
    const rate = bedOccupancy?.occupancy_rate || 0;
    const rounded = Math.round(rate);
    
    let color = 'success';
    let status = 'ADEQUATE';
    let isMASCAL = false;
    let tooltip = 'Bed utilization is within normal parameters.';
    
    if (rounded >= 120) {
      color = 'error';
      status = 'CRITICAL';
      isMASCAL = true;
      tooltip = `MASCAL CONDITION: Demand exceeds capacity by ${rounded - 100}%. Activate surge protocols, consider patient redistribution.`;
    } else if (rounded >= 100) {
      color = 'warning';
      status = 'SURGE';
      isMASCAL = true;
      tooltip = `SURGE: Demand equals/exceeds capacity. Monitor closely and prepare contingency plans.`;
    } else if (rounded >= 80) {
      color = 'warning';
      status = 'NEAR CAPACITY';
      tooltip = 'Approaching capacity threshold. Plan for potential surge.';
    }
    
    return {
      rate: rounded,
      color,
      status,
      isMASCAL,
      tooltip
    };
  }, [bedOccupancy?.occupancy_rate]);

  // v5.5: T4-003 National logistics differences
  const nationalDifferences = useMemo(() => {
    return getNationalDifferences(nationalStandard);
  }, [nationalStandard]);

  // v5.6: Get full national logistics config for display
  const currentNationalConfig = useMemo(() => {
    return getNationalLogistics(nationalStandard);
  }, [nationalStandard]);

  // =========================================================================
  // HYBRID IV CONSUMPTION CALCULATION (Educational Progression)
  // =========================================================================
  
  /**
   * Calculate average IV consumption per casualty using hybrid approach
   * Priority: Final results -> Preliminary estimate -> NATO standard
   * Educational: Shows planning progression from doctrine to precise data
   */
  const averageIVConsumption = useMemo(() => {
    const config = currentNationalConfig?.consumption_rates;
    if (!config) return { value: '?', source: 'unavailable' };
    
    // Priority 1: Use final casualty results from Tab 5 (most accurate)
    let t1 = scenario?.casualty_results?.by_triage?.T1;
    let t2 = scenario?.casualty_results?.by_triage?.T2;
    let t3 = scenario?.casualty_results?.by_triage?.T3;
    
    if (t1 !== undefined && t2 !== undefined && t3 !== undefined) {
      const total = t1 + t2 + t3;
      if (total > 0) {
        const totalIV = (
          t1 * config.iv_fluids_per_t1 +
          t2 * config.iv_fluids_per_t2 +
          t3 * config.iv_fluids_per_t3
        );
        return {
          value: (totalIV / total).toFixed(1),
          source: 'calculated'
        };
      }
    }
    
    // Priority 2: Use preliminary casualties from Tab 4 (rough estimate)
    if (preliminaryCasualties?.summary?.by_triage) {
      t1 = preliminaryCasualties.summary.by_triage.T1 || 0;
      t2 = preliminaryCasualties.summary.by_triage.T2 || 0;
      t3 = preliminaryCasualties.summary.by_triage.T3 || 0;
      
      const total = t1 + t2 + t3;
      if (total > 0) {
        const totalIV = (
          t1 * config.iv_fluids_per_t1 +
          t2 * config.iv_fluids_per_t2 +
          t3 * config.iv_fluids_per_t3
        );
        return {
          value: (totalIV / total).toFixed(1),
          source: 'estimated'
        };
      }
    }
    
    // Priority 3: Use NATO standard casualty distribution from triageCategories.js (IMP-004).
    // Old hardcoded: { t1: 0.30, t2: 0.50, t3: 0.20 } — replaced with canonical source.
    // New values: T1=0.25, T2=0.40, T3=0.30 (T4=0.05 excluded — no IV treatment for T4).
    const natoMix = {
      t1: TRIAGE_CATEGORIES.T1_IMMEDIATE.baseline_percentage,
      t2: TRIAGE_CATEGORIES.T2_DELAYED.baseline_percentage,
      t3: TRIAGE_CATEGORIES.T3_MINIMAL.baseline_percentage
    };
    const standardIV = (
      config.iv_fluids_per_t1 * natoMix.t1 +
      config.iv_fluids_per_t2 * natoMix.t2 +
      config.iv_fluids_per_t3 * natoMix.t3
    );
    
    return {
      value: standardIV.toFixed(1),
      source: 'standard'
    };
  }, [currentNationalConfig, scenario, preliminaryCasualties]);

  // v5.5: T4-004 Resupply needs calculation
  // BUG-MDOS-002 FIX: Use actual current stock DOS for resupply_by_day calculation, not targetMDOS.
  // targetMDOS is the COMMANDED target (what you want). The "Order by Day X" badge answers:
  //   "given your actual on-hand stock, when will you run out minus safety margin?"
  // If actual stock=10d and targetMDOS=7d, stockout is day 10 → order by day 8, not day 5.
  // Priority: logisticsProjection.overall_mdos (calculator-derived) →
  //           mdosAnalysis.overall_mdos (SupplyIntelligence estimate) →
  //           targetMDOS fallback (when neither is available — conservative).
  // Removed || 7 fallback: if opDuration unknown, return early rather than silently
  // comparing against 7, which produced false 'No resupply needed' for 7-day DOS.
  const resupplyNeeds = useMemo(() => {
    const operationDuration = scenario?.operation_parameters?.duration_days;
    if (!operationDuration) return { needs_resupply: false, resupply_by_day: null };
    const actualCurrentDOS = logisticsProjection?.overall_mdos
      ?? mdosAnalysis?.overall_mdos
      ?? targetMDOS;
    return calculateResupplyNeeds(actualCurrentDOS, operationDuration, 2);
  }, [targetMDOS, scenario, logisticsProjection, mdosAnalysis]);

  // v5.6: T4-007 Personnel breakdown by unit type
  const personnelBreakdownByUnit = useMemo(() => {
    const force = scenario?.force;
    if (!force?.units || !preliminaryCasualties) return null;

    const totalCasualties = preliminaryCasualties?.summary?.total_casualties || 0;
    const duration = scenario?.operation_parameters?.duration_days || 7;
    const dailyCasualtyRate = duration > 0 ? totalCasualties / duration : 0;
    
    // Calculate casualties per unit based on type and strength
    const unitBreakdown = [];
    let totalStrength = 0;
    
    // Process units from force structure
    // v3.0 PHASE 4: Helper to get casualty modifier from UNIT_TYPES
    const getCasualtyModifier = (unitType) => {
      if (!unitType) return 1.0;
      const unitTypeDef = UNIT_TYPES[unitType];
      if (!unitTypeDef) {
        return 1.0;
      }
      return unitTypeDef.casualty_modifier || 1.0;
    };
    
    const processUnits = (units, parentName = '') => {
      if (!units || !Array.isArray(units)) return;
      
      units.forEach(unit => {
        const unitStrength = unit.strength || unit.personnel || 0;
        
        // v3.0 PHASE 4: Check if unit has sub_units (brigade with doctrinal composition)
        if (unit.sub_units && Array.isArray(unit.sub_units) && unit.sub_units.length > 0) {
          
          // Process each sub-unit with its own casualty modifier
          unit.sub_units.forEach(subUnit => {
            const subUnitStrength = subUnit.strength || 0;
            totalStrength += subUnitStrength;
            
            const modifier = getCasualtyModifier(subUnit.unit_type);
            const subUnitCasualties = Math.round(
              (subUnitStrength / (force.total_strength || 5000)) * totalCasualties * modifier
            );
            const casualtyPercent = subUnitStrength > 0 ? 
              Math.round((subUnitCasualties / subUnitStrength) * 100) : 0;
            const dailySubUnitCasualties = duration > 0 ? 
              Math.round(subUnitCasualties / duration * 10) / 10 : 0;
            
            if (subUnitStrength > 100) {
              unitBreakdown.push({
                name: subUnit.name || `${unit.name} ${subUnit.unit_type}`,
                type: subUnit.unit_type || 'UNKNOWN',
                strength: subUnitStrength,
                projected_casualties: subUnitCasualties,
                daily_casualties: dailySubUnitCasualties,
                casualty_percent: casualtyPercent,
                replacements_needed: subUnitCasualties,
                priority: casualtyPercent > 15 ? 'HIGH' : casualtyPercent > 10 ? 'MEDIUM' : 'LOW',
                parent_brigade: unit.name,
                casualty_modifier: modifier,
                is_sub_unit: true
              });
            }
          });
        } else {
          // LEGACY PATH: Unit without sub_units
          totalStrength += unitStrength;
          
          const modifier = getCasualtyModifier(unit.type || unit.unit_type);
          const unitCasualties = Math.round((unitStrength / (force.total_strength || 5000)) * totalCasualties * modifier);
          const casualtyPercent = unitStrength > 0 ? Math.round((unitCasualties / unitStrength) * 100) : 0;
          const dailyUnitCasualties = duration > 0 ? Math.round(unitCasualties / duration * 10) / 10 : 0;
          
          if (unitStrength > 100) {
            unitBreakdown.push({
              name: unit.name || `${parentName} ${unit.type}`,
              type: unit.type || unit.unit_type || 'UNKNOWN',
              strength: unitStrength,
              projected_casualties: unitCasualties,
              daily_casualties: dailyUnitCasualties,
              casualty_percent: casualtyPercent,
              replacements_needed: unitCasualties,
              priority: casualtyPercent > 15 ? 'HIGH' : casualtyPercent > 10 ? 'MEDIUM' : 'LOW',
              casualty_modifier: modifier,
              is_sub_unit: false
            });
          }
          
          // Process sub-units (legacy field names)
          if (unit.battalions) processUnits(unit.battalions, unit.name);
          if (unit.companies) processUnits(unit.companies, unit.name);
          if (unit.subunits) processUnits(unit.subunits, unit.name);
        }
      });
    };
    
    // Process brigades and their units
    if (force.brigades) processUnits(force.brigades);
    if (force.units) processUnits(force.units);
    
    // Sort by replacements needed (highest first)
    unitBreakdown.sort((a, b) => b.replacements_needed - a.replacements_needed);
    
    // Calculate reserve coverage
    const totalReplacements = unitBreakdown.reduce((sum, u) => sum + u.replacements_needed, 0);
    const reservePool = (replacementPlanning.reserve_combat || 0) + (replacementPlanning.reserve_medical || 0);
    const coveragePercent = totalReplacements > 0 ? Math.round((reservePool / totalReplacements) * 100) : 100;
    
    
    return {
      units: unitBreakdown.slice(0, 10), // Top 10 units
      total_replacements: totalReplacements,
      reserve_pool: reservePool,
      coverage_percent: coveragePercent,
      shortfall: Math.max(0, totalReplacements - reservePool),
      duration: duration,
      daily_casualty_rate: dailyCasualtyRate,
      total_strength: totalStrength
    };
  }, [scenario, preliminaryCasualties, replacementPlanning]);

  // v5.8.0: DC-004 Medical facilities with evacuation distances
  // v5.10.0: FIX T4-012 - Added medical_configuration.facilities (Tab 3 save path)
  const medicalFacilities = useMemo(() => {
    // Try multiple sources for medical facilities
    // T4-012 FIX: Tab 3 saves to medical_configuration.facilities, not medical.facilities
    const facilities = 
      scenario?.medical_configuration?.facilities ||  // Tab 3 save path (PRIMARY)
      scenario?.medical?.facilities ||                // Legacy path
      scenario?.force?.forceBuilderResult?.facilities || // ForceBuilder original
      scenario?.force?.facilities ||                  // Force data path
      [];
    
    if (!facilities || facilities.length === 0) return null;
    
    // Calculate statistics
    const preSurgicalFacilities = facilities.filter(f => 
      f.type === 'ROLE1' || f.type === 'ROLE2_BASIC'
    );
    
    const goldenHourCompliant = preSurgicalFacilities.filter(f => 
      f.evacuation?.golden_hour_compliant === true
    ).length;
    
    const goldenHourNonCompliant = preSurgicalFacilities.filter(f => 
      f.evacuation?.golden_hour_compliant === false
    ).length;
    
    const totalDistance = facilities.reduce((sum, f) => 
      sum + (f.evacuation?.distance_km || 0), 0
    );
    
    const avgDistance = facilities.length > 0 
      ? Math.round(totalDistance / facilities.length * 10) / 10 
      : 0;
    
    const avgTime = facilities.reduce((sum, f) => 
      sum + (f.evacuation?.estimated_time_min || 0), 0
    ) / (facilities.length || 1);
    
    // Group by type
    const byType = {
      ROLE1: facilities.filter(f => f.type === 'ROLE1'),
      ROLE2_BASIC: facilities.filter(f => f.type === 'ROLE2_BASIC'),
      ROLE2_ENHANCED: facilities.filter(f => f.type === 'ROLE2_ENHANCED'),
      ROLE3: facilities.filter(f => f.type === 'ROLE3'),
      ROLE4: facilities.filter(f => f.type === 'ROLE4')
    };
    
    return {
      all: facilities,
      byType,
      stats: {
        total: facilities.length,
        preSurgical: preSurgicalFacilities.length,
        goldenHourCompliant,
        goldenHourNonCompliant,
        goldenHourPercent: preSurgicalFacilities.length > 0 
          ? Math.round((goldenHourCompliant / preSurgicalFacilities.length) * 100)
          : 100,
        avgDistance,
        avgTime: Math.round(avgTime)
      }
    };
  }, [scenario]);

  // v6.0.3: FIX - Check forceBuilderResult.total_strength FIRST (the correct value)
  // CRITICAL: forceBuilderResult.total_strength is calculated by ForceService (36000)
  // totalStrength is calculated by old broken Tab1 code (2000)
  const forceStrength = useMemo(() => {
    // Priority 1: forceBuilderResult.total_strength (sum of all brigades - CORRECT)
    if (scenario?.force?.forceBuilderResult?.total_strength) {
      return scenario.force.forceBuilderResult.total_strength;  //  36000
    }
    
    // Priority 2: Calculate from forceBuilderResult structure if total_strength missing
    if (scenario?.force?.forceBuilderResult) {
      const fb = scenario.force.forceBuilderResult;
      let total = 0;
      
      // Add Corps HQ strength
      if (fb.corps?.strength) {
        total += fb.corps.strength;
      }
      
      // Add all Division strengths
      if (fb.divisions && Array.isArray(fb.divisions)) {
        fb.divisions.forEach(div => {
          if (div.strength) total += div.strength;
        });
      }
      
      // Add all Brigade strengths
      if (fb.brigades && Array.isArray(fb.brigades)) {
        fb.brigades.forEach(bde => {
          if (bde.strength) total += bde.strength;
        });
      }
      
      // Only return if we found actual data
      if (total > 0) return total;
    }
    
    // Priority 3: Legacy fields (last resort)
    if (scenario?.force?.total_strength) return scenario.force.total_strength;
    if (scenario?.force?.totalStrength) return scenario.force.totalStrength;  // [WARN] May be wrong (2000)
    
    // Priority 4: No force data available
    return undefined;
  }, [scenario?.force]);

  // =========================================================================
  // HANDLERS
  // =========================================================================
  
  // PHASE 1.0: handleWbbChange removed - WBB now configured in Tab 3
  // v7.0.0 L4-04: handleSupplyChange removed - supply fields are read-only (populated from projection)

  // BUG-T20 fix: use parseInt (not Number) so personnel counts stay whole numbers
  const handlePersonnelChange = useCallback((field, value) => {
    setPersonnel(prev => ({ ...prev, [field]: parseInt(value, 10) || 0 }));
  }, []);

  const handleThpChange = useCallback((field, value) => {
    setThpConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleMentalHealthChange = useCallback((field, value) => {
    setMentalHealthConfig(prev => ({ ...prev, [field]: field === 'csc_team_deployed' ? value : parseInt(value, 10) || 0 }));
  }, []);

  const handleAddResupply = useCallback(() => {
    // BUG-156 FIX: Clamp default day so auto-generated events start within duration.
    const _addDuration = scenario?.operation_parameters?.duration_days || 30;
    setResupplyEvents(prev => [...prev, { 
      day: Math.min((prev.length + 1) * 7, _addDuration), 
      delivery_method: 'GROUND', 
      push_package: '', 
      supplies: {},
      // PHASE 4.0.1: Enhanced resupply event structure
      source: 'ROLE_3',              // Where supplies come from
      mtf_target: 'ALL',             // Which MTF receives (ALL = distributed to all)
      destination_brigade: 'ALL',    // Which brigade (ALL = distributed across force)
      // ENH-008: drone delivery fields (only used when delivery_method === 'DRONE')
      drone_type: DRONE_DEFAULT_TYPE,
      ad_threat_override: null       // null = use scenario AD threat
    }]);
    setResupplyModified(true); // PHASE 4.7.2: Track modification
  }, [scenario?.operation_parameters?.duration_days]);

  const handleRemoveResupply = useCallback((index) => {
    setResupplyEvents(prev => prev.filter((_, i) => i !== index));
    setResupplyModified(true); // PHASE 4.7.2: Track modification
  }, []);

  const handleResupplyChange = useCallback((index, field, value) => {
    setResupplyEvents(prev => {
      const updated = [...prev];
      // BUG-156 FIX: Clamp day to [1, operation duration] before storing.
      // Without this, any integer was accepted — values beyond duration were silently
      // stored, causing the calculator to receive events outside the simulation window.
      if (field === 'day') {
        const opDuration = scenario?.operation_parameters?.duration_days || 30;
        const clamped = Math.max(1, Math.min(Number(value), opDuration));
        updated[index] = { ...updated[index], day: clamped };
        return updated;
      }
      if (field === 'push_package' && value && PUSH_PACKAGES?.[value]) {
        updated[index] = { ...updated[index], [field]: value, supplies: { ...PUSH_PACKAGES[value].contents } };
      } else if (field === 'mtf_target') {
        // PKG-004: Auto-clear push_package if it is no longer valid for the new destination.
        // Prevents silent zero-blood events from persisting after destination change.
        const currentPackage = updated[index].push_package;
        const validPackages = getPackagesForDestination(value);
        const packageStillValid = !currentPackage || currentPackage in validPackages;
        updated[index] = {
          ...updated[index],
          [field]: value,
          ...(packageStillValid ? {} : { push_package: '', supplies: {} })
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
    setResupplyModified(true); // PHASE 4.7.2: Track modification
  }, [scenario?.operation_parameters?.duration_days]);

  // v5.9.0: Personnel Arrivals Schedule handlers
  // v5.10.0: T4-014 Added destination field for personnel allocation
  const handleAddPersonnelArrival = useCallback(() => {
    const duration = scenario?.operation_parameters?.duration_days || 30;
    setPersonnelArrivals(prev => [...prev, { 
      id: Date.now(), 
      day: Math.min((prev.length + 1) * 5, duration), 
      quantity: 50, 
      type: 'COMBAT',
      destination: 'ALL_UNITS'  // T4-014: Default to distributed allocation
    }]);
  }, [scenario?.operation_parameters?.duration_days]);

  const handleRemovePersonnelArrival = useCallback((index) => {
    setPersonnelArrivals(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handlePersonnelArrivalChange = useCallback((index, field, value) => {
    setPersonnelArrivals(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // =========================================================================
  // PHASE 4.0.4: STOCKOUT PREDICTION LOGIC
  // =========================================================================
  
  /**
   * Calculate predicted stockout days for blood products
   * @returns {Array} Array of predictions for products at risk
   */
  // =========================================================================
  // PHASE 4.3: REAL-TIME FEEDBACK & DATA CONSISTENCY
  // =========================================================================
  
  /**
   * Check if calculator has run and return data source metadata
   * Priority: Calculator results > Preliminary estimates > Fallback
   */
  const getConsumptionDataSource = useCallback(() => {
    // PHASE 4.3.1: Check if calculator has run
    // BUG-202 FIX: Calculator writes to casualty_projections.medical_requirements.resupply_summary.
    // daily_consumption — NOT to scenario.medical_requirements.blood.daily_requirements (stale path).
    const calcDailyConsumption = scenario?.casualty_projections?.medical_requirements?.resupply_summary?.daily_consumption;
    
    if (calcDailyConsumption?.prbc > 0) {
      return {
        source: 'CALCULATED',
        label: 'Calculated',
        color: 'success',
        badge: 'CALCULATED',
        data: calcDailyConsumption
      };
    } else if (preliminaryCasualties?.summary?.daily_average > 0) {
      return {
        source: 'PRELIMINARY',
        label: 'Preliminary Estimate',
        color: 'warning',
        badge: 'PRELIMINARY',
        data: null // Will use hardcoded rates with preliminary casualties
      };
    } else {
      return {
        source: 'FALLBACK',
        label: 'NATO Standard',
        color: 'info',
        badge: 'ESTIMATE',
        data: null
      };
    }
  }, [scenario, preliminaryCasualties]);

  const calculateStockoutPredictions = useCallback(() => {
    const duration = scenario?.operation_parameters?.duration_days || 7;
    const dataSource = getConsumptionDataSource();
    
    // Calculate daily consumption based on data source
    let dailyConsumption = {
      prbc: 0,
      ffp: 0,
      plt: 0,
      cryo: 0
    };
    
    if (dataSource.source === 'CALCULATED' && dataSource.data) {
      // PHASE 4.3.1: Use actual calculator results
      // BUG-202 FIX: resupply_summary.daily_consumption keys are prbc/ffp/plt/cryo.
      // Old: dataSource.data.prbcs (trailing s), dataSource.data.platelets (wrong key) → 0
      dailyConsumption = {
        prbc: dataSource.data.prbc  || 0,
        ffp:  dataSource.data.ffp   || 0,
        plt:  dataSource.data.plt   || 0,
        cryo: dataSource.data.cryo  || 0
      };
    } else {
      // Use preliminary estimates with NATO average consumption rates
      let avgDailyCasualties;
      if (preliminaryCasualties?.summary?.daily_average > 0) {
        avgDailyCasualties = preliminaryCasualties.summary.daily_average;
      } else if (preliminaryCasualties?.summary?.total_casualties > 0) {
        avgDailyCasualties = preliminaryCasualties.summary.total_casualties / duration;
      } else if (scenario?.casualty_projections?.total > 0) {
        avgDailyCasualties = scenario.casualty_projections.total / duration;
      } else {
        // IMP-005 (CLOSED March 4, 2026): NATIONAL_STANDARDS.NATO.brigade.strength replaces hardcoded 5000.
        // 0.01 = 1% daily attrition (AJMedP-1 baseline planning rate).
        const forceStrength = scenario?.force?.totalStrength ||
                              scenario?.force?.total_strength ||
                              scenario?.force?.forceBuilderResult?.corps?.strength ||
                              NATIONAL_STANDARDS.NATO.brigade.strength;
        avgDailyCasualties = forceStrength * 0.01;
      }

      // IMP-003 FIX (March 3, 2026): Blood consumption rates now derived from national standard
      // via currentNationalConfig rather than hardcoded flat NATO values (APP_RULES 2.1).
      // Blended per-casualty = per-T1 × T1_fraction + per-T2 × T2_fraction.
      // IMP-003 FIX (March 3, 2026): Blood consumption rates derived from currentNationalConfig.
      // Old result: Romanian 4.5×0.30 + 1.5×0.50 = 2.1/cas; NATO 6.0×0.30 + 2.0×0.50 = 2.8/cas.
      // IMP-004 (CLOSED March 4, 2026): tMix now from TRIAGE_CATEGORIES.baseline_percentage.
      // Old hardcoded: { t1: 0.30, t2: 0.50, t3: 0.20 } — replaced with canonical source.
      // New values: T1=0.25, T2=0.40 (T3/T4 excluded — only T1/T2 consume blood products).
      // New NATO result: 6.0×0.25 + 2.0×0.40 = 1.50+0.80 = 2.30/cas (vs old 2.80/cas, −18%).
      const nlRates = currentNationalConfig?.consumption_rates || {};
      const tMix = {
        t1: TRIAGE_CATEGORIES.T1_IMMEDIATE.baseline_percentage,
        t2: TRIAGE_CATEGORIES.T2_DELAYED.baseline_percentage
      };
      const consumptionRates = {
        prbc: (nlRates.prbc_per_t1 || 6.0) * tMix.t1 + (nlRates.prbc_per_t2 || 2.0) * tMix.t2,
        ffp:  (nlRates.ffp_per_t1  || 6.0) * tMix.t1 + (nlRates.ffp_per_t2  || 2.0) * tMix.t2,
        plt:  (nlRates.platelets_per_t1 || 1.0) * tMix.t1,
        cryo: 0.2  // Not modeled per-triage in nationalLogistics — AJMedP-1 planning estimate
      };

      dailyConsumption = {
        prbc: avgDailyCasualties * consumptionRates.prbc,
        ffp:  avgDailyCasualties * consumptionRates.ffp,
        plt:  avgDailyCasualties * consumptionRates.plt,
        cryo: avgDailyCasualties * consumptionRates.cryo
      };
    }

    // Calculate predictions for each product
    const predictions = [];
    const products = [
      { key: 'prbc', label: 'PRBC', stock: supplies.prbc_units },
      { key: 'ffp', label: 'FFP', stock: supplies.ffp_units },
      { key: 'plt', label: 'Platelets', stock: supplies.platelets_units },
      { key: 'cryo', label: 'Cryoprecipitate', stock: supplies.cryo_units }
    ];
    
    products.forEach(product => {
      const daily = dailyConsumption[product.key];
      
      if (daily > 0 && product.stock > 0) {
        const daysUntilStockout = Math.floor(product.stock / daily);
        
        // Only warn if stockout occurs before end of operation
        if (daysUntilStockout < duration) {
          const stockoutDay = Math.min(daysUntilStockout + 1, duration);
          
          // PHASE 4.5.3: Use configurable severity thresholds
          const severity = getStockoutSeverity(daysUntilStockout, thresholdProfile);
          const profile = getThresholdProfile(thresholdProfile);
          
          predictions.push({
            product: product.label,
            productKey: product.key,
            currentStock: product.stock,
            dailyConsumption: Math.round(daily * 10) / 10,
            daysRemaining: daysUntilStockout,
            stockoutDay,
            severity,
            // PHASE 4.5: Use profile's safety margin
            recommendedResupplyDay: Math.max(1, stockoutDay - profile.resupply.safety_margin),
            shortfall: Math.round((duration - daysUntilStockout) * daily),
            dataSource: dataSource.source // PHASE 4.3: Track data source
          });
        }
      }
    });
    
    return {
      predictions,
      dataSource,
      dailyConsumption // Return for discrepancy detection
    };
  }, [supplies, scenario, preliminaryCasualties, getConsumptionDataSource, thresholdProfile, currentNationalConfig]);

  // Memoized predictions for performance
  // PHASE 4.3: Enhanced to include data source and daily consumption
  const stockoutAnalysis = useMemo(() => {
    return calculateStockoutPredictions();
  }, [calculateStockoutPredictions]);
  
  // Extract for backwards compatibility
  const stockoutPredictions = stockoutAnalysis.predictions;
  const consumptionDataSource = stockoutAnalysis.dataSource;
  const currentDailyConsumption = stockoutAnalysis.dailyConsumption;
  
  // =========================================================================
  // PHASE 4.3.2: DISCREPANCY DETECTION & ALERT
  // =========================================================================
  
  /**
   * Detect discrepancy between preliminary estimates and actual calculator results
   * Alert when difference > 10%
   */
  const consumptionDiscrepancy = useMemo(() => {
    // Only check if we have BOTH preliminary and calculated data
    if (consumptionDataSource.source !== 'CALCULATED') {
      return null; // No discrepancy to check
    }
    
    // Calculate what the preliminary estimates would have been
    const duration = scenario?.operation_parameters?.duration_days || 7;
    let avgDailyCasualties = 0;
    
    if (preliminaryCasualties?.summary?.daily_average > 0) {
      avgDailyCasualties = preliminaryCasualties.summary.daily_average;
    } else if (preliminaryCasualties?.summary?.total_casualties > 0) {
      avgDailyCasualties = preliminaryCasualties.summary.total_casualties / duration;
    }
    
    if (avgDailyCasualties === 0) {
      return null; // No preliminary data to compare
    }
    
    // Preliminary rates (NATO averages)
    const preliminaryDaily = {
      prbc: avgDailyCasualties * 2.0,
      ffp: avgDailyCasualties * 2.0,
      plt: avgDailyCasualties * 0.4,
      cryo: avgDailyCasualties * 0.2
    };
    
    // Compare with actual calculator results
    const discrepancies = [];
    const products = [
      { key: 'prbc', label: 'PRBC' },
      { key: 'ffp', label: 'FFP' },
      { key: 'plt', label: 'Platelets' },
      { key: 'cryo', label: 'Cryoprecipitate' }
    ];
    
    products.forEach(product => {
      const preliminary = preliminaryDaily[product.key];
      const actual = currentDailyConsumption[product.key];
      
      if (preliminary > 0 && actual > 0) {
        const percentDiff = Math.abs((actual - preliminary) / preliminary * 100);
        
        if (percentDiff > 10) {
          discrepancies.push({
            product: product.label,
            productKey: product.key,
            preliminary: Math.round(preliminary * 10) / 10,
            actual: Math.round(actual * 10) / 10,
            percentDiff: Math.round(percentDiff),
            isHigher: actual > preliminary
          });
        }
      }
    });
    
    
    return discrepancies.length > 0 ? discrepancies : null;
  }, [consumptionDataSource, scenario, preliminaryCasualties, currentDailyConsumption]);

  // =========================================================================
  // PHASE 4.4: AUTO-SCHEDULE INTELLIGENCE WITH PREVIEW
  // =========================================================================
  
  /**
   * Generate auto-schedule preview using selected strategy
   * Shows preview dialog for user approval before applying
   */
  const handleGenerateAutoSchedule = useCallback(() => {
    
    // Generate schedule using resupply scheduler.
    // PKG-002: nationalStandard passed as 5th arg — activates national threshold DOS targets.
    // Romanian CONSERVATIVE=18d, BALANCED=8d, MINIMAL=4d (vs hardcoded NATO 15/7/3).
    const schedule = generateSchedule(
      stockoutAnalysis,
      supplies,
      scenario,
      autoScheduleStrategy,
      nationalStandard
    );
    
    if (schedule.events.length === 0) {
      return;
    }
    
    
    // Store preview and open dialog
    setAutoSchedulePreview(schedule);
    setPreviewDialogOpen(true);
  }, [stockoutAnalysis, supplies, scenario, autoScheduleStrategy, nationalStandard]);  // PKG-002
  
  /**
   * Apply auto-scheduled events to resupply schedule
   * Merges with existing manual events
   */
  const handleApplyAutoSchedule = useCallback(() => {
    if (!autoSchedulePreview) return;
    
    
    // Store current state for revert
    setLastAutoSchedule({
      previousEvents: [...resupplyEvents],
      appliedEvents: autoSchedulePreview.events,
      timestamp: new Date().toISOString()
    });
    
    // Merge auto-scheduled events with existing manual events
    setResupplyEvents(prev => {
      // Keep manual events, remove old auto-generated ones
      const manualEvents = prev.filter(e => !e.auto_generated);
      
      // Add new auto-generated events
      const merged = [...manualEvents, ...autoSchedulePreview.events];
      
      // Sort by day
      merged.sort((a, b) => a.day - b.day);
      
      return merged;
    });
    
    // Close dialog
    setPreviewDialogOpen(false);
    setResupplyModified(true); // PHASE 4.7.2: Track modification
  }, [autoSchedulePreview, resupplyEvents]);
  
  /**
   * Revert to manual planning - remove auto-generated events
   */
  const handleRevertAutoSchedule = useCallback(() => {
    if (!lastAutoSchedule) {
      return;
    }
    
    
    // Restore previous state
    setResupplyEvents(lastAutoSchedule.previousEvents);
    setLastAutoSchedule(null);
    
  }, [lastAutoSchedule]);
  
  /**
   * Legacy support: Single-product auto-schedule
   * Used by individual "Schedule Resupply" buttons in stockout predictions
   */
  const handleAutoScheduleResupply = useCallback((prediction = null) => {
    const predictionsToSchedule = prediction ? [prediction] : stockoutPredictions;
    
    if (predictionsToSchedule.length === 0) return;
    
    // Group predictions by recommended day to create single events per day
    const byDay = {};
    predictionsToSchedule.forEach(pred => {
      const day = pred.recommendedResupplyDay;
      if (!byDay[day]) {
        byDay[day] = {
          day,
          products: []
        };
      }
      byDay[day].products.push(pred);
    });
    
    // Create resupply events
    const newEvents = Object.values(byDay).map(group => {
      // Determine package type based on severity
      let pushPackage = 'STANDARD_RESUPPLY';
      const hasCritical = group.products.some(p => p.severity === 'CRITICAL');
      const hasMultiple = group.products.length > 2;
      
      if (hasCritical) {
        pushPackage = 'EMERGENCY_BLOOD';
      } else if (hasMultiple) {
        pushPackage = 'MASS_CASUALTY';
      }
      
      // Determine delivery method based on urgency
      let deliveryMethod = 'GROUND';
      if (hasCritical || group.day < 3) {
        deliveryMethod = 'BLUE_LIGHT'; // Urgent
      } else if (group.day < 5) {
        deliveryMethod = 'AIR'; // Expedited
      }
      
      return {
        day: group.day,
        delivery_method: deliveryMethod,
        push_package: pushPackage,
        supplies: {},
        source: 'ROLE_3',
        mtf_target: 'ALL', // Distribute to all by default
        destination_brigade: 'ALL',
        auto_generated: true, // Mark as auto-generated
        reasoning: `Auto-scheduled for: ${group.products.map(p => p.product).join(', ')}`
      };
    });
    
    // Add new events, avoiding duplicates for same day
    setResupplyEvents(prev => {
      const existingDays = new Set(prev.map(e => e.day));
      const uniqueNewEvents = newEvents.filter(e => !existingDays.has(e.day));
      return [...prev, ...uniqueNewEvents].sort((a, b) => a.day - b.day);
    });
  }, [stockoutPredictions]);

  // v5.10.0: T4-014 Personnel Arrival Destination Options
  const personnelDestinationOptions = useMemo(() => {
    const options = [
      { value: 'ALL_UNITS', label: 'All Units (Distributed)' },
      { value: 'RESERVE_POOL', label: 'Reserve Pool (Hold)' }
    ];
    
    // Add specific brigades from force structure
    const brigades = scenario?.force?.forceBuilderResult?.brigades || 
                     scenario?.force?.brigades || 
                     [];
    
    brigades.forEach((bde, index) => {
      const bdeLabel = bde.name || bde.brigade_name || `Brigade ${index + 1}`;
      options.push({ value: `BDE_${bde.id || index}`, label: bdeLabel });
    });
    
    // Add division-level if applicable
    const divisions = scenario?.force?.forceBuilderResult?.divisions || [];
    divisions.forEach((div, index) => {
      const divLabel = div.name || div.division_name || `Division ${index + 1}`;
      options.push({ value: `DIV_${div.id || index}`, label: `${divLabel} (HQ/Troops)` });
    });
    
    return options;
  }, [scenario?.force]);

  const handleLoadPreset = useCallback((preset) => {
    const presets = {
      NATO_BRIGADE: {
        supplies: { iv_fluids_liters: 500, prbc_units: 100, ffp_units: 100, platelets_units: 20, antibiotics_doses: 300, controlled_substances_doses: 200, dressings_sets: 500, oxygen_liters: 10000, anesthetics_uses: 50, tourniquets: 100, morphine_doses: 200 },
        personnel: { surgeons: 4, physicians: 6, nurses: 30, medics: 20, support_staff: 15, psychiatrists: 1, psychologists: 2, chaplains: 2 }
      },
      ROMANIAN_BRIGADE: {
        supplies: { iv_fluids_liters: 400, prbc_units: 80, ffp_units: 80, platelets_units: 15, antibiotics_doses: 250, controlled_substances_doses: 150, dressings_sets: 400, oxygen_liters: 8000, anesthetics_uses: 40, tourniquets: 80, morphine_doses: 150 },
        personnel: { surgeons: 3, physicians: 5, nurses: 25, medics: 18, support_staff: 12, psychiatrists: 1, psychologists: 1, chaplains: 2 }
      },
      US_BRIGADE: {
        supplies: { iv_fluids_liters: 600, prbc_units: 150, ffp_units: 150, platelets_units: 30, antibiotics_doses: 400, controlled_substances_doses: 250, dressings_sets: 600, oxygen_liters: 15000, anesthetics_uses: 60, tourniquets: 150, morphine_doses: 300 },
        personnel: { surgeons: 6, physicians: 8, nurses: 40, medics: 25, support_staff: 20, psychiatrists: 2, psychologists: 3, chaplains: 3 }
      }
    };
    if (presets[preset]) {
      setSupplies(prev => ({ ...prev, ...presets[preset].supplies }));
      setPersonnel(prev => ({ ...prev, ...presets[preset].personnel }));
    }
    setPresetDialogOpen(false);
  }, []);

  // v7.0.1 FIX: handleTargetMDOSChange — when user sets target MDOS (days of buffer stock),
  // recalculate the full logistics projection and save to localStorage.
  // Calling calculateLogisticsProjection() from UI is allowed per APP_RULES 2.2 —
  // the rule prohibits inline formulas in UI code, not calls to the calculation engine.
  // Same pattern as Tab3 calling calculateCasualties() on save.
  const handleTargetMDOSChange = useCallback((newValue) => {
    const clamped = Math.max(1, Math.min(90, Number(newValue) || 7));
    setTargetMDOS(clamped);

    // Need casualties and opDuration to recalculate. Skip if not yet available.
    const casualties = preliminaryCasualties;
    const opDuration  = scenario?.operation_parameters?.duration_days;
    // IMP-002 FIX: nationalStandard state is initialised to 'NATO' — || 'NATO' was dead code.
    // BUG-105 FIX: Check corpsLeadNation first for MULTINATIONAL corps with lead nation set.
    const natStandard = scenario?.force?.corpsLeadNation
                     || scenario?.force?.national_standard
                     || scenario?.logistics?.medical_supplies?.national_standard
                     || nationalStandard;

    if (!casualties || !opDuration) {
      // No data yet — just persist the target value, projection will be built when Tab3 runs
      return;
    }

    try {
      const proj = calculateLogisticsProjection(casualties, natStandard, opDuration, clamped, resupplyEvents, msrConfig, _buildMsrLegs(msrConfig, msrLeg2Config, msrLeg3Config));  // FIX: include events so MDOS preview stays consistent
      // Preserve commanded MDOS from any previous projection
      const prev = lsRead('psp_logistics_projection');
      if (prev?.commanded_mdos_days != null) proj.commanded_mdos_days = prev.commanded_mdos_days;
      lsWrite('psp_logistics_projection', proj);
      setLogisticsProjection(proj);

      // Populate read-only supply fields from new projection
      const rs = proj.required_stock || {};
      setSupplies(prev => ({
        ...prev,
        iv_fluids_liters:              rs.crystalloid             ?? prev.iv_fluids_liters,
        prbc_units:                    rs.prbc                    ?? prev.prbc_units,
        ffp_units:                     rs.ffp                     ?? prev.ffp_units,
        platelets_units:               rs.platelets               ?? prev.platelets_units,
        antibiotics_doses:             rs.antibiotics             ?? prev.antibiotics_doses,
        tourniquets:                   rs.tourniquets             ?? prev.tourniquets,
        morphine_doses:                rs.analgesics              ?? prev.morphine_doses,
        // ISSUE-01 FIX: three previously unmapped fields now wired from required_stock
        controlled_substances_doses:   rs.controlled_substances   ?? prev.controlled_substances_doses,
        dressings_sets:                rs.dressings_sets          ?? prev.dressings_sets,
        // surgical_kits removed — US Army ATP 4-02.25 only, not NATO doctrine
      }));
    } catch (e) {
      console.error('Tab4 handleTargetMDOSChange: calculateLogisticsProjection failed:', e.message);
    }
  }, [preliminaryCasualties, scenario, nationalStandard, resupplyEvents, msrConfig, msrLeg2Config, msrLeg3Config]);

  // =========================================================================
  // SAVE
  // =========================================================================
  
  // =========================================================================
  // BUILD UPDATES (shared by handleSave and flushState)
  // Constructs logistics payload from current local state.
  // Synchronous - no side effects. Used by:
  //   - handleSave: buildUpdates() -> onUpdate -> onComplete -> onNavigate
  //   - flushState: buildUpdatesRef.current() -> onUpdateRef.current only
  // =========================================================================

  const buildUpdates = () => ({
    logistics: {
      medical_supplies: {
        // L4-04: Supply values are read-only (populated from psp_logistics_projection).
        // Save the current display state so Tab4 can restore on re-entry.
        initial_stock: supplies,
        resupply_events: resupplyEvents,
        mdos_analysis: mdosAnalysis,
        // v7.0.1: Save targetMDOS (buffer planning input, replaces commandedMDOS)
        commanded_mdos: targetMDOS,
        national_standard: nationalStandard
      },
      // PHASE 1.0: walking_blood_bank removed - now in medical_configuration.blood_config
      blood_alert_level: bloodAlertLevel.level,
      personnel, replacement_planning: replacementPlanning,
      personnel_arrivals: personnelArrivals,  // v5.9.0: Personnel arrivals schedule
      // BUG-140 FIX: Augment thpConfig with policy_days so calculator MASCAL block reads
      // the reliable numeric path first instead of the regex fallback (which silently
      // defaulted to 7 → THP_7_DAY regardless of the actual Tab4C selection).
      theatre_holding_policy: {
        ...thpConfig,
        policy_days: THEATRE_HOLDING_POLICY[thpConfig.policy]?.days ?? 7
      },
      rtd_projection: rtdProjection, bed_occupancy: bedOccupancy,
      mental_health: mentalHealthConfig, csr_projection: csrProjection, validation,
      msr_config: msrConfig,           // ARCH-002: forward supply route Leg 1
      msr_leg2_config: msrLeg2Config,  // ENH-014b: FMSS → R2B
      msr_leg3_config: msrLeg3Config,  // ENH-014b: R2B → R1
      // v5.6.8: Mark as configured for validation
      configured: true
    }
  });

  // Keep buildUpdatesRef current on every render so flushState always uses latest state
  buildUpdatesRef.current = buildUpdates;

  const handleSave = useCallback(async (navigate = true) => {
    setIsCalculating(true);
    setCalculationError(null);

    try {
      // D-07: Tab4 is the sole owner of calculateLogisticsProjection (ARCH-009).
      // Primary: casualties from Tab3's Save & Continue (scenario.casualty_projections).
      // Fallback (v4.3.0): preliminaryCasualties — Tab4's own calculator run, same data,
      //   used when user reaches Tab4 for the first time before running Tab5.
      //   Without this fallback, scenario.casualty_projections is null on first Tab4 entry,
      //   calculateLogisticsProjection throws, and all supply fields show zero.
      const casualties = scenario?.casualty_projections ?? preliminaryCasualties;
      const opDuration  = scenario?.operation_parameters?.duration_days;
      // IMP-002 FIX: nationalStandard state is initialised to 'NATO' — || 'NATO' was dead code.
      // BUG-105 FIX: Check corpsLeadNation first for MULTINATIONAL corps with lead nation set.
      const natStandard = scenario?.force?.corpsLeadNation
                       || scenario?.force?.national_standard
                       || scenario?.logistics?.medical_supplies?.national_standard
                       || nationalStandard;

      // Compute updated supplies inline (React state is async — can't rely on setSupplies above)
      let latestSupplies = { ...supplies };

      // MDOS-VAL-001 FIX: hoist proj so its overall_mdos can be injected into the saved
      // mdos_analysis below. SupplyIntelligence.calculateScenarioMDOS uses population-based
      // rates (~11% of correct rate) → produces 74-day fiction. calculateLogisticsProjection
      // uses actual casualty_projections data → produces correct value (matches Tab 5).
      let proj = null;
      if (casualties && opDuration) {
        proj = calculateLogisticsProjection(casualties, natStandard, opDuration, targetMDOS, resupplyEvents, msrConfig, _buildMsrLegs(msrConfig, msrLeg2Config, msrLeg3Config));
        // D-08: Write to localStorage (compressed) — Tab5 reads from this key
        lsWrite('psp_logistics_projection', proj);
        setLogisticsProjection(proj);
        // Refresh supply display from the fresh projection
        if (proj.required_stock) {
          const rs = proj.required_stock;
          latestSupplies = {
            ...supplies,
            iv_fluids_liters:   rs.crystalloid  ?? supplies.iv_fluids_liters,
            prbc_units:         rs.prbc         ?? supplies.prbc_units,
            ffp_units:          rs.ffp          ?? supplies.ffp_units,
            platelets_units:    rs.platelets    ?? supplies.platelets_units,
            antibiotics_doses:  rs.antibiotics  ?? supplies.antibiotics_doses,
            tourniquets:        rs.tourniquets  ?? supplies.tourniquets,
            morphine_doses:     rs.analgesics   ?? supplies.morphine_doses,
          };
          setSupplies(latestSupplies);
        }
      }

      // Build updates with latest supplies (before React re-render completes)
      const updates = {
        logistics: {
          medical_supplies: {
            initial_stock: latestSupplies,
            resupply_events: resupplyEvents,
            // MDOS-VAL-001 FIX: when proj is available, override overall_mdos with the
            // calculator-derived value. SupplyIntelligence overall_mdos (mdosAnalysis) is kept
            // as structural fallback but its overall_mdos field is replaced with the correct value
            // so that validation, Tab5, and any downstream reader all see the correct figure.
            mdos_analysis: proj != null
              ? {
                  ...(mdosAnalysis ?? {}),
                  overall_mdos:    proj.overall_mdos,
                  status:          proj.status          ?? mdosAnalysis?.status,
                  limiting_factor: proj.limiting_factor ?? mdosAnalysis?.limiting_factor,
                  data_source:     'CALCULATOR'
                }
              : mdosAnalysis,
            commanded_mdos: targetMDOS,
            national_standard: nationalStandard
          },
          blood_alert_level: bloodAlertLevel.level,
          personnel, replacement_planning: replacementPlanning,
          personnel_arrivals: personnelArrivals,
          // BUG-140 FIX: Same augmentation as buildUpdates — add policy_days.
          theatre_holding_policy: {
            ...thpConfig,
            policy_days: THEATRE_HOLDING_POLICY[thpConfig.policy]?.days ?? 7
          },
          rtd_projection: rtdProjection, bed_occupancy: bedOccupancy,
          mental_health: mentalHealthConfig, csr_projection: csrProjection, validation,
          configured: true,
          logistics_saved_at: new Date().toISOString()  // HIGH-1: Tab5 useEffect dep — fires on every Tab4 Save
        }
      };

      if (onUpdate) onUpdate(updates);
      // calculationService: Tab4 saved — any existing Tab5 calculation is now stale
      markDirty();
      // BUG-S109-003 FIX: onComplete must be gated on `navigate` — same as onNavigate.
      // markTabComplete (App.jsx) always navigates to the next tab ("Navigation ALWAYS happens").
      // Calling onComplete unconditionally meant handleSave(false) ["Save Changes"] still
      // advanced to Tab 5 via markTabComplete, defeating the stay-on-Tab4 intent.
      if (navigate && onComplete) onComplete(updates.logistics);
      if (navigate && onNavigate) onNavigate(4);

    } catch (error) {
      console.error('Tab4 Save Error:', error);
      setCalculationError(error.message);
      // Still attempt to save logistics config on error
      const fallbackUpdates = buildUpdatesRef.current();
      if (onUpdate) onUpdate(fallbackUpdates);
      if (navigate && onComplete) onComplete(fallbackUpdates.logistics);
      if (navigate && onNavigate) onNavigate(4);
    } finally {
      setIsCalculating(false);
    }
  }, [onUpdate, onComplete, onNavigate, scenario, nationalStandard, targetMDOS, resupplyEvents,
      supplies, mdosAnalysis, bloodAlertLevel, personnel, replacementPlanning, personnelArrivals,
      thpConfig, rtdProjection, bedOccupancy, mentalHealthConfig, csrProjection, validation,
      preliminaryCasualties, msrConfig, msrLeg2Config, msrLeg3Config]);

  // =========================================================================
  // RENDER HELPERS
  // =========================================================================
  
  const getMDOSColor = (mdos) => {
    // ISSUE-02 FIX: null/undefined = no projection yet (pending state), not CRITICAL_ZERO.
    // Return MUI 'default' (grey) so chip/card does not show alarming red before
    // the user has configured anything.
    if (mdos == null) return 'default';
    if (!mdos) return 'error';
    if (mdos < (MDOS_STANDARDS?.MINIMUM?.days || 5)) return 'error';
    if (mdos < (MDOS_STANDARDS?.LOW?.days || 10)) return 'warning';
    if (mdos < (MDOS_STANDARDS?.ADEQUATE?.days || 15)) return 'info';
    return 'success';
  };

  const getValidationIcon = (status) => {
    if (status === 'passed' || status === 'green') return <CheckCircleIcon color="success" />;
    if (status === 'warning' || status === 'yellow') return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  
  return (
    <Box sx={{ p: 2 }}>
      {/* Header - v5.6.7: Simplified, removed redundant buttons (now at bottom only) */}
      <Paper sx={{ p: 2, mb: 2 }}>
         <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#9c27b0', mb: 3 }}>
          {t('tab4.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('tab4.subtitle')}
        </Typography>
      </Paper>

      {/* Upstream Data Summary */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>{t('tab4.upstream_data')}</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 3 }}>
            <Chip label={`${t('tab4.force_label')}: ${forceStrength?.toLocaleString() || 'N/A'}`} color={forceStrength ? 'success' : 'error'} size="small" />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Chip label={`${t('tab4.duration_label')}: ${scenario?.operation_parameters?.duration_days || 'N/A'} days`} color={scenario?.operation_parameters?.duration_days ? 'success' : 'error'} size="small" />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Chip
              label={bedOccupancy?.csu_pha_beds > 0
                ? `${t('tab4.beds_label')}: ${bedOccupancy.total_beds} (incl. ${bedOccupancy.csu_pha_beds} CSU/PHA)`
                : `${t('tab4.beds_label')}: ${bedOccupancy?.total_beds || 'N/A'}`}
              color={bedOccupancy?.total_beds ? 'success' : 'warning'}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Chip label={`${t('tab4.casualties_label')}: ${scenario?.casualty_projections?.total || t('tab4.estimated_label')}`} color={scenario?.casualty_projections?.total ? 'success' : 'warning'} size="small" />
          </Grid>
        </Grid>
      </Paper>

      {/* Section 4A: Medical Supplies */}
      <Accordion expanded={expandedSections.supplies} onChange={() => setExpandedSections(prev => ({ ...prev, supplies: !prev.supplies }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <MedicalServicesIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4a')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* BUG-DISPLAY-001 FIX: Split into Target DOS (commanded) and Current Stock DOS (actual) */}
            <Chip label={`${t('tab4.commanded_dos')}: ${targetMDOS || '?'}d`} color="primary" size="small" variant="outlined" />
            <Chip label={`Current: ${(logisticsProjection?.overall_mdos ?? (logisticsProjection ? mdosAnalysis?.overall_mdos : null)) || '?'}d DOS`} color={getMDOSColor(logisticsProjection?.overall_mdos ?? (logisticsProjection ? mdosAnalysis?.overall_mdos : null))} size="small" />
            <Chip label={`Blood: ${bloodAlertLevel.level}`} color={bloodAlertLevel.color} size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* PHASE 4.0.6: MDOS Educational Accordion */}
          <Accordion sx={{ mb: 2, border: '1px solid', borderColor: 'info.light' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="info" />
                <Typography variant="subtitle2">
                  {t('tab4.what_is_dos')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                <strong>Minimum Days of Supply (MDOS in this tool)</strong> is a planning standard that specifies 
                how many days of medical supplies must be maintained at each medical treatment facility 
                echelon to sustain operations during combat.
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                {t('tab4.dos_requirements')}
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>Role 1 (Battalion Aid Station):</strong> 3 days minimum
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Limited resupply capability, forward deployed, minimal storage
                  </Typography>
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Role 2 (Brigade/Division FST):</strong> 7 days minimum
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Surgical capability, moderate storage, regular resupply cycle
                  </Typography>
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Role 3 MTF (Field Hospital):</strong> 14 days minimum
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Full surgical capability, extensive storage, supply hub for forward units.
                    (US Army equivalent: Combat Support Hospital / CSH — not a NATO term.)
                  </Typography>
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Role 4 (Strategic Hospital):</strong> 30+ days
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Definitive care, extensive stockpiles, strategic reserve capability
                  </Typography>
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                {t('tab4.how_dos_calculated')}
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                <Typography variant="body2" component="div" paragraph>
                  1. <strong>Estimate Daily Consumption:</strong>
                  <br />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 2 }}>
                    - Average daily casualties x consumption rates per casualty type
                    <br />
                    - T1 (Immediate): High resource consumption (surgery, blood, fluids)
                    <br />
                    - T2 (Urgent): Moderate consumption (stabilization, monitoring)
                    <br />
                    - T3 (Delayed): Lower consumption (basic care, observation)
                  </Typography>
                </Typography>
                
                <Typography variant="body2" component="div" paragraph>
                  2. <strong>Current Stock Levels:</strong>
                  <br />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 2 }}>
                    - Blood products (PRBC, FFP, Platelets, Cryo)
                    <br />
                    - IV fluids and medications
                    <br />
                    - Surgical supplies and dressings
                  </Typography>
                </Typography>
                
                <Typography variant="body2" component="div" paragraph>
                  3. <strong>Calculate DOS per Product:</strong>
                  <br />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 2 }}>
                    DOS = Current Stock / Daily Consumption Rate
                  </Typography>
                </Typography>
                
                <Typography variant="body2" component="div">
                  4. <strong>Overall DOS:</strong>
                  <br />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 2 }}>
                    The MINIMUM DOS across all critical supplies
                    <br />
                    Example: If you have 10 days of blood but only 3 days of IV fluids, DOS = 3
                  </Typography>
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                {t('tab4.why_dos_matters')}
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Operational Planning:</strong> When DOS drops below operation duration, 
                  resupply operations must be planned and executed to prevent medical capability degradation.
                </Typography>
              </Alert>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>MASCAL Preparedness:</strong> Mass casualty events consume supplies rapidly. 
                  Higher DOS provides buffer capacity for surge operations.
                </Typography>
              </Alert>
              <Alert severity="error">
                <Typography variant="body2">
                  <strong>Critical Threshold:</strong> When MDOS falls below 5 days, medical operations 
                  are at immediate risk. Emergency resupply via MASCAL Escalation Protocol may be required.
                </Typography>
              </Alert>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', textAlign: 'center' }}>
                {t('tab4.mdos_reference')}
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* L4-03/04/05/06/09: Initial Stock Configuration — display-only from psp_logistics_projection */}
          <Card sx={{ mb: 2, bgcolor: 'primary.light', border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrackChangesIcon sx={{ color: 'primary.contrastText' }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'primary.contrastText' }}>{t('tab4.initial_stock_config')}</Typography>
                <IconButton size="small" onClick={() => setDosHelperOpen(!dosHelperOpen)} sx={{ color: 'primary.contrastText' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'primary.contrastText' }}>
                {t('tab4.initial_stock_used_by')}
              </Typography>

              {/* L4-09: No projection yet — guide user to Tab 3 */}
              {!logisticsProjection && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>{t('tab4.initial_stock_not_calc')}</AlertTitle>
                  Enter your <strong>Commanded DOS</strong> (days of initial stock per the command order) below to calculate supply requirements.
                  Stock is calculated automatically when you change the Commanded DOS.
                </Alert>
              )}

              <Grid container spacing={2} alignItems="flex-end">
                {/* L4-05/L4-06: Commanded MDOS — adequacy threshold, NOT a calculation input */}
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Tooltip
                    title="Commanded DOS: The number of days of medical supply you are ordered to start with on Day 1 (e.g. 'All units start with 7 DOS'). This is the command order — it directly sets the initial stock quantity. If the operation runs longer than your Commanded DOS, the system will warn you and you must plan resupply."
                    arrow
                    placement="top"
                  >
                    <TextField
                      fullWidth
                      label={t('tab4.commanded_dos')}
                      type="number"
                      value={targetMDOS}
                      onChange={(e) => handleTargetMDOSChange(e.target.value)}
                      size="small"
                      InputProps={{ inputProps: { min: 1, max: 90 } }}
                      helperText={t('tab4.commanded_dos_helper')}
                      sx={{
                        '& .MuiInputBase-root': { bgcolor: 'background.paper', mt: 0.5 },
                        '& .MuiInputLabel-root': {
                          fontWeight: 700, fontSize: '0.95rem', color: 'white', mb: 1,
                          '&.Mui-focused': { color: 'white', fontWeight: 700 }
                        },
                        '& .MuiFormHelperText-root': { color: 'white', fontWeight: 600, fontSize: '0.8rem' }
                      }}
                    />
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small" sx={{
                    '& .MuiInputBase-root': { bgcolor: 'background.paper', mt: 0.5 },
                    '& .MuiInputLabel-root': {
                      fontWeight: 700, fontSize: '0.95rem', color: 'white',
                      '&.Mui-focused': { color: 'white', fontWeight: 700 }
                    }
                  }}>
                    <InputLabel>{t('tab4.national_standard')}</InputLabel>
                    <Select
                      value={nationalStandard}
                      onChange={(e) => setNationalStandard(e.target.value)}
                      label={t('tab4.national_standard')}
                    >
                      {AVAILABLE_NATIONAL_STANDARDS?.map((std) => (
                        <MenuItem key={std.code} value={std.code} disabled={!std.complete}>
                          {std.flag} {std.name} {!std.complete && '(Coming Soon)'}
                        </MenuItem>
                      )) || [
                        <MenuItem key="NATO" value="NATO"> NATO Standard</MenuItem>,
                        <MenuItem key="ROMANIAN" value="ROMANIAN"> Romanian</MenuItem>,
                        <MenuItem key="US" value="US"> US Army</MenuItem>
                      ]}
                    </Select>
                    <Typography variant="caption" sx={{ color: 'white', mt: 0.5, ml: 1.75, fontWeight: 600, fontSize: '0.8rem' }}>
                      {t('tab4.consumption_rates')}
                    </Typography>
                  </FormControl>
                </Grid>
                {/* PHASE 4.5.3: Threshold Profile Selector */}
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="small" sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 700, fontSize: '0.95rem', color: 'white',
                      '&.Mui-focused': { color: 'white', fontWeight: 700 }
                    }
                  }}>
                    <InputLabel>{t('tab4.dos_thresholds')}</InputLabel>
                    <Select
                      value={thresholdProfile}
                      onChange={(e) => setThresholdProfile(e.target.value)}
                      label={t('tab4.dos_thresholds')}
                      renderValue={(selected) => THRESHOLD_PROFILES[selected]?.label || selected}
                    >
                      {Object.entries(THRESHOLD_PROFILES).map(([key, profile]) => (
                        <MenuItem key={key} value={key}>
                          <Box>
                            <Typography variant="body2">{profile.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Critical &lt;{profile.stockout.CRITICAL}, Warning &lt;{profile.stockout.WARNING}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" sx={{ color: 'white', mt: 0.5, ml: 1.75, fontWeight: 600, fontSize: '0.8rem' }}>
                      {t('tab4.severity_levels')}
                    </Typography>
                  </FormControl>
                </Grid>
                {/* L4-07: Projection DOS status vs commanded MDOS */}
                {logisticsProjection && (
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Box sx={{ textAlign: 'center', pb: 0.5 }}>
                      <Chip
                        label={`${logisticsProjection.overall_mdos ?? '?'} days`}
                        size="small"
                        color={(() => {
                          // G-06 / C-012: Four-tier color via ASSET_STATUS_THRESHOLDS.
                          // Percentage = (achieved MDOS / target MDOS) × 100.
                          // Thresholds: GREEN ≥60%, YELLOW 40–59%, ORANGE/RED <40%/<20%.
                          // ORANGE and RED both use 'error' — MUI has no native orange Chip color.
                          // All values from ASSET_STATUS_THRESHOLDS; no hardcoded percentages.
                          if (logisticsProjection.overall_mdos == null || !(targetMDOS > 0)) return 'default';
                          const mdosCoveragePct = (logisticsProjection.overall_mdos / targetMDOS) * 100;
                          if (mdosCoveragePct >= ASSET_STATUS_THRESHOLDS.GREEN.min_percent)  return ASSET_STATUS_THRESHOLDS.GREEN.mui_color;
                          if (mdosCoveragePct >= ASSET_STATUS_THRESHOLDS.YELLOW.min_percent) return ASSET_STATUS_THRESHOLDS.YELLOW.mui_color;
                          if (mdosCoveragePct >= ASSET_STATUS_THRESHOLDS.ORANGE.min_percent) return ASSET_STATUS_THRESHOLDS.ORANGE.mui_color;
                          return ASSET_STATUS_THRESHOLDS.RED.mui_color;
                        })()}
                      />
                      <Typography variant="caption" sx={{ color: 'white', mt: 0.5, display: 'block', fontWeight: 600, fontSize: '0.8rem' }}>
                        {t('tab4.projected_mdos')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {!logisticsProjection && (
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Box sx={{ textAlign: 'center', pb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {scenario?.operation_parameters?.duration_days || '?'} days
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem' }}>
                        {t('tab4.op_duration')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* National Differences Info */}
              {nationalStandard !== 'NATO' && nationalDifferences?.count > 0 && (
                <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
                  <AlertTitle>{nationalStandard} differs from NATO in {nationalDifferences.count} areas</AlertTitle>
                  <Typography variant="body2">
                    {nationalDifferences.differences?.slice(0, 3).map(d =>
                      `${d.field.split('.').pop()}: ${d.national_value} (NATO: ${d.nato_value})`
                    ).join(' - ')}
                    {nationalDifferences.count > 3 && ` - +${nationalDifferences.count - 3} more`}
                  </Typography>
                </Alert>
              )}

              {/* T4-004: Resupply Warning */}
              {resupplyNeeds?.needs_resupply && (
                <Alert severity={resupplyNeeds.urgency === 'CRITICAL' ? 'error' : resupplyNeeds.urgency === 'HIGH' ? 'warning' : 'info'} sx={{ mt: 2 }}>
                  <AlertTitle>Resupply Required</AlertTitle>
                  {resupplyNeeds.message}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Commanded DOS: {targetMDOS || 0} days
                    &nbsp;|&nbsp; Operation Duration: {scenario?.operation_parameters?.duration_days || '?'} days
                    &nbsp;|&nbsp; Resupply needed by Day {resupplyNeeds.resupply_by_day}
                  </Typography>
                </Alert>
              )}

              {/* FIX 1: Simplified DOS Helper - Quick Reference Rates Only */}
              {dosHelperOpen && currentNationalConfig?.consumption_rates && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '2px solid', borderColor: 'info.main' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="info.dark">
                    {nationalStandard} Consumption Rates - Quick Reference
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6" color="error.main" fontWeight="bold">
                          {currentNationalConfig.consumption_rates.prbc_per_surgical || '?'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('tab4.prbc_per_surgery')}</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6" color="warning.main" fontWeight="bold">
                          {currentNationalConfig.consumption_rates.ffp_per_surgical || '?'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('tab4.ffp_per_surgery')}</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6" color="info.main" fontWeight="bold">
                          {averageIVConsumption.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('tab4.iv_per_casualty')}</Typography>
                        <Typography variant="caption" color={
                          averageIVConsumption.source === 'calculated' ? 'success.main' :
                          averageIVConsumption.source === 'estimated' ? 'info.main' : 'text.secondary'
                        } sx={{ display: 'block', fontSize: '0.65rem', mt: 0.5, fontStyle: 'italic' }}>
                          {averageIVConsumption.source === 'calculated' ? t('tab4.calculated_source_label') :
                           averageIVConsumption.source === 'estimated' ? t('tab4.estimated_source_label') : t('tab4.nato_standard_source_label')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          {currentNationalConfig.consumption_rates.antibiotics_per_casualty || '?'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('tab4.antibiotics_per_casualty')}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1.5 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.primary">
                        Commanded DOS: <Chip label={`${targetMDOS} days`} size="small" color="primary" />
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.primary">
                        Minimum: <Chip label={`${currentNationalConfig.mdos?.minimum_days || 5} days`} size="small" color="warning" />
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.primary">
                        {/*
                          ORDER DAY LABEL -- DO NOT REMOVE OR SIMPLIFY.
                          This is NOT a hardcoded value (APP_RULES 2.1 compliant).
                          It is a computed calendar day derived from two constants-sourced values:
                            resupply_by_day = max(1, currentMDOS - safetyBuffer)
                          Example: targetMDOS=8, threshold=7 → order on Day 1 of the operation.
                          Displaying the raw threshold (e.g. "7 days") was found to be confusing
                          for untrained users who misread it as either operation days remaining
                          or a resupply interval. The calendar day is the actionable information
                          a commander needs. Threshold value remains visible in the tooltip below.
                            safetyBuffer = 2 (NATO default per AMedP-9.1, passed explicitly to calculateResupplyNeeds())
                        */}
                        {resupplyNeeds?.needs_resupply ? (
                          <Tooltip
                            title={"Actual stock on hand: " + (logisticsProjection?.overall_mdos ?? mdosAnalysis?.overall_mdos ?? targetMDOS) + "d DOS. NATO safety buffer: 2d (AMedP-9.1). Place order by Day " + resupplyNeeds.resupply_by_day + " to avoid stockout (Day " + (scenario?.operation_parameters?.duration_days ?? '?') + ")."}
                            arrow
                            placement="top"
                          >
                            <span>
                              Order by: <Chip label={"Day " + resupplyNeeds.resupply_by_day} size="small" color="warning" sx={{ cursor: "help" }} />
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={"Commanded DOS (" + targetMDOS + "d) covers the full operation. No resupply required."}
                            arrow
                            placement="top"
                          >
                            <span>
                              <Chip label={t('tab4.no_resupply_needed')} size="small" color="success" sx={{ cursor: "help" }} />
                            </span>
                          </Tooltip>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', fontStyle: 'italic', textAlign: 'center' }}>
                    {t('tab4.stock_from_tab3')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* v8.1.6 UX FIX: MDOS status card moved here (below Initial Stock Configuration).
              Previously rendered at the TOP of 4A before any user input, causing a red
              CRITICAL_ZERO alarm to fire on every Tab4 entry when stale localStorage held
              a zero-MDOS projection. Information architecture fix: cause (configuration)
              before effect (status result). The header chip still shows MDOS at a glance. */}
          {/* MDOS Dashboard - T4-011: Enhanced with educational tooltip */}
          {/* ISSUE-02 FIX: null MDOS = pending (no calculation yet) → neutral grey, not red */}
          {/* MDOS-D-002 FIX: stale projection (target_mdos_days !== targetMDOS or op duration changed)
               shows neutral grey + "Outdated" badge instead of alarming red CRITICAL_ZERO.
               A projection is stale when it was saved with different parameters than current inputs.
               Trigger: user opens Tab4 with saved projection from a previous session. */}
          {/* MDOS-D-003 FIX: displayMDOS is null when no projection exists yet.
              getMDOSColor(null) → 'default' (grey). Prevents 0 from triggering error/red.
              When logisticsProjection exists, prefer its overall_mdos; fall back to
              mdosAnalysis only as a secondary live estimate. */}
          {(() => {
            const displayMDOS = logisticsProjection
              ? (logisticsProjection.overall_mdos ?? mdosAnalysis?.overall_mdos)
              : null;

          return (
          <Card sx={{ mb: 2, bgcolor: (() => {
            // MDOS-D-003 FIX A: No projection yet → always neutral grey.
            if (!logisticsProjection) return 'grey.100';
            const isStale = logisticsProjection && (
              logisticsProjection.target_mdos_days !== targetMDOS ||
              logisticsProjection.op_duration_days !== scenario?.operation_parameters?.duration_days
            );
            if (isStale) return 'grey.100';
            const mdosVal = logisticsProjection?.overall_mdos ?? mdosAnalysis?.overall_mdos;
            if (mdosVal == null) return 'grey.100';
            return mdosVal < 5 ? 'error.light' : 'grey.100';
          })() }}>
            <CardContent>
              {/* PHASE 4.3.3: Data Source Badge at top right */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -2 }}>
                <Chip 
                  label={`Data: ${consumptionDataSource.badge}`}
                  size="small"
                  color={consumptionDataSource.color}
                  sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                />
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 3 }}>
                  <Typography variant="h3" color={getMDOSColor(displayMDOS)}>{displayMDOS ?? '?'}</Typography>
                  <Tooltip 
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Medical Days of Supply (MDOS)</Typography>
                        <Typography variant="body2" gutterBottom>
                          MDOS indicates how many days your current medical supplies will sustain operations 
                          based on projected casualty rates and consumption patterns.
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ color: 'success.light' }}>
                          <strong>[GREEN] GREEN (15+ days):</strong> Adequate supply
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'info.light' }}>
                          <strong>[BLUE] BLUE (10-14 days):</strong> Monitor closely
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'warning.light' }}>
                          <strong>[YELLOW] YELLOW (5-9 days):</strong> Plan resupply
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'error.light' }}>
                          <strong>[RED] RED (&lt;5 days):</strong> Critical shortage
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                          Reference: NATO STANAG 2228, AMedP-9.1
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="right"
                  >
                    <Typography variant="caption" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                      {t('tab4.medical_days_of_supply')} <InfoIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 3 }}>
                  {/* MDOS-D-002: Show "Outdated" instead of stale status/limiting values */}
                  {(() => {
                    // MDOS-D-003 FIX C: No projection → show neutral "Not yet calculated".
                    if (!logisticsProjection) return (
                      <>
                        <Typography variant="body2">Status: <strong style={{color:'grey'}}>{t('tab4.not_yet_calculated')}</strong></Typography>
                        <Typography variant="caption" color="text.secondary">{t('tab4.hit_save_to_calc')}</Typography>
                      </>
                    );
                    const isStale = logisticsProjection && (
                      logisticsProjection.target_mdos_days !== targetMDOS ||
                      logisticsProjection.op_duration_days !== scenario?.operation_parameters?.duration_days
                    );
                    if (isStale) return (
                      <>
                        <Typography variant="body2">Status: <strong style={{color:'orange'}}>{t('tab4.outdated')}</strong></Typography>
                        <Typography variant="caption" color="text.secondary">{t('tab4.modify_commanded_dos')}</Typography>
                      </>
                    );
                    return (
                      <>
                        <Typography variant="body2">Status: <strong>{logisticsProjection?.status || mdosAnalysis?.status || 'Unknown'}</strong></Typography>
                        <Typography variant="body2">Limiting: <strong>{logisticsProjection?.limiting_factor || mdosAnalysis?.limiting_factor || 'N/A'}</strong></Typography>
                      </>
                    );
                  })()}
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <LinearProgress variant="determinate" value={Math.min(100, (displayMDOS || 0) / (scenario?.operation_parameters?.duration_days || 7) * 100)} color={getMDOSColor(displayMDOS)} sx={{ height: 10, borderRadius: 5 }} />
                  <Typography variant="caption">{Math.round((displayMDOS || 0) / (scenario?.operation_parameters?.duration_days || 7) * 100)}{t('tab4.pct_of_operation')}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          ); // close return( of displayMDOS IIFE
          })()} {/* close displayMDOS IIFE */}

          {/* Supply Inputs — L4-04: Read-only, populated from psp_logistics_projection */}
          <Typography variant="subtitle2" gutterBottom>{t('tab4.initial_stock_levels')}</Typography>
          {logisticsProjection && (
            <Alert severity="success" sx={{ mb: 1 }} icon={<CheckCircleIcon />}>
              <Typography variant="caption">
                Stock levels calculated for <strong>{logisticsProjection.target_mdos_days ?? targetMDOS} Commanded DOS</strong>
                ({logisticsProjection.national_standard} rates, {logisticsProjection.op_duration_days ?? '?'}-day operation).
                Last updated: {new Date(logisticsProjection.calculated_at).toLocaleString()}.
                Change the Commanded DOS above to recalculate.
              </Typography>
            </Alert>
          )}
          <Grid container spacing={2}>
            {[
              { field: 'iv_fluids_liters', label: t('tab4.iv_fluids') },
              { field: 'prbc_units', label: t('tab4.prbc_units_label') },
              { field: 'ffp_units', label: t('tab4.ffp_units_label') },
              { field: 'platelets_units', label: t('tab4.platelets_label') },
              { field: 'antibiotics_doses', label: t('tab4.antibiotics_label') },
              { field: 'controlled_substances_doses', label: t('tab4.controlled_substances_label') },
              { field: 'dressings_sets', label: t('tab4.dressings_label') },
              // v5.6.9: Added critical items required by validation
              { field: 'tourniquets', label: t('tab4.tourniquets_label') },
              { field: 'morphine_doses', label: t('tab4.morphine_label') }
            ].map(({ field, label }) => (
              <Grid size={{ xs: 6, sm: 3 }} key={field}>
                <TextField
                  fullWidth
                  label={label}
                  type="number"
                  value={supplies[field] || 0}
                  size="small"
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'grey.100' } }}
                  helperText={logisticsProjection ? t('tab4.from_projection') : t('tab4.complete_tab3_first')}
                />
              </Grid>
            ))}
          </Grid>
          {/* v5.6.9: Tourniquet info */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('tab4.tourniquet_note')}
          </Typography>

          {/* F3-MRoE-STREAM2: Inform planner when civilian patients are included in blood DOS.
              The supplyChainModule adds civilian T1/T2 to daily demand transparently —
              without this note, planners would not know why stock quantities are higher than
              the military-only baseline. T3 goes to HNS, EPW flows through a separate chain.
              Only rendered when mroe_config is enabled AND civilian_stream competes_with_military. */}
          {(() => {
            const _mroeCfg = scenario?.operation_parameters?.mroe_config;
            const _civStream = scenario?.casualty_projections?.mascal_analysis?.mroe_load?.civilian_stream;
            if (!_mroeCfg?.enabled || !_civStream?.competes_with_military) return null;
            const _t1 = Math.round(_civStream.t1_surgical_daily || 0);
            const _t2 = Math.round(_civStream.t2_ward_daily || 0);
            return (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                <AlertTitle>MRoE Stream 2 — Civilian Patients Included in Blood DOS</AlertTitle>
                <Typography variant="body2">
                  MedROE is active. Civilian T1/T2 patients enter the main chain at R2B and are included
                  in blood and surgical supply calculations above.
                </Typography>
                {(_t1 > 0 || _t2 > 0) && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Daily civilian demand: T1 (surgical) +{_t1}{' '}
                    {_t2 > 0 ? <>| T2 (ward) +{_t2}</> : null} patients/day.
                    T3 casualties routed to HNS facilities (not included).
                    EPW/EWIA flow through a dedicated chain (GC III Art.12) — not included here.
                  </Typography>
                )}
              </Alert>
            );
          })()}

          {/* ENH-027: Convoy Planning card moved below resupply schedule — see end of Section 4A */}

          <Divider sx={{ my: 2 }} />

          {/* Walking Blood Bank - Read-Only Display (PHASE 1.0) */}
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BloodtypeIcon color="error" /> {t('tab4.wbb_title')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('tab4.wbb_configured_in_tab3')}
                </Typography>
                {scenario?.medical_configuration?.blood_config?.wbb_enabled ? (
                  <>
                    <Typography variant="body2">
                       {t('tab4.wbb_enabled_status')} — {scenario.medical_configuration.blood_config.wbb_donors || 0} {t('tab4.wbb_donors')}
                    </Typography>
                    <Typography variant="body2">
                      {t('tab4.wbb_universal_donors')} {scenario.medical_configuration.blood_config.wbb_low_titer_donors || 0} (Low-Titer O+)
                    </Typography>
                    <Typography variant="body2">
                      {t('tab4.wbb_activation')} {scenario.medical_configuration.blood_config.wbb_activation_threshold || 30}% of stock
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">
                     {t('tab4.wbb_not_enabled')}
                  </Typography>
                )}
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => onNavigate && onNavigate(2)}
                sx={{ minWidth: '120px' }}
              >
                {t('tab4.wbb_configure_btn')}
              </Button>
            </Box>
          </Alert>

          <Divider sx={{ my: 2 }} />

          {/* Resupply Events */}
          <Box id="resupply-section" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2">{t('tab4.resupply_schedule')}</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddResupply}>{t('tab4.add_resupply')}</Button>
          </Box>
          
          {/* PHASE 4.7.2: Return to Tab 5 Prompt */}
          {resupplyModified && onNavigate && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setResupplyModified(false)}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      // Save in place — do NOT navigate. User may still be editing Tab 4.
                      setResupplyModified(false);
                      handleSave(false);
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('tab4.save_changes')}
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setResupplyModified(false);
                      handleSave(true);
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('tab4.save_go_tab5')}
                  </Button>
                </Box>
              }
            >
              <AlertTitle>{t('tab4.resupply_schedule_updated')}</AlertTitle>
              <Typography variant="body2">
                {t('tab4.resupply_modified_body')}
              </Typography>
            </Alert>
          )}
          
          {/* PHASE 4.3.2: Consumption Discrepancy Alert */}
          {consumptionDiscrepancy && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon fontSize="small" />
                  {t('tab4.consumption_rates_updated')}
                </Box>
              </AlertTitle>
              <Typography variant="body2" paragraph>
                The calculator (Tab 5) has produced more accurate consumption rates than the preliminary estimates. 
                The differences are shown below:
              </Typography>
              <Grid container spacing={1}>
                {consumptionDiscrepancy.map((disc, i) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={i}>
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" fontWeight="bold">{disc.product}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('tab4.preliminary_label')}
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {disc.preliminary} {t('tab4.units_per_day')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('tab4.calculated_value_label')}
                        </Typography>
                        <Typography variant="caption" fontWeight="bold" color="success.main">
                          {disc.actual} {t('tab4.units_per_day')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color={disc.isHigher ? 'error.main' : 'success.main'}>
                          {disc.percentDiff}% {disc.isHigher ? t('tab4.higher_label') : t('tab4.lower_label')}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('tab4.using_calculated_note')}
              </Typography>
            </Alert>
          )}
          
          {/* PHASE 4.0.5: Auto-Suggest Resupply Panel */}
          {stockoutPredictions.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon fontSize="small" />
                    {t('tab4.predicted_stockouts')}
                  </Box>
                  {/* PHASE 4.3.3: Data source badge */}
                  <Chip 
                    label={consumptionDataSource.badge}
                    size="small"
                    color={consumptionDataSource.color}
                    sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                  />
                </Box>
              </AlertTitle>
              <Typography variant="body2" paragraph>
                Based on current stock levels and {consumptionDataSource.label.toLowerCase()} consumption rates, the following products 
                will run out before the end of the {scenario?.operation_parameters?.duration_days || 7}-day operation:
              </Typography>
              <Grid container spacing={2}>
                {stockoutPredictions.map((pred, i) => (
                  <Grid size={{ xs: 12, md: 6 }} key={i}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: pred.severity === 'CRITICAL' ? 'error.light' : 
                                pred.severity === 'WARNING' ? 'warning.light' : 'grey.50',
                        border: pred.severity === 'CRITICAL' ? 2 : 1,
                        borderColor: pred.severity === 'CRITICAL' ? 'error.main' : 
                                    pred.severity === 'WARNING' ? 'warning.main' : 'grey.300'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography 
                          variant="subtitle2" 
                          color={pred.severity === 'CRITICAL' ? 'error.dark' : 
                                pred.severity === 'WARNING' ? 'warning.dark' : 'text.primary'}
                          fontWeight="bold"
                        >
                          {pred.product}
                        </Typography>
                        <Chip 
                          label={pred.severity} 
                          size="small"
                          color={pred.severity === 'CRITICAL' ? 'error' : 
                                pred.severity === 'WARNING' ? 'warning' : 'default'}
                          sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('tab4.current_stock_label')} <strong>{pred.currentStock} units</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('tab4.daily_consumption_label')} <strong>{pred.dailyConsumption} {t('tab4.units_per_day')}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('tab4.days_remaining_label')} <strong>{pred.daysRemaining} days</strong>
                      </Typography>
                      <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('tab4.stockout_day_label')} {pred.stockoutDay}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      {/* BUG-SCHED-001 FIX: renamed from "Recommended" to "Emergency deadline" —
                          this is the last-resort order deadline (stockout - safety margin),
                          not a DOS-optimized schedule. For planned resupply use Auto-Schedule. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.light', p: 1, borderRadius: 1 }}>
                        <LocalShippingIcon fontSize="small" color="warning" />
                        <Typography variant="body2" color="warning.dark" fontWeight="bold">
                          {t('tab4.emergency_deadline_label')} {pred.recommendedResupplyDay}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('tab4.dos_planning_note')}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAutoScheduleResupply(pred)}
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{ mt: 1 }}
                        color={pred.severity === 'CRITICAL' ? 'error' : 'primary'}
                      >
                        {t('tab4.schedule_resupply_btn')} {pred.recommendedResupplyDay}
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              {/* PHASE 4.4: Enhanced Auto-Schedule with Strategy Selection */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>{t('tab4.strategy_label')}</InputLabel>
                  <Select
                    value={autoScheduleStrategy}
                    onChange={(e) => setAutoScheduleStrategy(e.target.value)}
                    label={t('tab4.strategy_label')}
                  >
                    {Object.entries(STRATEGIES).map(([key, strategy]) => (
                      <MenuItem key={key} value={key}>
                        <Box>
                          <Typography variant="body2">{strategy.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {strategy.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip 
                  title="Auto-Schedule analyzes Tab 5 calculator results and automatically generates optimal resupply events to prevent stockout. Uses actual consumption rates (not estimates) for accuracy." 
                  arrow 
                  placement="top"
                >
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={handleGenerateAutoSchedule}
                    startIcon={<CalculateIcon />}
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {t('tab4.generate_auto_schedule')}
                  </Button>
                </Tooltip>
                {lastAutoSchedule && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRevertAutoSchedule}
                    color="warning"
                  >
                    {t('tab4.revert_to_manual')}
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 400 }}>
                  Preview optimal resupply schedule before applying. {STRATEGIES[autoScheduleStrategy]?.description}
                </Typography>
              </Box>
            </Alert>
          )}
          
          {resupplyEvents.length > 0 ? (
            /* UX-4A-005 FIX: overflowX:'auto' exposes rightmost action columns (eye + delete)
               that were hidden without a scroll affordance on narrow viewports. */
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Tooltip title="Day the convoy departs. Stock arrives 1–2 days later depending on delivery method and source distance." arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                          {t('tab4.dispatch_day')}
                          <InfoIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'action.active' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delivery method affects speed and reliability. BLUE_LIGHT = emergency helicopter resupply (MASCAL Escalation), Air = fixed-wing transport, Ground = convoy" arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                          {t('tab4.method_col')}
                          <InfoIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'action.active' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="NATO standard supply packages. Emergency Blood = critical resupply, Routine = standard replenishment, Custom = specify individual items" arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                          {t('tab4.replenishment_pkg')}
                          <InfoIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'action.active' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{t('tab4.destination_mtf')}</TableCell>
                    {/* PHASE 4.5.1: Brigade column */}
                    <TableCell>
                      <Tooltip title="Target specific brigade or distribute across all forces. Useful for multi-brigade operations with uneven consumption" arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                          {t('tab4.brigade_col')}
                          <InfoIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'action.active' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* PHASE 4.5.2: Source column */}
                    <TableCell>
                      <Tooltip title="Supply source affects delivery time. Role 3 = Corps (fastest), Role 4 = Theater (reliable), Theater Depot = Strategic (slow but large capacity)" arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                          {t('tab4.source_col')}
                          <InfoIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'action.active' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{t('tab4.contents_col')}</TableCell>
                    <TableCell align="right">{t('tab4.actions_col')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resupplyEvents.map((event, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>
                          {/* BUG-156 FIX: inputProps enforces [1, duration] at browser level */}
                          <TextField
                            type="number"
                            value={event.day}
                            onChange={(e) => handleResupplyChange(index, 'day', Number(e.target.value))}
                            size="small"
                            sx={{ width: 80 }}
                            inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days || 30 }}
                            error={event.day > (scenario?.operation_parameters?.duration_days || 30) || event.day < 1}
                            title={event.day > (scenario?.operation_parameters?.duration_days || 30) ? `Day ${event.day} exceeds operation duration` : ''}
                          />
                        </TableCell>
                        <TableCell>
                          <Select value={event.delivery_method} onChange={(e) => handleResupplyChange(index, 'delivery_method', e.target.value)} size="small" sx={{ width: 160 }}>
                            <MenuItem value="GROUND">{t('tab4.ground_method')}</MenuItem>
                            <MenuItem value="AIR">{t('tab4.air_method')}</MenuItem>
                            <MenuItem value="BLUE_LIGHT">{t('tab4.mascal_emergency_air')}</MenuItem>
                            <MenuItem value="DRONE">{t('tab4.drone_uas')}</MenuItem>
                          </Select>
                          {/* ENH-008: Drone type selector — only shown when DRONE delivery selected */}
                          {event.delivery_method === 'DRONE' && (
                            <Box sx={{ mt: 0.5 }}>
                              <Select
                                value={event.drone_type || DRONE_DEFAULT_TYPE}
                                onChange={(e) => handleResupplyChange(index, 'drone_type', e.target.value)}
                                size="small"
                                sx={{ width: 200 }}
                              >
                                {Object.entries(DRONE_DELIVERY_SPECS).map(([key, spec]) => (
                                  <MenuItem key={key} value={key}>
                                    <Box>
                                      <Typography variant="body2">{spec.label}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {spec.cold_chain_viable ? `${spec.blood_units_per_flight} PRBC + ` : ''}{spec.tourniquet_packs} tourniquets
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {DRONE_DELIVERY_SPECS[event.drone_type || DRONE_DEFAULT_TYPE]?.cold_chain_note}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* PKG-004: Package list filtered by mtf_target via getPackagesForDestination() */}
                          {event.push_package && !(event.push_package in getPackagesForDestination(event.mtf_target || 'ALL')) && (
                            <Alert severity="error" sx={{ mb: 0.5, py: 0, fontSize: '0.72rem' }}>
                              <strong>{PUSH_PACKAGES[event.push_package]?.label || event.push_package}</strong> is not valid for this destination — it contains no blood products or surgical supplies. Select a different package.
                            </Alert>
                          )}
                          {/* ENH-021 (March 14, 2026): Package dropdown now shows label + short_summary
                              caption so user can distinguish packages without opening a separate panel.
                              Select widened to minWidth 220 to accommodate two-line items cleanly.
                              short_summary field added to each PUSH_PACKAGES entry in classVIII.js v3.2. */}
                          <Select value={event.push_package || ''} onChange={(e) => handleResupplyChange(index, 'push_package', e.target.value)} size="small" displayEmpty sx={{ minWidth: 220 }}>
                            <MenuItem value="">Custom</MenuItem>
                            {Object.entries(getPackagesForDestination(event.mtf_target || 'ALL')).map(([key, pkg]) => (
                              <MenuItem key={key} value={key}>
                                <Box>
                                  <Typography variant="body2">{pkg.label}</Typography>
                                  {pkg.short_summary && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {pkg.short_summary}
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        {/* PHASE 4.0.2: Destination MTF Dropdown */}
                        <TableCell>
                          <Select 
                            value={event.mtf_target || 'ALL'} 
                            onChange={(e) => handleResupplyChange(index, 'mtf_target', e.target.value)} 
                            size="small" 
                            sx={{ width: 160 }}
                          >
                            <MenuItem value="ALL">{t('tab4.all_mtfs')}</MenuItem>
                            <MenuItem value="ROLE1">{t('tab4.role1_bas_short')}</MenuItem>
                            <MenuItem value="ROLE2_BASIC">{t('tab4.role2b_short')}</MenuItem>
                            <MenuItem value="ROLE2_ENHANCED">{t('tab4.role2e_short')}</MenuItem>
                            <MenuItem value="ROLE3">{t('tab4.role3_mtf_short')}</MenuItem>
                            {medicalFacilities && medicalFacilities.facilities && (
                              <>
                                <MenuItem disabled>&nbsp;</MenuItem>
                                <MenuItem disabled>{t('tab4.specific_facilities')}</MenuItem>
                                {medicalFacilities.facilities
                                  .filter(f => ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3'].includes(f.type))
                                  .map(f => (
                                    <MenuItem key={f.id} value={f.id}>
                                      {f.name || `${f.type} - ${f.hierarchy?.brigade || f.hierarchy?.division || f.hierarchy?.corps || 'Unknown'}`}
                                    </MenuItem>
                                  ))}
                              </>
                            )}
                          </Select>
                        </TableCell>
                        {/* PHASE 4.5.1: Brigade-Specific Targeting */}
                        <TableCell>
                          <Select 
                            value={event.destination_brigade || 'ALL'} 
                            onChange={(e) => handleResupplyChange(index, 'destination_brigade', e.target.value)} 
                            size="small" 
                            sx={{ width: 160 }}
                          >
                            <MenuItem value="ALL">{t('tab4.all_brigades')}</MenuItem>
                            {scenario?.force?.forceBuilderResult?.brigades?.map((bde, i) => (
                              <MenuItem key={i} value={`brigade_${i}`}>
                                {bde.name || bde.brigade_name || `Brigade ${i + 1}`}
                              </MenuItem>
                            ))}
                            {scenario?.force?.brigades?.map((bde, i) => (
                              <MenuItem key={i} value={`brigade_${i}`}>
                                {bde.name || bde.brigade_name || `Brigade ${i + 1}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        {/* PHASE 4.5.2: Alternate Supply Sources */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Select
                              value={event.source || 'ROLE_3'}
                              onChange={(e) => handleResupplyChange(index, 'source', e.target.value)}
                              size="small"
                              sx={{ width: 180 }}
                            >
                              {Object.entries(SUPPLY_SOURCES).map(([key, source]) => (
                                <MenuItem key={key} value={key}>
                                  <Box>
                                    <Typography variant="body2">{source.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {/* ENUM-SWEEP FIX */}
                      {estimateDeliveryTime(key, event.delivery_method)}h via {({ AIR: 'Air', GROUND: 'Ground', BLUE_LIGHT: 'Blue Light', DRONE: 'Drone' }[event.delivery_method] ?? event.delivery_method)}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            <Tooltip
                              title={
                                <Box sx={{ p: 0.5 }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {getSupplySource(event.source || 'ROLE_3').label}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {getSupplySource(event.source || 'ROLE_3').description}
                                  </Typography>
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    Reliability: {(getSupplySource(event.source || 'ROLE_3').reliability * 100).toFixed(0)}%
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Capacity: {getSupplySource(event.source || 'ROLE_3').capacity}
                                  </Typography>
                                </Box>
                              }
                              arrow
                            >
                              <InfoIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'action.active', cursor: 'help', flexShrink: 0 }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {/* T4-005: View Contents Button */}
                        <TableCell>
                          <Tooltip title={expandedPackageIndex === index ? t('tab4.hide_contents') : t('tab4.view_contents')}>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedPackageIndex(expandedPackageIndex === index ? null : index)}
                              color={expandedPackageIndex === index ? "primary" : "default"}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right"><IconButton size="small" onClick={() => handleRemoveResupply(index)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                      
                      {/* T4-005: Package Contents Viewer / T4-006: Custom Package Editor */}
                      {/* T4-013: Added defensive null checks for getPushPackageDetails */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedPackageIndex === index} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              {event.push_package ? (
                                // T4-005: Show standard package contents using getPushPackageDetails
                                (() => {
                                  const pkgDetails = getPushPackageDetails(event.push_package, nationalStandard);
                                  // T4-013: Defensive check - show fallback if no package details
                                  if (!pkgDetails || !pkgDetails.contents) {
                                    return (
                                      <Alert severity="warning">
                                        <AlertTitle>Package Contents Unavailable</AlertTitle>
                                        Package "{event.push_package}" details not found for {nationalStandard} standard. 
                                        Try selecting a different package or use "Custom" to define contents manually.
                                      </Alert>
                                    );
                                  }
                                  const contents = pkgDetails.contents || {};
                                  // Extract nested structures for proper access
                                  const bloodProducts = contents.blood_products || {};
                                  const pharmaceuticals = contents.pharmaceuticals || {};
                                  
                                  return (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InventoryIcon fontSize="small" />
                                        {pkgDetails?.label || event.push_package} Contents ({nationalStandard} Standard)
                                      </Typography>
                                      <Grid container spacing={2}>
                                        <Grid size={{ xs: 6 }}>
                                          <Typography variant="body2" fontWeight="bold">{t('tab4.blood_products_label')}</Typography>
                                          <Typography variant="body2">- PRBC: {bloodProducts.prbc_units ?? 0} units</Typography>
                                          <Typography variant="body2">- FFP: {bloodProducts.ffp_units ?? 0} units</Typography>
                                          <Typography variant="body2">- Platelets: {bloodProducts.platelets_units ?? 0} units</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                          <Typography variant="body2" fontWeight="bold">{t('tab4.fluids_meds_label')}</Typography>
                                          <Typography variant="body2">- IV Fluids: {pharmaceuticals.iv_fluids_liters ?? 0} L</Typography>
                                          <Typography variant="body2">- Antibiotics: {pharmaceuticals.antibiotics_doses ?? 0} doses</Typography>
                                          <Typography variant="body2">- Controlled: {pharmaceuticals.morphine_doses ?? pharmaceuticals.controlled_doses ?? 0} doses</Typography>
                                        </Grid>
                                      </Grid>
                                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {pkgDetails?.dos_equivalent && (
                                          <Chip 
                                            label={`~${pkgDetails.dos_equivalent} DOS equivalent`} 
                                            size="small" 
                                            color="info" 
                                          />
                                        )}
                                        {/* ENH-010: Package weight/volume chips */}
                                        {(() => {
                                          // Compute blood product weight for this package
                                          const prcbW  = (bloodProducts.prbc_units     ?? 0) * (ITEM_WEIGHTS_KG.prbc       || 0);
                                          const ffpW   = (bloodProducts.ffp_units       ?? 0) * (ITEM_WEIGHTS_KG.ffp        || 0);
                                          const pltW   = (bloodProducts.platelets_units ?? 0) * (ITEM_WEIGHTS_KG.platelets  || 0);
                                          const ivW    = (pharmaceuticals.iv_fluids_liters ?? 0) * (ITEM_WEIGHTS_KG.crystalloid || 0);
                                          const bloodPkgKg    = prcbW + ffpW + pltW;
                                          const materiелPkgKg = ivW;
                                          const totalPkgKg    = bloodPkgKg + materiелPkgKg;
                                          const prcbV  = (bloodProducts.prbc_units     ?? 0) * (ITEM_VOLUMES_M3.prbc       || 0);
                                          const ffpV   = (bloodProducts.ffp_units       ?? 0) * (ITEM_VOLUMES_M3.ffp        || 0);
                                          const pltV   = (bloodProducts.platelets_units ?? 0) * (ITEM_VOLUMES_M3.platelets  || 0);
                                          const ivV    = (pharmaceuticals.iv_fluids_liters ?? 0) * (ITEM_VOLUMES_M3.crystalloid || 0);
                                          const totalPkgM3    = prcbV + ffpV + pltV + ivV;
                                          if (totalPkgKg === 0) return null;
                                          return (
                                            <>
                                              <Chip
                                                icon={<LocalShippingIcon />}
                                                label={`~${totalPkgKg.toFixed(0)} kg total`}
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                              />
                                              <Chip
                                                label={`${(totalPkgM3 * 1000).toFixed(1)} L volume`}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                              />
                                              {bloodPkgKg > 0 && (
                                                <Chip
                                                  label={`${bloodPkgKg.toFixed(0)} kg blood (cold chain)`}
                                                  size="small"
                                                  color="error"
                                                  variant="outlined"
                                                />
                                              )}
                                            </>
                                          );
                                        })()}
                                        {pkgDetails?.description && (
                                          <Typography variant="caption" color="text.secondary">
                                            {pkgDetails.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })()
                              ) : (
                                // T4-006: Custom Package Editor
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EditIcon fontSize="small" />
                                    {t('tab4.custom_pkg_def')}
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {[
                                      { field: 'iv_fluids_liters', label: 'IV Fluids (L)', default: 50 },
                                      { field: 'prbc_units', label: 'PRBC Units', default: 10 },
                                      { field: 'ffp_units', label: 'FFP Units', default: 10 },
                                      { field: 'platelets_units', label: 'Platelets', default: 5 },
                                      { field: 'antibiotics_doses', label: 'Antibiotics', default: 50 },
                                      { field: 'controlled_substances_doses', label: 'Controlled', default: 20 },
                                      { field: 'dressings_sets', label: 'Dressings', default: 50 }
                                    ].map(({ field, label, default: defaultVal }) => (
                                      <Grid size={{ xs: 6, sm: 3 }} key={field}>
                                        <TextField
                                          fullWidth
                                          label={label}
                                          type="number"
                                          size="small"
                                          value={event.supplies?.[field] || defaultVal}
                                          onChange={(e) => {
                                            const newSupplies = { ...event.supplies, [field]: Number(e.target.value) || 0 };
                                            // BUG-84-008: manual field edit while a predefined package is selected
                                            // → switch type to Custom so the calculator uses the edited values,
                                            //   not the predefined package defaults.
                                            if (event.push_package) {
                                              handleResupplyChange(index, 'push_package', '');
                                            }
                                            handleResupplyChange(index, 'supplies', newSupplies);
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                  <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
                                    {t('tab4.custom_pkg_info')}
                                  </Alert>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">{t('tab4.no_resupply_events')}</Alert>
          )}

          {/* ENH-026: Cumulative Transport Summary — shown when events are scheduled */}
          {resupplyEvents.length > 0 && (() => {
            const METHOD_LABELS = { GROUND: 'Ground', AIR: 'Air', BLUE_LIGHT: 'MASCAL Emergency (Air)', DRONE: 'Drone UAS' };
            const methodCounts = resupplyEvents.reduce((acc, ev) => {
              const m = ev.delivery_method || 'GROUND';
              acc[m] = (acc[m] || 0) + 1;
              return acc;
            }, {});
            const totalUnits = logisticsProjection?.medical_requirements?.resupply_summary?.total_units;
            return (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <InventoryIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                    {t('tab4.cumulative_transport_summary')}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {resupplyEvents.length} event{resupplyEvents.length !== 1 ? 's' : ''} scheduled
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('tab4.delivery_method_breakdown')}
                </Typography>
                <Grid container spacing={1} sx={{ mb: totalUnits ? 1.5 : 0 }}>
                  {Object.entries(methodCounts).map(([method, count]) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={method}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight="bold">{count}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {METHOD_LABELS[method] || method}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {totalUnits && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('tab4.total_blood_products')}
                    </Typography>
                    <Grid container spacing={1}>
                      {[
                        { key: 'prbc', label: 'PRBC',      borderColor: 'error.light',     color: 'error.main' },
                        { key: 'ffp',  label: 'FFP',       borderColor: 'warning.light',   color: 'warning.main' },
                        { key: 'plt',  label: 'Platelets', borderColor: 'info.light',      color: 'info.main' },
                        { key: 'cryo', label: 'Cryo',      borderColor: 'secondary.light', color: 'secondary.main' }
                      ].filter(({ key }) => (totalUnits[key] ?? 0) > 0).map(({ key, label, borderColor, color }) => (
                        <Grid size={{ xs: 6, sm: 3 }} key={key}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor }}>
                            <Typography variant="h6" fontWeight="bold" color={color}>
                              {totalUnits[key].toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">{label} units</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                  Aggregate across all {resupplyEvents.length} scheduled resupply event{resupplyEvents.length !== 1 ? 's' : ''}.
                  {totalUnits ? ' Blood product totals from logistics projection.' : ' Run logistics projection for blood product totals.'}
                </Typography>
              </Box>
            );
          })()}

          {/* ENH-027: Convoy Planning card — moved here (below resupply schedule) for logical UX flow */}
          {logisticsProjection?.daily_weight_volume && (
            <Card sx={{ mt: 2, mb: 1, border: '1px solid', borderColor: 'grey.300', bgcolor: 'grey.50' }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <LocalShippingIcon sx={{ color: 'warning.dark' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                    {/* BUG-083 FIX: Title reflects whether convoys are scheduled */}
                    Convoy Planning — {resupplyEvents.length > 0 ? 'Resupply Requirement' : 'Consumption Reference'}
                  </Typography>
                  <Tooltip
                    title={`Based on ${logisticsProjection.national_standard} national standard average daily consumption. Truck capacity: ${CONVOY_CAPACITY.STANDARD_TRUCK.label} (${CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg.toLocaleString()} kg / ${CONVOY_CAPACITY.STANDARD_TRUCK.effective_volume_m3} m³ effective, 80% planning factor). Binding constraint (weight or volume) determines trucks required. Cold-chain vehicles required for blood products.`}
                    arrow
                    placement="top"
                  >
                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Commanded DOS: {logisticsProjection.target_mdos_days ?? '?'} days
                  </Typography>
                </Box>

                {(() => {
                  const wv = logisticsProjection.daily_weight_volume;
                  return (
                    <Grid container spacing={2}>
                      {/* Blood weight */}
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'error.light' }}>
                          <Typography variant="h6" color="error.main" fontWeight="bold">
                            {wv.blood_weight_kg.toLocaleString()} kg
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab4.blood_products_per_day')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                            {(wv.blood_volume_m3 * 1000).toFixed(1)} L · {t('tab4.cold_chain_required')}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* Materiel weight */}
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                          <Typography variant="h6" color="info.main" fontWeight="bold">
                            {wv.materiel_weight_kg.toLocaleString()} kg
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab4.materiel_per_day')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                            {(wv.materiel_volume_m3 * 1000).toFixed(1)} L
                          </Typography>
                        </Box>
                      </Grid>
                      {/* Total */}
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                          <Typography variant="h6" fontWeight="bold">
                            {wv.total_weight_kg.toLocaleString()} kg
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab4.total_daily_weight')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                            {wv.total_volume_m3.toFixed(2)} m³ volume
                          </Typography>
                        </Box>
                      </Grid>
                      {/* BUG-083 FIX: Trucks box only shown when convoys are scheduled. */}
                      <Grid size={{ xs: 6, sm: 3 }}>
                        {resupplyEvents.length > 0 ? (
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: wv.trucks_required >= 3 ? 'error.light' : wv.trucks_required >= 2 ? 'warning.light' : 'success.light', borderRadius: 1, border: '1px solid', borderColor: wv.trucks_required >= 3 ? 'error.main' : wv.trucks_required >= 2 ? 'warning.main' : 'success.main' }}>
                            <Typography variant="h6" fontWeight="bold" color={wv.trucks_required >= 3 ? 'error.dark' : wv.trucks_required >= 2 ? 'warning.dark' : 'success.dark'}>
                              {wv.trucks_required} truck{wv.trucks_required !== 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('tab4.per_resupply_event')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                              {t('tab4.binding_constraint')} {wv.trucks_weight >= wv.trucks_volume ? 'weight' : 'volume'}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1, border: '1px dashed', borderColor: 'grey.400', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              {t('tab4.no_convoys_scheduled')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', mt: 0.5 }}>
                              {t('tab4.configure_schedule_above')}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  );
                })()}

                {/* BUG-083 FIX: Footnote reflects schedule state */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                  {resupplyEvents.length > 0
                    ? `Truck estimate based on ${resupplyEvents.length} scheduled resupply event${resupplyEvents.length !== 1 ? 's' : ''}. Actual truck count per event depends on consolidated load. `
                    : 'Weight and volume figures are average daily consumption — for planning reference only. Truck requirement cannot be estimated without a scheduled convoy. '}
                  Blood products require cold-chain vehicles (isothermal body or refrigerated truck).
                </Typography>

                {/* ARCH-002: Convoy travel time — shown when projection has convoy_plan */}
                {logisticsProjection?.convoy_plan && (() => {
                  const cp = logisticsProjection.convoy_plan;
                  const borderColor = cp.warning === 'EXTREME' ? 'error.main' : cp.warning === 'MULTI_DAY' ? 'warning.main' : cp.warning === 'SLOW_ROUTE' ? 'warning.light' : 'success.main';
                  const bgColor     = cp.warning === 'EXTREME' ? 'error.light'   : cp.warning === 'MULTI_DAY' ? 'warning.light'  : 'success.light';
                  return (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: bgColor, borderRadius: 1, border: '1px solid', borderColor }}>
                      <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                        {t('tab4.msr_travel_time')} — {cp.road_type_label}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">{t('tab4.one_way')}</Typography>
                          <Typography variant="body2" fontWeight="bold">{cp.one_way_hours.toFixed(1)} h</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">{t('tab4.round_trip')}</Typography>
                          <Typography variant="body2" fontWeight="bold">{cp.round_trip_hours.toFixed(1)} h</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">{t('tab4.speed')}</Typography>
                          <Typography variant="body2" fontWeight="bold">{cp.effective_speed_kmh} km/h</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">{t('tab4.lead_time')}</Typography>
                          <Typography variant="body2" fontWeight="bold"
                            color={cp.lead_time_days >= 3 ? 'error.main' : cp.lead_time_days >= 2 ? 'warning.main' : 'success.main'}>
                            {cp.lead_time_days} day{cp.lead_time_days !== 1 ? 's' : ''} advance
                          </Typography>
                        </Grid>
                      </Grid>
                      {cp.warning_label && (
                        <Typography variant="caption" color={cp.warning === 'EXTREME' ? 'error.dark' : 'text.secondary'} sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                          {cp.warning_label}
                        </Typography>
                      )}
                      {cp.same_day_feasible && !cp.warning && (
                        <Typography variant="caption" color="success.dark" sx={{ display: 'block', mt: 0.5 }}>
                          {t('tab4.same_day_feasible')}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          )}
          {/* END ENH-010/ARCH-002/ENH-027 Convoy Planning card */}
        </AccordionDetails>
      </Accordion>

      {/* Section 4B: Personnel Planning */}
      <Accordion expanded={expandedSections.personnel} onChange={() => setExpandedSections(prev => ({ ...prev, personnel: !prev.personnel }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4b')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Chip 
              label={`Staffing: ${personnelAdequacy?.overall_percent || 0}%`} 
              color={personnelAdequacy?.color || 'default'} 
              size="small" 
            />
            <Chip 
              label={personnelAdequacy?.overall_status || 'UNKNOWN'} 
              color={personnelAdequacy?.color || 'default'} 
              size="small" 
              variant="outlined"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* T45-004: Personnel Adequacy Dashboard */}
          <Card sx={{ mb: 2, bgcolor: personnelAdequacy?.overall_status === 'CRITICAL' ? 'error.light' : personnelAdequacy?.overall_status === 'MARGINAL' ? 'warning.light' : 'success.light' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {personnelAdequacy?.overall_status === 'ADEQUATE' ? <CheckCircleIcon color="success" /> : 
                 personnelAdequacy?.overall_status === 'MARGINAL' ? <WarningIcon color="warning" /> : 
                 <ErrorIcon color="error" />}
                {t('tab4.personnel_adequacy_assessment')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="h3" color={personnelAdequacy?.color}>{personnelAdequacy?.overall_percent || 0}%</Typography>
                  <Typography variant="caption">{t('tab4.overall_staffing')}</Typography>
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="body2">{t('tab4.mtfs_configured')}</Typography>
                  <Typography variant="body2">R1: {personnelAdequacy?.mtf_counts?.role1 || 0} | R2B: {personnelAdequacy?.mtf_counts?.role2b || 0}</Typography>
                  <Typography variant="body2">R2E: {personnelAdequacy?.mtf_counts?.role2e || 0} | R3: {personnelAdequacy?.mtf_counts?.role3 || 0}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('tab4.col_category')}</TableCell>
                          <TableCell align="right">{t('tab4.col_required')}</TableCell>
                          <TableCell align="right">{t('tab4.col_available')}</TableCell>
                          <TableCell align="right">{t('tab4.col_status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {personnelAdequacy?.details?.map((row) => (
                          <TableRow key={row.category}>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{row.category}</TableCell>
                            <TableCell align="right">{row.required}</TableCell>
                            <TableCell align="right">{row.available}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={row.shortage > 0 ? `-${row.shortage}` : row.surplus > 0 ? `+${row.surplus}` : 'OK'} 
                                size="small" 
                                color={row.status === 'ADEQUATE' ? 'success' : row.status === 'MARGINAL' ? 'warning' : 'error'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
              {personnelAdequacy?.critical_shortages?.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>{t('tab4.critical_shortages')}</AlertTitle>
                  {personnelAdequacy.critical_shortages.map(s =>
                    `${s.category}: Need ${s.shortage} more (${s.available}/${s.required})`
                  ).join(' | ')}
                </Alert>
              )}
              {/* BUG-S96-013/014: explain available count sources for transparency */}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Available = organic MTF staffing + manual additional (Medical Personnel Strength fields below)
                {(personnelAdequacy?.medical_arrival_total || 0) > 0 && ` + ${personnelAdequacy.medical_arrival_total} from Personnel Arrivals Schedule`}.
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="subtitle2" gutterBottom>{t('tab4.medical_personnel')}</Typography>
          <Grid container spacing={2}>
            {[
              { field: 'surgeons', labelKey: 'surgeons' },
              { field: 'physicians', labelKey: 'physicians' },
              { field: 'nurses', labelKey: 'nurses' },
              { field: 'medics', labelKey: 'combat_medics' }
            ].map(({ field, labelKey }) => (
              <Grid size={{ xs: 6, sm: 3 }} key={field}>
                <TextField fullWidth label={t(`tab4.${labelKey}`)} type="number" value={personnel[field]} onChange={(e) => handlePersonnelChange(field, e.target.value)} size="small" />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>{t('tab4.mental_health_personnel_title')}</Typography>
          <Grid container spacing={2}>
            {[
              { field: 'psychiatrists', labelKey: 'psychiatrists' },
              { field: 'psychologists', labelKey: 'psychologists' },
              { field: 'chaplains', labelKey: 'chaplains' }
            ].map(({ field, labelKey }) => (
              <Grid size={{ xs: 6, sm: 4 }} key={field}>
                <TextField fullWidth label={t(`tab4.${labelKey}`)} type="number" value={personnel[field]} onChange={(e) => handlePersonnelChange(field, e.target.value)} size="small" />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel control={<Switch checked={replacementPlanning.enabled} onChange={(e) => setReplacementPlanning(prev => ({ ...prev, enabled: e.target.checked }))} />} label={t('tab4.enable_replacement')} />

          {replacementPlanning.enabled && (
            <>
              {/* BUG-S96-016: Clarify Reserve Pool vs Arrivals */}
              <Alert severity="info" icon={false} sx={{ mb: 1.5, mt: 1, py: 0.5 }}>
                <Typography variant="caption" display="block">
                  <strong>Reserve Pool</strong> — personnel already in theater, held back from forward deployment as a buffer for in-place casualties. These are organic to the operation (e.g. theater replacement company).
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  <strong>Personnel Arrivals Schedule</strong> (below) — external reinforcements arriving from outside theater during the operation (e.g. NATO augmentation, national rotation). These are additive to the reserve pool from the day they arrive.
                </Typography>
              </Alert>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 4 }}><TextField fullWidth label={t('tab4.combat_reserve_pool')} type="number" value={replacementPlanning.reserve_combat} onChange={(e) => setReplacementPlanning(prev => ({ ...prev, reserve_combat: Number(e.target.value) }))} size="small" helperText={t('tab4.combat_reserve_pool_helper')} /></Grid>
                <Grid size={{ xs: 4 }}><TextField fullWidth label={t('tab4.medical_reserve_pool')} type="number" value={replacementPlanning.reserve_medical} onChange={(e) => setReplacementPlanning(prev => ({ ...prev, reserve_medical: Number(e.target.value) }))} size="small" helperText={t('tab4.medical_reserve_pool_helper')} /></Grid>
                <Grid size={{ xs: 4 }}><TextField fullWidth label={t('tab4.replacement_timeline')} type="number" value={replacementPlanning.replacement_timeline_days} onChange={(e) => setReplacementPlanning(prev => ({ ...prev, replacement_timeline_days: Number(e.target.value) }))} size="small" helperText={t('tab4.replacement_timeline_helper')} /></Grid>
              </Grid>
            </>
          )}

          {/* v5.9.0: Personnel Arrivals Schedule */}
          {replacementPlanning.enabled && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonAddIcon color="secondary" />
                  {t('tab4.personnel_arrivals_schedule')}
                </Typography>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddPersonnelArrival}>
                  {t('tab4.add_arrival')}
                </Button>
              </Box>

              {personnelArrivals.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('tab4.no_reinforcements')}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>{t('tab4.arrivals_col_day')}</TableCell>
                        <TableCell>{t('tab4.arrivals_col_quantity')}</TableCell>
                        <TableCell>{t('tab4.arrivals_col_type')}</TableCell>
                        <TableCell>{t('tab4.arrivals_col_destination')}</TableCell>
                        <TableCell align="right">{t('tab4.arrivals_col_actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {personnelArrivals.map((arrival, index) => (
                        <TableRow key={arrival.id || index} hover>
                          <TableCell>
                            <TextField
                              type="number"
                              value={arrival.day}
                              onChange={(e) => handlePersonnelArrivalChange(index, 'day', Number(e.target.value))}
                              size="small"
                              sx={{ width: 80 }}
                              inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days || 30 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={arrival.quantity}
                              onChange={(e) => handlePersonnelArrivalChange(index, 'quantity', Number(e.target.value))}
                              size="small"
                              sx={{ width: 100 }}
                              inputProps={{ min: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={arrival.type}
                                onChange={(e) => handlePersonnelArrivalChange(index, 'type', e.target.value)}
                              >
                                <MenuItem value="COMBAT">{t('tab4.personnel_type_combat')}</MenuItem>
                                <MenuItem value="MEDICAL">{t('tab4.personnel_type_medical')}</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          {/* T4-014: Destination selector */}
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                              <Select
                                value={arrival.destination || 'ALL_UNITS'}
                                onChange={(e) => handlePersonnelArrivalChange(index, 'destination', e.target.value)}
                              >
                                {personnelDestinationOptions.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleRemovePersonnelArrival(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {personnelArrivals.length > 0 && (() => {
                const medicalArrivalTotal = personnelArrivals.filter(a => a.type === 'MEDICAL').reduce((sum, a) => sum + a.quantity, 0);
                const combatArrivalTotal  = personnelArrivals.filter(a => a.type === 'COMBAT').reduce((sum, a) => sum + a.quantity, 0);
                // BUG-S96-016: warn when arrivals exceed reserve pool (likely external, not from pool)
                const medExceedsPool = medicalArrivalTotal > (replacementPlanning.reserve_medical || 0) && (replacementPlanning.reserve_medical || 0) > 0;
                return (
                  <>
                    <Alert severity="success" icon={<TrendingUpIcon />}>
                      <strong>{personnelArrivals.length}</strong> scheduled arrivals totaling{' '}
                      <strong>{personnelArrivals.reduce((sum, a) => sum + a.quantity, 0)}</strong> personnel{' '}
                      ({combatArrivalTotal} combat, {medicalArrivalTotal} medical)
                    </Alert>
                    {medExceedsPool && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Medical arrivals ({medicalArrivalTotal}) exceed the Medical Reserve Pool ({replacementPlanning.reserve_medical}).
                        If these are external reinforcements from outside theater, that is expected. If they are drawn from the reserve pool, the pool is over-committed.
                      </Alert>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* v5.6: T4-007 Personnel Breakdown by Unit */}
          {replacementPlanning.enabled && personnelBreakdownByUnit?.units?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupsIcon color="primary" />
                {t('tab4.projected_replacement_needs')}
              </Typography>
              
              {/* Summary Card */}
              <Card sx={{ mb: 2, bgcolor: personnelBreakdownByUnit.coverage_percent >= 100 ? 'success.light' : personnelBreakdownByUnit.coverage_percent >= 50 ? 'warning.light' : 'error.light' }}>
                <CardContent sx={{ py: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 2 }}>
                      <Typography variant="h4">{personnelBreakdownByUnit.coverage_percent}%</Typography>
                      <Typography variant="caption">{t('tab4.reserve_coverage')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Typography variant="h6">{personnelBreakdownByUnit.total_replacements}</Typography>
                      <Typography variant="caption">{t('tab4.total_replacements')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Typography variant="h6">{Math.round(personnelBreakdownByUnit.daily_casualty_rate * 10) / 10}</Typography>
                      <Typography variant="caption">{t('tab4.daily_casualty_rate')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Typography variant="h6">{personnelBreakdownByUnit.duration} days</Typography>
                      <Typography variant="caption">{t('tab4.operation_duration')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Typography variant="h6">{personnelBreakdownByUnit.reserve_pool}</Typography>
                      <Typography variant="caption">{t('tab4.reserve_pool')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Typography variant="h6" color={personnelBreakdownByUnit.shortfall > 0 ? 'error' : 'success'}>
                        {personnelBreakdownByUnit.shortfall > 0 ? `-${personnelBreakdownByUnit.shortfall}` : '" OK'}
                      </Typography>
                      <Typography variant="caption">{t('tab4.shortfall')}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Unit Breakdown Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>{t('tab4.unit_col_unit')}</TableCell>
                      <TableCell>{t('tab4.unit_col_type')}</TableCell>
                      <TableCell align="right">{t('tab4.unit_col_strength')}</TableCell>
                      <TableCell align="right">{t('tab4.unit_col_total_casualties')}</TableCell>
                      <TableCell align="right">{t('tab4.unit_col_daily_rate')}</TableCell>
                      <TableCell align="right">{t('tab4.unit_col_pct_loss')}</TableCell>
                      <TableCell align="center">{t('tab4.unit_col_priority')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {personnelBreakdownByUnit.units.map((unit, idx) => (
                      <TableRow key={idx} sx={{ bgcolor: unit.priority === 'HIGH' ? 'error.light' : unit.priority === 'MEDIUM' ? 'warning.light' : 'inherit' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={unit.priority === 'HIGH' ? 'bold' : 'normal'}>
                            {unit.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={unit.type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{unit.strength.toLocaleString()}</TableCell>
                        <TableCell align="right">{unit.projected_casualties}</TableCell>
                        <TableCell align="right">{unit.daily_casualties}/day</TableCell>
                        <TableCell align="right">{unit.casualty_percent}%</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={unit.priority} 
                            size="small" 
                            color={unit.priority === 'HIGH' ? 'error' : unit.priority === 'MEDIUM' ? 'warning' : 'success'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {personnelBreakdownByUnit.shortfall > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <AlertTitle>{t('tab4.replacement_shortfall_title')}</AlertTitle>
                  {t('tab4.replacement_shortfall_body', { pool: personnelBreakdownByUnit.reserve_pool, total: personnelBreakdownByUnit.total_replacements, shortfall: personnelBreakdownByUnit.shortfall })}
                </Alert>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Section 4C: Theatre Patient Return Policy (TPRP) */}
      <Accordion expanded={expandedSections.thp} onChange={() => setExpandedSections(prev => ({ ...prev, thp: !prev.thp }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <LocalHospitalIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4c')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Chip label={`RTD: ${rtdProjection?.rtd_rate || '?'}%`} color="info" size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              {/* A-11: InputLabel wrapped in Tooltip with AJP 4.10C reference.
                  "Theatre Holding Policy (THP)" is deprecated per AJP 4.10C Art. 1.16. */}
              <Tooltip
                title="Theatre Patient Return Policy (TPRP) — AJP 4.10C Article 1.16. Formerly known as Theatre Holding Policy (THP); that term is deprecated in current NATO doctrine. TPRP defines the maximum number of days a patient is held in-theatre before strategic evacuation."
                arrow
                placement="top"
              >
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab4.tprp')}</InputLabel>
                  <Select
                    value={thpConfig.policy}
                    onChange={(e) => handleThpChange('policy', e.target.value)}
                    label={t('tab4.tprp')}
                    MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }}
                  >
                    {Object.entries(THEATRE_HOLDING_POLICY || {}).map(([key, policy]) => (<MenuItem key={key} value={key}>{policy.label} ({policy.days} days)</MenuItem>))}
                  </Select>
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}><FormControlLabel control={<Switch checked={thpConfig.strategic_evac_available} onChange={(e) => handleThpChange('strategic_evac_available', e.target.checked)} />} label={t('tab4.stratevac_available')} /></Grid>
            <Grid size={{ xs: 6, sm: 4 }}><FormControlLabel control={<Switch checked={thpConfig.role3_in_theatre} onChange={(e) => handleThpChange('role3_in_theatre', e.target.checked)} />} label={t('tab4.role3_in_theatre')} /></Grid>
          </Grid>

          {rtdProjection && (
            <Card sx={{ mt: 2, bgcolor: (currentScenario?.casualty_projections?.is_estimate || rtdProjection.is_estimated) ? 'warning.light' : 'info.light' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t('tab4.rtd_projection')}
                  {(currentScenario?.casualty_projections?.is_estimate || rtdProjection.is_estimated) && (
                    <Chip label={t('tab4.based_on_estimates')} color="warning" size="small" sx={{ ml: 1 }} />
                  )}
                  {/* v5.11.0: T4-016 MASCAL indicator */}
                  {bedOccupancyAnalysis?.isMASCAL && (
                    <Chip label="MASCAL" color="error" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 3 }}><Typography variant="h4">{rtdProjection.rtd_rate || 0}%</Typography><Typography variant="caption">{t('tab4.overall_rtd_rate')}</Typography></Grid>
                  <Grid size={{ xs: 3 }}><Typography variant="h6">{rtdProjection.total_rtd || 0}</Typography><Typography variant="caption">{t('tab4.total_rtd')}</Typography></Grid>
                  {/* v5.11.0: T4-015 FIX - Use computed evacuationsCount */}
                  <Grid size={{ xs: 3 }}>
                    <Typography variant="h6">{evacuationsCount}</Typography>
                    <Typography variant="caption">{t('tab4.strategic_evac')}</Typography>
                  </Grid>
                  {/* v5.11.0: T4-016 FIX - Enhanced bed occupancy with tooltip and color */}
                  <Grid size={{ xs: 3 }}>
                    <Tooltip title={bedOccupancyAnalysis?.tooltip || ''} arrow>
                      <Box sx={{ cursor: 'help' }}>
                        <Typography 
                          variant="h6" 
                          color={bedOccupancyAnalysis?.color === 'error' ? 'error.main' : 
                                 bedOccupancyAnalysis?.color === 'warning' ? 'warning.main' : 
                                 'success.main'}
                        >
                          {bedOccupancyAnalysis?.rate || 0}%
                          {bedOccupancyAnalysis?.isMASCAL && (
                            <WarningIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} color="error" />
                          )}
                        </Typography>
                        <Typography variant="caption">
                          {t('tab4.bed_occupancy_caption')}
                          {bedOccupancyAnalysis?.status && bedOccupancyAnalysis.status !== 'ADEQUATE' && (
                            <Typography component="span" variant="caption" color={bedOccupancyAnalysis.color + '.main'} sx={{ ml: 0.5 }}>
                              ({bedOccupancyAnalysis.status})
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>
                {(currentScenario?.casualty_projections?.is_estimate || rtdProjection.is_estimated) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {rtdProjection.estimation_note || currentScenario?.casualty_projections?.source || 'Based on historical rates. Run Tab 5 for precise calculation.'}
                  </Typography>
                )}
                {/* v5.11.0: T4-016 MASCAL warning */}
                {bedOccupancyAnalysis?.isMASCAL && (
                  <Alert severity="error" sx={{ mt: 2 }} icon={<WarningIcon />}>
                    <AlertTitle>Bed Capacity Exceeded ({bedOccupancyAnalysis.rate}%)</AlertTitle>
                    Medical bed demand exceeds available capacity. Consider:
                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                      <li>Activating surge beds and overflow areas</li>
                      <li>Expediting strategic evacuations to Role 4</li>
                      <li>Requesting additional medical assets</li>
                      <li>Adjusting Theatre Patient Return Policy (TPRP) to shorter duration</li>
                    </Box>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Section 4D: Bed Occupancy */}
      {/* v8.2.0 BED-OCC: New section. bedOccupancy comes from personnelIntelligence.calculateBedOccupancyAnalysis()
          called in useMemo (guarded by BED-001 — requires Tab3 save). Per-role breakdown added in
          personnelIntelligence.js v2.9. HOSPITALIZATION_RATE and ECHELON_ADMISSION_DISTRIBUTION are
          constants from theatreHoldingPolicy.js v2.6 (APP_RULES 2.1 compliant).
          Role 1 is excluded from all bed counts per AJMedP-1 non-holding doctrine. */}
      <Accordion expanded={expandedSections.bedOccupancy} onChange={() => setExpandedSections(prev => ({ ...prev, bedOccupancy: !prev.bedOccupancy }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <LocalHospitalIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4d')}</Typography>
            <Chip
              label={t('tab4.estimated_chip')}
              size="small"
              variant="outlined"
              color="warning"
              sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}
            />
            <Box sx={{ flexGrow: 1 }} />
            {bedOccupancy && (
              /* BUG-107 FIX: badge now reads bedOccupancyAnalysis.rate (Math.round of occupancy_rate,
                 same source as THP RTD card) and bedOccupancyAnalysis.color (same threshold system).
                 Previously read bedOccupancy.occupancy_rate + bedOccupancy.status directly from
                 PersonnelIntelligence, which uses different status labels (CRITICAL/HIGH/ELEVATED)
                 and potentially a different denominator (incl. CSU/PHA beds), producing a value
                 inconsistent with the Tab4-level bedOccupancyAnalysis used everywhere else in Tab4. */
              <Chip
                label={`${bedOccupancyAnalysis?.rate ?? '?'}% occupied`}
                color={bedOccupancyAnalysis?.color ?? 'success'}
                size="small"
              />
            )}
            {!bedOccupancy && <Chip label={t('tab4.complete_tab3_first')} color="default" size="small" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!bedOccupancy ? (
            <Alert severity="info">
              {t('tab4.tab3_required_beds')}
            </Alert>
          ) : (
            <>
              {/* System-wide summary card */}
              <Card sx={{
                mb: 2,
                bgcolor: bedOccupancy.status === 'CRITICAL' ? 'error.light' :
                         bedOccupancy.status === 'HIGH' ? 'error.light' :
                         bedOccupancy.status === 'ELEVATED' ? 'warning.light' : 'success.light'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {/* UX-S97-003: "ESTIMATED" chip removed from card body — already shown in section header; orange-on-orange = zero contrast */}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('tab4.system_wide_bed_occupancy')}
                    </Typography>
                  </Box>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 3 }}>
                      <Typography variant="h3" color={
                        bedOccupancy.status === 'CRITICAL' || bedOccupancy.status === 'HIGH' ? 'error.dark' :
                        bedOccupancy.status === 'ELEVATED' ? 'warning.dark' : 'success.dark'
                      }>
                        {bedOccupancy.occupancy_rate ?? '?'}%
                      </Typography>
                      <Typography variant="caption">{t('tab4.occupancy_rate')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <Typography variant="h6">{bedOccupancy.total_beds ?? '?'}</Typography>
                      <Typography variant="caption">{t('tab4.total_beds')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <Typography variant="h6">{bedOccupancy.steady_state_beds_required ?? '?'}</Typography>
                      <Typography variant="caption">{t('tab4.beds_required')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <Typography variant="h6">{bedOccupancy.buffer_beds ?? '?'}</Typography>
                      <Typography variant="caption">{t('tab4.buffer_beds')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="body2">
                        Status: <strong>{bedOccupancy.status}</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="body2">
                        {t('tab4.avg_los')}: <strong>{bedOccupancy.avg_length_of_stay ?? '?'} days</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="body2">
                        {t('tab4.surge_capacity')}: <strong>{bedOccupancy.surge_capacity ?? '?'} beds</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Role 1 excluded (non-holding per AJMedP-1). Source: {bedOccupancy.bed_source || 'scenario data'}.
                    Hospitalization rate: {Math.round((bedOccupancy.hospitalization_rate || 0) * 100)}% of casualties.
                  </Typography>
                  <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
                    {t('tab4.estimated_note')}
                  </Typography>
                </CardContent>
              </Card>

              {/* MASCAL warning */}
              {bedOccupancy.status === 'CRITICAL' && (
                <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  <AlertTitle>Bed Capacity Exceeded ({bedOccupancy.occupancy_rate}%)</AlertTitle>
                  Medical bed demand exceeds available capacity. Recommended actions:
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                    <li>Activate surge beds ({bedOccupancy.surge_capacity} additional beds available)</li>
                    <li>Expedite strategic evacuations to Role 4</li>
                    <li>Request additional medical assets from corps</li>
                    <li>Adjust Theatre Patient Return Policy (TPRP) to shorter duration</li>
                  </Box>
                </Alert>
              )}
              {bedOccupancy.status === 'HIGH' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>{t('tab4.bed_occupancy_high', { rate: bedOccupancy.occupancy_rate })}</AlertTitle>
                  {t('tab4.approaching_capacity')}
                </Alert>
              )}

              {/* Per-role breakdown */}
              {bedOccupancy.per_role && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    {t('tab4.per_echelon_breakdown')}
                    <Tooltip
                      title="Admission distribution per AJMedP-1: Role 2B receives 60% of admitted casualties (primary surgical layer), Role 2E 30% (complex/ICU), Role 3 10% (specialist/long-stay). Role 1 excluded — non-holding facility."
                      arrow
                    >
                      <InfoIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', cursor: 'help' }} />
                    </Tooltip>
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { key: 'role_2_basic',    label: 'Role 2B (Brigade FST)',   fraction: '60%' },
                      { key: 'role_2_enhanced', label: 'Role 2E (Division)',       fraction: '30%' },
                      { key: 'role_3',          label: 'Role 3 (Corps MTF)',       fraction: '10%' },
                    ].map(({ key, label, fraction }) => {
                      const role = bedOccupancy.per_role[key];
                      if (!role) return (
                        <Grid size={{ xs: 12, sm: 4 }} key={key}>
                          <Card variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2">{label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('tab4.no_beds_configured')}
                            </Typography>
                          </Card>
                        </Grid>
                      );
                      return (
                        <Grid size={{ xs: 12, sm: 4 }} key={key}>
                          <Card variant="outlined" sx={{
                            p: 1.5,
                            borderColor: role.status === 'CRITICAL' || role.status === 'HIGH' ? 'error.main' :
                                         role.status === 'ELEVATED' ? 'warning.main' : 'success.main'
                          }}>
                            <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                            <Typography variant="h5" color={
                              role.status === 'CRITICAL' || role.status === 'HIGH' ? 'error.main' :
                              role.status === 'ELEVATED' ? 'warning.main' : 'success.main'
                            }>
                              {role.occupancy_rate}%
                            </Typography>
                            <Typography variant="caption" display="block">
                              {role.steady_state_beds_required}/{role.beds} beds ({fraction} of admissions)
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {role.daily_admissions} admissions/day — {role.status}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, role.occupancy_rate)}
                              color={
                                role.status === 'CRITICAL' || role.status === 'HIGH' ? 'error' :
                                role.status === 'ELEVATED' ? 'warning' : 'success'
                              }
                              sx={{ mt: 1, height: 6, borderRadius: 3 }}
                            />
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
              {!bedOccupancy.per_role && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('tab4.no_per_role_breakdown')}
                </Alert>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Section 4E: Mental Health */}
      <Accordion expanded={expandedSections.mentalHealth} onChange={() => setExpandedSections(prev => ({ ...prev, mentalHealth: !prev.mentalHealth }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <PsychologyIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4e')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {csrProjection && <Chip label={`CSR: ${csrProjection.total_csr_casualties || 0} projected`} color="warning" size="small" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}><FormControlLabel control={<Switch checked={mentalHealthConfig.csc_team_deployed} onChange={(e) => handleMentalHealthChange('csc_team_deployed', e.target.checked)} />} label={t('tab4.csc_team_deployed')} /></Grid>
            <Grid size={{ xs: 4 }}><TextField fullWidth label={t('tab4.restoration_beds')} type="number" value={mentalHealthConfig.restoration_beds} onChange={(e) => handleMentalHealthChange('restoration_beds', e.target.value)} size="small" /></Grid>
            <Grid size={{ xs: 4 }}><TextField fullWidth label={t('tab4.peer_support_trained')} type="number" value={mentalHealthConfig.peer_support_trained} onChange={(e) => handleMentalHealthChange('peer_support_trained', e.target.value)} size="small" /></Grid>
          </Grid>

          {csrProjection && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>{t('tab4.csr_projection')}</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 3 }}><Typography variant="h6">{csrProjection.total_csr_casualties || 0}</Typography><Typography variant="caption">{t('tab4.total_csr_expected')}</Typography></Grid>
                  {/* v5.4 FIX: Show daily rate as percentage for clarity (was showing 0.0 due to toFixed(1)) */}
                  <Grid size={{ xs: 3 }}><Typography variant="h6">{csrProjection.daily_csr_rate ? `${(csrProjection.daily_csr_rate * 100).toFixed(2)}%` : '0%'}</Typography><Typography variant="caption">{t('tab4.csr_daily_rate')}</Typography></Grid>
                  <Grid size={{ xs: 3 }}><Typography variant="h6">{csrProjection.rtd_within_72h || 0}</Typography><Typography variant="caption">{t('tab4.rtd_within_72h')}</Typography></Grid>
                  <Grid size={{ xs: 3 }}><Typography variant="h6">{csrProjection.require_evacuation || 0}</Typography><Typography variant="caption">{t('tab4.require_evacuation')}</Typography></Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong> BICEPS Principles: B</strong>revity - <strong>I</strong>mmediacy - <strong>C</strong>entrality - <strong>E</strong>xpectancy - <strong>P</strong>roximity - <strong>S</strong>implicity
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ====================================================================
          Section 4F: Forward Supply Chain — MSR & Distribution Legs
          v8.5.0: Replaces previous evacuation distance table which was wrong
          content for Tab 4. Tab 4 = logistics (forward); Tab 3 = evacuation
          (rearward patient movement). These are opposite directions.
          Three forward supply legs: Depot→FMSS, FMSS→R2B, R2B→R1.
          ENH-014b: Cold Chain Viability Matrix per blood product per leg.
          ==================================================================== */}
      <Accordion expanded={expandedSections.supplyRoutes} onChange={() => setExpandedSections(prev => ({ ...prev, supplyRoutes: !prev.supplyRoutes }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <LocalShippingIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4f')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* Live travel time chips for all 3 legs */}
            {(() => {
              try {
                const l1 = calculateConvoyTravelTime(msrConfig);
                const l2 = calculateConvoyTravelTime(msrLeg2Config);
                const l3 = calculateConvoyTravelTime(msrLeg3Config);
                const cc = (h) => h > 16 ? 'error' : h > 8 ? 'warning' : 'success';
                return (
                  <>
                    <Chip label={`Leg 1: ${l1.one_way_hours.toFixed(1)}h`} size="small" color={cc(l1.one_way_hours)} sx={{ mr: 0.5 }} />
                    <Chip label={`Leg 2: ${l2.one_way_hours.toFixed(1)}h`} size="small" color={cc(l2.one_way_hours)} sx={{ mr: 0.5 }} />
                    <Chip label={`Leg 3: ${l3.one_way_hours.toFixed(1)}h`} size="small" color={cc(l3.one_way_hours)} sx={{ mr: 1 }} />
                  </>
                );
              } catch (_) { return null; }
            })()}
          </Box>
        </AccordionSummary>
        <AccordionDetails>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('tab4.fwd_supply_chain_info')}
            </Typography>
          </Alert>

          {/* ================================================================
              THREE-LEG MSR INPUTS — same 4-field structure per leg
              ================================================================ */}
          {[
            {
              key: 'leg1',
              label: 'Leg 1: Depot \u2192 FMSS',
              subtitle: 'Main Supply Route — longest leg, typically paved road',
              config: msrConfig,
              setConfig: setMsrConfig,
            },
            {
              key: 'leg2',
              label: 'Leg 2: FMSS \u2192 R2B',
              subtitle: 'Forward distribution — shorter, often unimproved road',
              config: msrLeg2Config,
              setConfig: setMsrLeg2Config,
            },
            {
              key: 'leg3',
              label: 'Leg 3: R2B \u2192 R1',
              subtitle: 'Tactical resupply — shortest, most contested, typically track',
              config: msrLeg3Config,
              setConfig: setMsrLeg3Config,
            },
          ].map(({ key, label, subtitle, config, setConfig }) => {
            let preview = null;
            try { preview = calculateConvoyTravelTime(config); } catch (_) {}
            const borderColor = !preview             ? 'grey.300'
              : preview.warning === 'EXTREME'        ? 'error.main'
              : preview.warning === 'MULTI_DAY'      ? 'warning.main'
              : preview.warning                      ? 'warning.light'
              : 'success.main';

            return (
              <Box key={key} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShippingIcon fontSize="small" />
                  {label}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    &mdash; {subtitle}
                  </Typography>
                </Typography>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      label={t('tab4.distance_km')}
                      type="number"
                      size="small"
                      fullWidth
                      value={config.distance_km}
                      onChange={e => setConfig(prev => ({ ...prev, distance_km: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                      inputProps={{ min: 1, max: 500, step: 5 }}
                      helperText={t('tab4.one_way_distance')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      select
                      label={t('tab4.road_type')}
                      size="small"
                      fullWidth
                      value={config.road_type}
                      onChange={e => setConfig(prev => ({ ...prev, road_type: e.target.value }))}
                      helperText={`${MSR_ROAD_SPEEDS[config.road_type]?.speed_kmh ?? '?'} km/h base`}
                    >
                      {Object.entries(MSR_ROAD_SPEEDS).map(([k, spec]) => (
                        <MenuItem key={k} value={k}>{spec.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      select
                      label={t('tab4.terrain')}
                      size="small"
                      fullWidth
                      value={config.terrain}
                      onChange={e => setConfig(prev => ({ ...prev, terrain: e.target.value }))}
                      helperText={`\u00d7${MSR_TERRAIN_MODIFIERS[config.terrain]?.modifier ?? '?'} speed`}
                    >
                      {Object.entries(MSR_TERRAIN_MODIFIERS).map(([k, spec]) => (
                        <MenuItem key={k} value={k}>{spec.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      select
                      label={t('tab4.surface_condition')}
                      size="small"
                      fullWidth
                      value={config.season}
                      onChange={e => setConfig(prev => ({ ...prev, season: e.target.value }))}
                      helperText={`\u00d7${MSR_SEASON_MODIFIERS[config.season]?.modifier ?? '?'} speed`}
                    >
                      {Object.entries(MSR_SEASON_MODIFIERS).map(([k, spec]) => (
                        <MenuItem key={k} value={k}>{spec.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                {/* BUG-081: Unrefrigerated vehicle flag — per leg cold chain availability */}
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!config.unrefrigerated}
                        onChange={e => setConfig(prev => ({ ...prev, unrefrigerated: e.target.checked }))}
                        size="small"
                        color="error"
                      />
                    }
                    label={
                      <Typography variant="caption" color={config.unrefrigerated ? 'error.main' : 'text.secondary'}>
                        {config.unrefrigerated
                          ? t('tab4.no_refrigerated_vehicle')
                          : t('tab4.refrigerated_vehicle_available')}
                      </Typography>
                    }
                  />
                  <Tooltip
                    title="When enabled, cold-chain blood products (FFP, PRBC, WB, FWB) are marked non-viable on this leg regardless of travel time. Only FDP and platelets (no refrigeration required) remain viable. Applies to the Cold Chain Viability matrix below."
                    arrow
                  >
                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                {/* Live travel time preview */}
                {preview && (
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor }}>
                    <Typography variant="caption" color="text.secondary">
                      Speed: <strong>{preview.effective_speed_kmh} km/h</strong>
                      &nbsp;|&nbsp; One-way: <strong>{preview.one_way_hours.toFixed(1)} h</strong>
                      &nbsp;|&nbsp; Round-trip: <strong>{preview.round_trip_hours.toFixed(1)} h</strong>
                      &nbsp;|&nbsp; Lead time:&nbsp;
                      <strong style={{ color: preview.lead_time_days >= 3 ? '#d32f2f' : preview.lead_time_days >= 2 ? '#ed6c02' : '#2e7d32' }}>
                        {preview.lead_time_days} day{preview.lead_time_days !== 1 ? 's' : ''} advance order
                      </strong>
                      {preview.warning_label && (
                        <span style={{ color: preview.warning === 'EXTREME' ? '#d32f2f' : '#ed6c02' }}>
                          &nbsp;&mdash; {preview.warning_label}
                        </span>
                      )}
                    </Typography>
                    {preview.same_day_feasible && !preview.warning && (
                      <Typography variant="caption" color="success.dark" sx={{ display: 'block', mt: 0.3 }}>
                        {t('tab4.same_day_feasible')}
                      </Typography>
                    )}
                    {preview.warning && (
                      <Typography variant="caption" color={preview.warning === 'EXTREME' ? 'error.dark' : 'warning.dark'} sx={{ display: 'block', mt: 0.3, fontStyle: 'italic' }}>
                        {t('tab4.save_persist_msr')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}

          {/* ================================================================
              ENH-014b: COLD CHAIN VIABILITY MATRIX
              Checks each blood product's max transport time against all 3 legs.
              Data source: BLOOD_TRANSPORT_LIMITS in classVIII.js v2.9.
              ================================================================ */}
          {(() => {
            let legs = null;
            try {
              // BUG-081: carry unrefrigerated flag from each leg config
              legs = [
                { label: 'Leg 1', sub: 'Depot→FMSS', hours: calculateConvoyTravelTime(msrConfig).one_way_hours,     unrefrigerated: !!msrConfig.unrefrigerated },
                { label: 'Leg 2', sub: 'FMSS→R2B',   hours: calculateConvoyTravelTime(msrLeg2Config).one_way_hours, unrefrigerated: !!msrLeg2Config.unrefrigerated },
                { label: 'Leg 3', sub: 'R2B→R1',     hours: calculateConvoyTravelTime(msrLeg3Config).one_way_hours, unrefrigerated: !!msrLeg3Config.unrefrigerated },
              ];
            } catch (_) { return null; }

            const products = Object.values(BLOOD_TRANSPORT_LIMITS);

            // BUG-081 FIX: cellStatus now accepts leg object (not just legHours).
            // New status 'no_vehicle': cold-chain product on unrefrigerated leg —
            // overrides time check entirely (vehicle unavailability trumps travel time).
            // BUG-4F-001 FIX: PLT (cold_chain:false, max_hours:4) was short-circuiting to
            //   'no_cold_chain' before its 4h limit was checked. PLT requires agitation +
            //   20–24°C (not refrigeration), but IS time-limited — the most restrictive product.
            //   Correct logic: products with cold_chain:false && max_hours:null → 'no_cold_chain' (FDP only).
            //   Products with cold_chain:false && max_hours set (PLT) → check time limit, skip unrefrigerated.
            // Returns 'ok' | 'warning' | 'exceeded' | 'no_cold_chain' | 'no_vehicle'
            const cellStatus = (product, leg) => {
              if (!product.cold_chain && product.max_hours === null) return 'no_cold_chain'; // FDP: truly no limit
              if (!product.cold_chain) {
                // BUG-4F-001: PLT — room temp + agitation required; no refrigeration needed but time IS limited
                if (leg.hours > product.max_hours) return 'exceeded';
                if (leg.hours > product.max_hours * 0.75) return 'warning';
                return 'ok';
              }
              if (leg.unrefrigerated) return 'no_vehicle';           // BUG-081: no refrigerated vehicle on this leg
              if (product.max_hours === null) return 'ok';
              if (leg.hours > product.max_hours) return 'exceeded';
              if (leg.hours > product.max_hours * 0.75) return 'warning';
              return 'ok';
            };

            const anyExceeded = products.some(p => legs.some(l => cellStatus(p, l) === 'exceeded' || cellStatus(p, l) === 'no_vehicle'));
            const anyWarning  = !anyExceeded && products.some(p => legs.some(l => cellStatus(p, l) === 'warning'));

            const bg    = { ok: 'success.light',  warning: 'warning.light', exceeded: '#ffebee', no_cold_chain: 'grey.50',       no_vehicle: '#ffebee' };
            const color = { ok: 'success.dark',   warning: 'warning.dark',  exceeded: 'error.dark', no_cold_chain: 'text.secondary', no_vehicle: 'error.dark' };

            const cellLabel = (status, product, leg) => {
              if (status === 'no_vehicle')    return 'NO VEHICLE — no cold chain';
              if (status === 'no_cold_chain') return product.max_hours === null ? 'No limit' : `OK — no refrigeration`;
              if (status === 'ok' && product.max_hours === null) return 'No limit';
              if (status === 'ok')       return `OK  ${leg.hours.toFixed(1)}h / ${product.max_hours}h`;
              if (status === 'warning')  return `CAUTION  ${leg.hours.toFixed(1)}h / ${product.max_hours}h`;
              return `EXCEEDED  ${leg.hours.toFixed(1)}h > ${product.max_hours}h`;
            };

            return (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('tab4.cold_chain_viability')}
                  <Tooltip
                    title="Each blood product has a maximum transport time before degradation. RED = transport time exceeds limit. YELLOW = approaching limit (>75%). FDP only has no transport time limit. PLT requires agitation + 20–24°C — no refrigeration, but IS time-limited (4h max). Sources: NATO AMedP-7.1 §3.2–3.5; FRESH_WHOLE_BLOOD warm ischaemia 8h."
                    arrow
                  >
                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Typography>

                {anyExceeded && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    <Typography variant="body2">
                      {t('tab4.blood_not_survive')}
                    </Typography>
                  </Alert>
                )}
                {anyWarning && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    <Typography variant="body2">
                      {t('tab4.approaching_limit_warning')}
                    </Typography>
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 700 }}>{t('tab4.col_product')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{t('tab4.col_max_transport')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{t('tab4.col_cold_chain')}</TableCell>
                        {legs.map((l, i) => (
                          <TableCell key={i} align="center" sx={{ fontWeight: 700 }}>
                            {l.label}<br />
                            <Typography variant="caption" color="text.secondary">{l.sub}</Typography>
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 700 }}>{t('tab4.col_storage_note')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map(product => (
                        <TableRow key={product.label} hover>
                          <TableCell><strong>{product.label}</strong></TableCell>
                          <TableCell align="center">
                            {product.max_hours === null
                              ? <Typography variant="caption" color="success.dark">&#8734; (no limit)</Typography>
                              : <Typography variant="caption">{product.max_hours}h</Typography>
                            }
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={product.cold_chain ? t('tab4.cold_chain_required') : t('tab4.cold_chain_not_required')}
                              size="small"
                              color={product.cold_chain ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          {legs.map((leg, i) => {
                            // BUG-081: pass full leg object so unrefrigerated flag is visible
                            const status = cellStatus(product, leg);
                            return (
                              <TableCell key={i} align="center" sx={{ bgcolor: bg[status] }}>
                                <Typography
                                  variant="caption"
                                  color={color[status]}
                                  sx={{ fontWeight: (status === 'exceeded' || status === 'no_vehicle') ? 700 : 400, display: 'block' }}
                                >
                                  {cellLabel(status, product, leg)}
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{product.note}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  <strong>FDP</strong> (Freeze-Dried Plasma) is the only plasma product viable on any route — no cold chain, no time limit, reconstitutes in 3 min at point of use.
                  &nbsp;<strong>PLT</strong> (Platelets) requires agitation during transport; 4h limit is the most restrictive of all products.
                  &nbsp;<strong>FWB / LTOWB</strong> are Walking Blood Bank / Emergency Blood Collection (WBB / EBC) products generated at R2B — depot transport not applicable in practice.
                </Typography>
              </Box>
            );
          })()}

          {/* Link back to Tab 3 for evacuation distances */}
          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => onNavigate && onNavigate('tab3')}
            >
              {t('tab4.edit_evac_distances')}
            </Button>
          </Box>

        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expandedSections.validation} onChange={() => setExpandedSections(prev => ({ ...prev, validation: !prev.validation }))}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <VerifiedIcon color="primary" />
            <Typography variant="h6">{t('tab4.section_4g')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {getValidationIcon(validation?.overall_status)}
            <Chip label={validation?.overall_status?.toUpperCase() || 'UNKNOWN'} color={validation?.overall_status === 'green' ? 'success' : validation?.overall_status === 'yellow' ? 'warning' : 'error'} size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 3 }}><Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light' }}><Typography variant="h4">{validation?.summary?.passed || 0}</Typography><Typography variant="caption">Passed</Typography></Paper></Grid>
            <Grid size={{ xs: 3 }}><Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'warning.light' }}><Typography variant="h4">{validation?.summary?.warnings || 0}</Typography><Typography variant="caption">Warnings</Typography></Paper></Grid>
            <Grid size={{ xs: 3 }}><Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'error.light' }}><Typography variant="h4">{validation?.summary?.errors || 0}</Typography><Typography variant="caption">Errors</Typography></Paper></Grid>
            <Grid size={{ xs: 3 }}><Paper sx={{ p: 1, textAlign: 'center' }}><Typography variant="h4">{validation?.summary?.total_checks || 0}</Typography><Typography variant="caption">Total Checks</Typography></Paper></Grid>
          </Grid>

          {/* Display Calculator Error if present */}
          {calculationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>{t('tab4.calc_error')}</AlertTitle>
              {calculationError}
            </Alert>
          )}

          {/* v5.6.8: Extract and display all issues from validation.checks (object with tab keys) */}
          {(() => {
            // Extract all issues from all checks
            const allIssues = [];
            if (validation?.checks) {
              Object.entries(validation.checks).forEach(([tabKey, check]) => {
                if (check?.issues && Array.isArray(check.issues)) {
                  check.issues.forEach(issue => {
                    allIssues.push({
                      ...issue,
                      tab: check.tab || tabKey
                    });
                  });
                }
              });
            }
            
            const warnings = allIssues.filter(i => i.severity === 'WARNING');
            const errors = allIssues.filter(i => i.severity === 'ERROR');
            
            return (
              <>
                {/* Display Warnings */}
                {warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>{warnings.length} Issue(s) Found - Review for Optimal Configuration</AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {warnings.map((warning, idx) => (
                        <li key={idx} style={{ marginBottom: '8px' }}>
                          <Typography variant="body2">
                            <strong>{warning.tab}:</strong> {warning.message}
                          </Typography>
                          {warning.fix && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
                              -> Fix: {warning.fix}
                            </Typography>
                          )}
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}

                {/* Display Errors */}
                {errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>{errors.length} Error(s) - Must Fix Before Proceeding</AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {errors.map((error, idx) => (
                        <li key={idx} style={{ marginBottom: '8px' }}>
                          <Typography variant="body2">
                            <strong>{error.tab}:</strong> {error.message}
                          </Typography>
                          {error.fix && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
                              -> Fix: {error.fix}
                            </Typography>
                          )}
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
              </>
            );
          })()}

          {/* Display recommendations - v5.6.8: filter out generic warning/error count messages since we show details above */}
          {validation?.recommendations?.filter(rec => 
            !rec.message?.includes('warning(s) found') && 
            !rec.message?.includes('error(s) found')
          ).map((rec, index) => (
            <Alert key={index} severity={rec.priority === 'CRITICAL' ? 'error' : rec.priority === 'HIGH' ? 'warning' : 'info'} sx={{ mb: 1 }}>
              <AlertTitle>{rec.message}</AlertTitle>{rec.action}
            </Alert>
          ))}

          {/* v5.6.8: Expandable detailed checks list - shows each tab check with status */}
          {validation?.checks && (
            <Accordion sx={{ mt: 2, bgcolor: 'grey.50' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  {t('tab4.view_all_validation', { count: Object.keys(validation.checks).length })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>Status</TableCell>
                      <TableCell>Tab/Check</TableCell>
                      <TableCell>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(validation.checks).map(([tabKey, check], idx) => {
                      if (!check) return null;
                      const isPassed = check.status === 'passed';
                      const isWarning = check.status === 'warning';
                      const issueCount = check.issues?.length || 0;
                      return (
                        <TableRow key={idx} sx={{ bgcolor: isPassed ? 'success.light' : isWarning ? 'warning.light' : 'error.light' }}>
                          <TableCell>
                            {isPassed ? <CheckCircleIcon color="success" fontSize="small" /> :
                             isWarning ? <WarningIcon color="warning" fontSize="small" /> :
                             <ErrorIcon color="error" fontSize="small" />}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{check.tab || tabKey}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {isPassed ? t('tab4.all_checks_passed_cell') : `${issueCount} issue(s)`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          )}

          {/* BUG-T21 fix: when Tab 4 logistics checks have issues, guide users to scroll up —
              not go back to Tab 3. "Back to Tab 3" is only needed for medical/force config issues. */}
          {(() => {
            const tab4Check = validation?.checks?.tab4_logistics;
            const hasTab4Issues = tab4Check && tab4Check.status !== 'passed';
            return hasTab4Issues ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>{t('tab4.fix_logistics_above')}</AlertTitle>
                Tab 4 issues (supply quantities, blood stock, resupply schedule) are configured
                in the sections above — scroll up to correct them. Only use "Back to Medical (Tab 3)"
                if you need to change facility or asset configuration.
              </Alert>
            ) : null;
          })()}

          {validation?.can_proceed_to_tab5 && (
            <Alert severity="success" sx={{ mt: 2 }}><AlertTitle>Ready to Proceed</AlertTitle>{t('tab4.all_checks_passed')}</Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* v5.6.9: Navigation Buttons - Load Preset moved here from header */}
      {/* BUG-T21 fix: renamed from "Back to Tab 3" → "Back to Medical (Tab 3)" to clarify
          that Tab 4 logistics issues should be fixed by scrolling up to 4A–4F above, not navigating back. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => onNavigate && onNavigate(2)}
        >
          {t('tab4.back_to_medical')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<InventoryIcon />}
          onClick={() => setPresetDialogOpen(true)}
        >
          {t('tab4.load_preset_btn')}
        </Button>
        <Button
          variant="contained"
          endIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
          onClick={handleSave}
          color="primary"
          disabled={isCalculating}
        >
          {isCalculating ? t('tab4.calculating') : t('tab4.save_continue')}
        </Button>
      </Box>

      {/* Preset Dialog */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)}>
        <DialogTitle>{t('tab4.load_national_preset')}</DialogTitle>
        <DialogContent>
          <List>
            <ListItem button onClick={() => handleLoadPreset('NATO_BRIGADE')}><ListItemText primary={t('tab4.preset_nato_brigade')} secondary={t('tab4.preset_nato_brigade_desc')} /></ListItem>
            <ListItem button onClick={() => handleLoadPreset('ROMANIAN_BRIGADE')}><ListItemText primary={t('tab4.preset_romanian_brigade')} secondary={t('tab4.preset_romanian_brigade_desc')} /></ListItem>
            <ListItem button onClick={() => handleLoadPreset('US_BRIGADE')}><ListItemText primary={t('tab4.preset_us_brigade')} secondary={t('tab4.preset_us_brigade_desc')} /></ListItem>
          </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setPresetDialogOpen(false)}>{t('tab4.cancel')}</Button></DialogActions>
      </Dialog>

      {/* PHASE 4.4: Auto-Schedule Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('tab4.auto_schedule_preview')}</Typography>
            {autoSchedulePreview && (
              <Chip 
                label={autoSchedulePreview.summary.strategyLabel}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {autoSchedulePreview ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>{t('tab4.proposed_schedule')}</AlertTitle>
                <Typography variant="body2">
                  {autoSchedulePreview.summary.message}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('tab4.target_dos')}: <strong>{autoSchedulePreview.summary.targetDOS} days</strong>
                </Typography>
              </Alert>

              {/* Before/After DOS Comparison */}
              {autoSchedulePreview.dosProjection && (
                <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('tab4.dos_comparison')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab4.without_resupply')}
                          </Typography>
                          <Typography variant="h6" color="error.dark">
                            {stockoutPredictions[0]?.daysRemaining || 'N/A'} days
                          </Typography>
                          <Typography variant="caption" color="error.dark">
                            Stockout: Day {stockoutPredictions[0]?.stockoutDay || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab4.with_auto_schedule')}
                          </Typography>
                          <Typography variant="h6" color="success.dark">
                            {Math.round(autoSchedulePreview.dosProjection.minimumDOS)} days
                          </Typography>
                          <Typography variant="caption" color="success.dark">
                            {autoSchedulePreview.dosProjection.stockoutDay ?
                              `Stockout: Day ${autoSchedulePreview.dosProjection.stockoutDay}` :
                              t('tab4.no_stockout')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Resupply Events Table */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                {t('tab4.scheduled_resupply_events', { count: autoSchedulePreview.events.length })}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tab4.col_dispatch_day')}</TableCell>
                      <TableCell>{t('tab4.col_package')}</TableCell>
                      <TableCell>{t('tab4.col_method')}</TableCell>
                      <TableCell>{t('tab4.col_products_covered')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {autoSchedulePreview.events.map((event, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2">D+{event.day}</Typography>
                          {event.arrival_day && event.arrival_day !== event.day && (
                            <Typography variant="caption" color="text.secondary">
                              arrives D+{event.arrival_day}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {PUSH_PACKAGES[event.push_package]?.name || event.push_package}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {/* ENUM-SWEEP FIX */}
                          <Chip
                            label={({ AIR: 'Air', GROUND: 'Ground', BLUE_LIGHT: 'Blue Light', DRONE: 'Drone' }[event.delivery_method] ?? event.delivery_method)}
                            size="small"
                            color={event.delivery_method === 'BLUE_LIGHT' ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {event.products_covered?.map((p, i) => (
                            <Chip 
                              key={i}
                              label={`${p.product}: ${p.units} units`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Total Units Summary */}
              <Card sx={{ mt: 2, bgcolor: 'info.light' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('tab4.total_units_resupplied')}
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(autoSchedulePreview.summary.totalUnits).map(([key, value]) => (
                      <Grid size={{ xs: 6, sm: 3 }} key={key}>
                        <Typography variant="body2">
                          <strong>{key.toUpperCase()}:</strong> {value} units
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </>
          ) : (
            <Typography>{t('tab4.generating_schedule')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            {t('tab4.cancel')}
          </Button>
          <Button
            onClick={handleApplyAutoSchedule}
            variant="contained"
            color="primary"
            disabled={!autoSchedulePreview}
          >
            {t('tab4.apply_schedule')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default Tab4_Logistics;
